/**
 * Five Out's sound engine. Everything is synthesized with the Web Audio API —
 * no audio assets — matching the rest of the game's procedural art direction.
 *
 * Two buses hang off a master gain:
 *  - music: a generative per-venue ambience (ocean + warm pad for the Coastal
 *    Terrace, fireplace crackle + low pad for the Alpine Lodge), kept very quiet.
 *  - sfx: short, soft, physical one-shots (card friction, flips, chimes, slams).
 *
 * Both also feed a shared REVERB send, whose impulse response is generated here
 * rather than loaded — see `makeImpulse`. Adding it is the single largest step
 * up this engine has taken: every venue was previously bone dry, which is what
 * made even the well-voiced ones sound like a mix rather than a room. Each venue
 * names its own decay and send level in `VENUE_ACOUSTICS`.
 *
 * Two more pieces of shared plumbing exist for the same reason — they lift every
 * venue rather than dressing one:
 *
 *  - STEREO. Sparse one-shots take a pan position. A bell that always arrives
 *    dead center is heard as a sound effect; off to one side it is heard as
 *    something happening nearby.
 *  - A LOOKAHEAD SCHEDULER (`startPulse`). `setTimeout` is fine for a pad that
 *    changes chord every eleven seconds and useless for anything with a pulse:
 *    it drifts, audibly, inside a minute. Rhythmic material schedules against
 *    `ctx.currentTime`.
 *
 * And the score is no longer wallpaper: `setTension` lets the table say how
 * close the round is to ending, and `duck` dips the music under big moments so
 * they land in a hole instead of on top of a pad.
 *
 * See docs/PREMIUM_VENUES.md.
 *
 * Browsers block audio until a user gesture, so everything lazily starts on the
 * first pointer/key event. Settings persist in localStorage.
 */

import type { VenueId } from "../theme/venues";
import { VENUE_BEDS, type DriftVoice } from "./beds";
import { PLUCK_CACHE_MAX, PLUCK_RENDER_DAMPING, PLUCK_RENDER_SECONDS, pluckVoice } from "./pluck";
import { audioDiagState, audioLog } from "./audioDiag";

/**
 * How long the page may be hidden before the ambience is rebuilt rather than
 * resumed. See `resumeAmbience`.
 *
 * A minute is comfortably under Chrome's five-minute intensive-throttling cliff
 * and comfortably over an ordinary glance at another app, so a quick tab switch
 * leaves the bed exactly as it was and anything longer gets a clean start. The
 * cost of being wrong in the restarting direction is a slow fade-in nobody
 * notices; the cost of being wrong the other way is the bug this exists for.
 */
export const STALE_HIDDEN_MS = 60_000;

/**
 * Whether the ambience must be REBUILT rather than resumed.
 *
 * Exported so the stickiness can be tested, because that is the part that is
 * easy to get wrong and the part that reintroduces the bug: after a long lock
 * the first wake usually cannot resume the context at all, so a flag cleared
 * eagerly there leaves the tap that finally works resuming a wrecked ambience.
 */
export function ambienceIsStale(wasStale: boolean, hiddenAt: number, now: number): boolean {
  return wasStale || (hiddenAt > 0 && now - hiddenAt > STALE_HIDDEN_MS);
}

/**
 * Whether waking should build a brand-new AudioContext instead of resuming the
 * old one: the page was away past the same threshold that makes the ambience
 * stale.
 *
 * Rebuilding the ambience on the OLD context was not enough. After a night with
 * the phone locked, iOS handed back a context that reported `running` but
 * rendered in bursts - out for about three quarters of a second, in for a
 * quarter - and every restart of the ambience on top of it sounded the same.
 * Only closing the web app and reopening it fixed it, i.e. only a new context
 * did. Everything here is synthesized, so a new context costs nothing to build.
 */
export function contextNeedsRebuild(hiddenAt: number, now: number): boolean {
  return hiddenAt > 0 && now - hiddenAt > STALE_HIDDEN_MS;
}

/**
 * How much faster every room's pulse plays at full tension than at rest: 200%
 * (three times the written tempo), on the last lap, set by ear. Linear in the
 * tension - none at 0, twice as fast at 0.5, three times at 1 - so
 * the music starts pushing as the decks thin rather than jumping when the lap
 * begins.
 */
export const TENSION_TEMPO_LIFT = 2.0;

/** The tempo multiplier at a tension (0-1): 1 at rest, `1 + TENSION_TEMPO_LIFT` at the peak. */
export function tensionTempo(tension: number): number {
  return 1 + TENSION_TEMPO_LIFT * Math.max(0, Math.min(1, tension));
}

/**
 * How fast the tension the TEMPO follows may move, per second: the whole way
 * from rest to the peak in eight seconds.
 *
 * Linear in the tension is not enough on its own, because the tension itself
 * jumps. The table reports it after every move straight from the deck counts,
 * so somebody going out takes it from 0.5 to 1 in one call, and the smallest
 * deck taking a pile drops it to 0 - a lurch of 15% or 30% in the beat between
 * one bar and the next. Gliding towards it instead makes every change an
 * accelerando or a ritardando over a few bars.
 */
export const TEMPO_GLIDE_PER_SEC = 0.125;

/** One step of that glide: `current` moved towards `target` by at most `dtSec` seconds' worth. */
export function glideTension(current: number, target: number, dtSec: number): number {
  const most = TEMPO_GLIDE_PER_SEC * Math.max(0, dtSec);
  return target > current ? Math.min(target, current + most) : Math.max(target, current - most);
}

/**
 * How long an ambience takes to fade out when it is stopped - by the music
 * toggle, a change of venue or a rebuild. Long enough not to click, short
 * enough that the room is plainly gone by the time a player listens for it.
 */
export const AMBIENCE_FADE_S = 0.35;

const MUSIC_KEY = "fiveout:musicOn";
const SFX_KEY = "fiveout:sfxOn";
const MUSIC_VOLUME_KEY = "fiveout:musicVolume";

function loadFlag(key: string): boolean {
  return localStorage.getItem(key) !== "0"; // default on
}
function saveFlag(key: string, on: boolean): void {
  localStorage.setItem(key, on ? "1" : "0");
}

/**
 * A 0..1 setting out of storage, defaulting when it is missing OR unusable.
 *
 * `Number(null)` is 0 and `Number("")` is 0, so a plain `Number(...) ?? 1` would
 * turn "never set" and "cleared" into SILENCE rather than into the default —
 * which is the one failure mode of a volume setting that reads as the game
 * being broken rather than as a preference being lost.
 *
 * Exported for the same reason `ambienceIsStale` is: the wrong answer here is
 * silent, arrives on a first launch, and looks like the sound engine failing
 * rather than like a default being mis-read.
 */
export function loadLevel(key: string, fallback: number): number {
  const raw = localStorage.getItem(key);
  // The BLANK case is the one that bites, and a finiteness check does not catch
  // it: `Number("")` is 0, and 0 is a perfectly finite number, so a cleared
  // value would sail through and mute the game. Test caught this on the first
  // run, which is the whole argument for having it.
  if (raw === null || raw.trim() === "") return fallback;
  const value = Number(raw);
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(1, value));
}

/**
 * How the room sounds, per venue: reverb length, brightness and how much of the
 * mix is sent to it.
 *
 * `send` is the setting that carries the character. Coastal is nearly dry
 * because a terrace over the sea has nothing to reflect off; the Reading Room
 * has plaster and shelves; Mount Olympus is a marble hall, which is why its
 * numbers look extreme next to the rest — it is supposed to.
 *
 * `bright` biases the impulse's spectrum: stone and glass keep their highs,
 * wool and wood eat them. Getting this wrong is what makes a long reverb sound
 * like a cheap effect rather than a big space.
 */
interface Acoustics {
  /** Reverb tail in seconds. */
  seconds: number;
  /** 0 = dark and woolly, 1 = hard and bright. */
  bright: number;
  /** Return level into the master bus. */
  send: number;
}

const VENUE_ACOUSTICS: Record<VenueId, Acoustics> = {
  coastal: { seconds: 1.5, bright: 0.55, send: 0.1 },
  alpine: { seconds: 1.1, bright: 0.2, send: 0.13 },
  library: { seconds: 1.6, bright: 0.35, send: 0.17 },
  pavilion: { seconds: 1.3, bright: 0.5, send: 0.12 },
  observatory: { seconds: 2.7, bright: 0.8, send: 0.22 },
  velvet: { seconds: 1.4, bright: 0.25, send: 0.18 },
  /*
   * Still the brightest, wettest room here, and dialled back from 0.72 / 0.32:
   * at that tilt every pluck's attack hung in the marble for seconds, and most
   * of the venue's energy in the 2-5kHz band was the reverb, not the strings.
   */
  olympian: { seconds: 3.6, bright: 0.58, send: 0.27 },
  /*
   * The other extreme from Olympus, and the reason `bright` is a parameter at
   * all. Both rooms are enormous and neither sounds remotely like the other:
   * marble keeps its highs and a kilometre of water eats them, so a long tail
   * with the tilt left bright would have made the abyss sound like a cathedral.
   * The length is dialled back from where it wanted to go (5s+) because the bed
   * under it is the lowest in the file, and low content in a long dark tail is
   * how a reverb turns into mud.
   */
  biolume: { seconds: 4.2, bright: 0.18, send: 0.3 },
  /*
   * The third premium room is the SMALL one, and that is its acoustic identity:
   * a glassed-in porch of painted board and cushions, with a short, warm tail.
   * Both other premium rooms are enormous, so a big send here would have made a
   * cosy space sound like a third hall. It still sits above the free venues'
   * sends, because an instrument with no room at all sounds like a toy on a
   * table — which is the one thing a music box must not sound like.
   */
  porch: { seconds: 1.25, bright: 0.52, send: 0.16 },
  /*
   * A cathedral: the longest tail in the game. An organ is an instrument built
   * FOR a stone room — what its chords are is mostly what the vault does with
   * them — and a dry one sounds like a keyboard. Stone keeps more of the top
   * than the abyss's water does, so it is brighter than the Midnight Zone, but
   * darker than Olympus' marble, because a grand nave is far enough away that
   * the air takes the edge off.
   *
   * Not as long as it wanted to be (8s+), for the reason the abyss gives: the
   * pedal goes down to D1, and low content in a very long tail is how a reverb
   * turns into mud. Five seconds is already a cathedral to an ear at a card
   * table.
   */
  hallowed: { seconds: 5, bright: 0.4, send: 0.32 },
  /*
   * A timber longhouse full of people and smoke. Mid-length and DARK: oak
   * boards, rafters and a hall of bodies in furs absorb the highs, so the tail
   * is darker than the porch's painted board and nothing like Hallowed's
   * cathedral, where stone gives the top back. Nearly two seconds rather than
   * the porch's one and a quarter because a longhouse is long and tall under
   * its roof, and a bowed string with no room behind it is a lone violin in a
   * cupboard.
   */
  meadhall: { seconds: 1.9, bright: 0.32, send: 0.2 },
  /*
   * The vast one: longer than the Midnight Zone's water and darker than
   * Hallowed Ground's nave, which is the pair of numbers that says "bigger than
   * a room". A cathedral still has walls that give the top back (0.4); nothing
   * out here does, so the tilt sits under even the abyss's 0.18 and the tail
   * dies as a hum rather than a ring. The send is generous because the voices
   * are meant to arrive from a long way off, and distance is mostly wet.
   *
   * Held at 6.5 seconds rather than the 8+ it wanted, for the reason the abyss
   * and the cathedral both give: the bed goes down to C2, and low content in a
   * very long dark tail is how a reverb turns into mud.
   */
  singularity: { seconds: 6.5, bright: 0.12, send: 0.34 },
};

/**
 * A generated impulse response: noise shaped by a decay curve.
 *
 * The textbook two-liner, and it is genuinely all that is needed — a convolution
 * reverb's character comes almost entirely from length and spectral tilt, both
 * of which are parameters here. Loading a real impulse would sound better in a
 * mastering suite and would also be the first audio asset in the project, with
 * a licence and a download attached to it.
 *
 * Stereo, with the two channels generated independently. That is not a detail:
 * identical channels collapse to a mono tail, which is heard as a filter rather
 * than as a space, and is exactly what the reverb was added to avoid.
 */
function makeImpulse(ctx: AudioContext, seconds: number, bright: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.floor(rate * seconds));
  const buf = ctx.createBuffer(2, len, rate);
  // A darker room loses its highs faster; modelled as a one-pole lowpass whose
  // coefficient rises with `bright`.
  const a = 0.12 + bright * 0.8;
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const t = i / len;
      // Slightly super-exponential: real tails thin out rather than fading flat.
      const envelope = Math.pow(1 - t, 2 + (1 - bright) * 2);
      const white = Math.random() * 2 - 1;
      last = last + a * (white - last);
      data[i] = last * envelope;
    }
  }
  return buf;
}

/**
 * The music bus at full setting.
 *
 * Raised from 0.4, where every bed and ambience in this file was originally
 * voiced. The beds are deliberately tiny (`beds.test.ts` caps a whole bed at
 * 0.12 summed) and the reverb sends are gentle, which together left the score
 * further under the game than it wanted to be — audible in a quiet room and
 * gone under anything else.
 *
 * It is the BUS that moved, not the material. Re-voicing thirty-odd gains
 * individually would have changed the balance between the layers of every venue
 * as well as the overall level, and the balance was not the thing that was
 * wrong. One number keeps every mix exactly as it was tuned.
 */
const MUSIC_BASE = 0.52;

/** One overtone of a struck object: where it sits, how loud it starts, how long it lasts. */
interface Partial_ {
  /** Multiple of the fundamental. Whole numbers ring; anything else clangs. */
  ratio: number;
  gain: number;
  /** Fraction of the strike's total length this partial survives for. */
  decay: number;
}

/** Bronze: the idealised bell set, wildly inharmonic, with the highs dying first. */
const BRONZE_PARTIALS: readonly Partial_[] = [
  { ratio: 1, gain: 1, decay: 1 },
  { ratio: 2.02, gain: 0.5, decay: 0.62 },
  { ratio: 2.76, gain: 0.34, decay: 0.44 },
  { ratio: 5.4, gain: 0.16, decay: 0.26 },
  { ratio: 8.93, gain: 0.08, decay: 0.16 },
];

/**
 * Glass, which is a different object rather than a duller bell.
 *
 * Two things separate the two and both are in these numbers. Glass is very
 * nearly HARMONIC — the ratios sit a hair off whole numbers instead of at 2.76
 * and 8.93 — so it rings a pitch where bronze rings a clang; and its highs
 * OUTLIVE bronze's, which is what turns a strike into a shimmer that hangs
 * rather than a hum that settles. That hanging top is the entire reason it
 * reads as a light coming on rather than as something being hit.
 */
const GLASS_PARTIALS: readonly Partial_[] = [
  { ratio: 1, gain: 1, decay: 1 },
  { ratio: 2.005, gain: 0.42, decay: 0.86 },
  { ratio: 3.01, gain: 0.26, decay: 0.7 },
  { ratio: 4.03, gain: 0.15, decay: 0.55 },
  { ratio: 6.06, gain: 0.09, decay: 0.4 },
];

/**
 * A tine — the plucked steel tongue of a kalimba or a music box's comb.
 *
 * A tine is a bar clamped at one end, and a clamped bar's overtones are the
 * cantilever modes: 6.27 and 17.55 times the fundamental, nowhere near the
 * harmonic series. Those two are loud for an instant and gone almost at once —
 * that is the "plink" — leaving a nearly pure fundamental to ring on. So this
 * is the inverse of glass, whose highs outlive the note: here the highs are
 * only the attack, which is why a tine sounds small, close and friendly where
 * glass hangs in the air. A faint octave rides along because a real tine is
 * never perfectly linear, and without it the tail is a sine wave.
 */
const TINE_PARTIALS: readonly Partial_[] = [
  { ratio: 1, gain: 1, decay: 1 },
  { ratio: 2, gain: 0.1, decay: 0.45 },
  // Softer than a music box's: at 0.3 this plink was the loudest thing in the
  // room in the 2-5kHz band, on every note, and that is what wore players out.
  { ratio: 6.27, gain: 0.15, decay: 0.08 },
  { ratio: 17.55, gain: 0.025, decay: 0.025 },
];

/**
 * The stops Hallowed Ground's organ draws, as harmonic spectra (see `organ`).
 *
 * A flue pipe's partials are true harmonics, so a stop is just a row of
 * amplitudes: the 1st is the note, the 2nd its octave, the 3rd a twelfth, and
 * so on. What tells one stop from another is how quickly that row falls away.
 *
 *  - `flute`: a stopped flute on the upper manual - nearly a pure tone, with a
 *    breath at the start. The melody. It sits high (up to D6) because it can:
 *    the xylophone it replaced carried a loud twelfth into 2.8-3.5kHz, and this
 *    carries almost nothing above its octave.
 *  - `principal`: the diapason, the sound people mean by "church organ". A long
 *    row of harmonics; the chords and the Dies irae.
 *  - `pedal`: a 16-foot bourdon with its octave drawn. The note itself goes as
 *    low as D1, which no phone speaker can play, so the 2nd to 4th harmonics are
 *    strong on purpose: the ear hears the missing fundamental from them, as it
 *    does from a real organ's pedal in a room too small for it.
 *
 * `speech` is how long the pipe takes to fill (seconds), `celeste` how far out
 * of tune the second rank is (cents), `chiff` the breath at the start, and
 * `ceiling` the lowpass over the whole note.
 */
type OrganStop = "flute" | "principal" | "pedal";
const ORGAN_STOPS: Record<OrganStop, { harmonics: readonly number[]; speech: number; celeste: number; chiff: number; ceiling: number }> = {
  flute: { harmonics: [1, 0.16, 0.07, 0.02], speech: 0.05, celeste: 1.3, chiff: 0.45, ceiling: 2600 },
  principal: { harmonics: [1, 0.55, 0.34, 0.22, 0.14, 0.09, 0.05, 0.03], speech: 0.09, celeste: 1.8, chiff: 0.12, ceiling: 2300 },
  pedal: { harmonics: [1, 0.85, 0.5, 0.3, 0.15, 0.07], speech: 0.16, celeste: 0.7, chiff: 0, ceiling: 900 },
};

/**
 * A few notes that belong to one room, stated now and then and varied each
 * time (see `startPhrases`). Every root must keep every note in the room's key.
 */
interface Motif {
  /** Semitones from the root, one per note. */
  semis: readonly number[];
  /** Each note's length, in beats. */
  beats: readonly number[];
  roots: readonly number[];
}

/** How a room's melody speaks. See `startPhrases`. */
interface PhraseSpec {
  scale: readonly number[];
  /** Step sizes in degrees, with weights: the room's melodic habit. */
  steps: ReadonlyArray<readonly [number, number]>;
  /** The degree the line lives around. */
  center: number;
  /** Notes in a phrase. */
  notes: readonly [number, number];
  /** Beats of silence between phrases. */
  gap: readonly [number, number];
  /** Degrees a phrase ends on: its chord tones. */
  land: readonly number[];
  /** Seconds until the next beat. Asked once per beat. */
  beat: () => number;
  /** Which beats may start a note. All of them if absent. */
  onBeat?: (beat: number) => boolean;
  motif?: Motif;
  /** Seconds before the first statement, and between the rest. */
  motifFirst?: readonly [number, number];
  motifEvery?: readonly [number, number];
  /** Seconds the line plays, then seconds it leaves the room to its bed. */
  episodeOn?: readonly [number, number];
  episodeOff?: readonly [number, number];
  play: (t0: number, freq: number, note: { last: boolean; motif: boolean; beats: number }) => void;
}

/**
 * The turn chime in each room: two notes of the room's own key, a sixth or so
 * apart, on the room's instrument where that instrument can carry a signal.
 * It used to be G5 and E6 everywhere, which put a G natural against the
 * Midnight Zone's G#s and a G against the Velvet Room's Ab - the most-heard
 * tonal sound in the game, out of tune in the room players like best.
 */
const VENUE_CHIME: Record<VenueId, { notes: readonly [number, number]; partials: readonly Partial_[] }> = {
  coastal: { notes: [783.99, 1318.51], partials: GLASS_PARTIALS }, // G5 E6
  alpine: { notes: [783.99, 1318.51], partials: GLASS_PARTIALS }, // G5 E6
  library: { notes: [783.99, 1318.51], partials: GLASS_PARTIALS }, // G5 E6
  pavilion: { notes: [783.99, 1318.51], partials: GLASS_PARTIALS }, // G5 E6
  observatory: { notes: [783.99, 1174.66], partials: GLASS_PARTIALS }, // G5 D6: no third, like the room
  velvet: { notes: [622.25, 1046.5], partials: GLASS_PARTIALS }, // Eb5 C6
  olympian: { notes: [698.46, 1174.66], partials: GLASS_PARTIALS }, // F5 D6
  biolume: { notes: [830.61, 1318.51], partials: GLASS_PARTIALS }, // G#5 E6
  porch: { notes: [698.46, 1174.66], partials: TINE_PARTIALS }, // F5 D6, on the kalimba
  hallowed: { notes: [698.46, 1174.66], partials: GLASS_PARTIALS }, // F5 D6
  meadhall: { notes: [659.25, 987.77], partials: GLASS_PARTIALS }, // E5 B5: the open fifth of the lyre's strings
  singularity: { notes: [523.25, 783.99], partials: GLASS_PARTIALS }, // C5 G5: the tonic's open fifth, no Db anywhere near a signal
};

/**
 * One note the ambience scheduled, for the music lab (`music-lab.html`). `at` is
 * on the audio clock and may be a little in the future: the scheduler looks
 * ahead.
 */
export interface MusicNote {
  at: number;
  freq: number;
  gain: number;
  kind: "pluck" | "struck" | "organ" | "call" | "blip" | "pop" | "bed";
}

class SoundManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  /** Shared reverb: both buses send to it, it returns to master. */
  private convolver: ConvolverNode | null = null;
  private reverbReturn: GainNode | null = null;
  private musicSend: GainNode | null = null;
  private sfxSend: GainNode | null = null;
  /**
   * The player's own music level, 0..1, where 1 means MUSIC_BASE.
   *
   * Separate from `musicOn` on purpose: the toggle is "do I want a soundscape at
   * all" and this is "how much of it". Folding them together would mean a player
   * who turned it down to a whisper and later toggled it off lost their level.
   */
  private musicVolume = loadLevel(MUSIC_VOLUME_KEY, 1);
  /**
   * The music bus's resting level, which `duck` returns to.
   *
   * Derived rather than stored, because a duck in progress has already moved the
   * AudioParam — recovering to "whatever it is now" would let repeated ducks
   * walk the music down to nothing over a busy turn, and storing a second copy
   * would let the setting and the level disagree after a volume change lands
   * mid-duck.
   */
  private get musicLevel(): number {
    return MUSIC_BASE * this.musicVolume;
  }
  /**
   * 0 = the round has just started, 1 = somebody is out and the lap is closing.
   *
   * Read by the ambiences that have something to do with it. Kept on the manager
   * rather than passed in, because it changes on a different clock from the one
   * the music is scheduled on.
   */
  private tension = 0;
  /** The tension the tempo has glided to so far, and the audio time it got there. See `tempo`. */
  private tempoTension = 0;
  private tempoAt = 0;
  /**
   * Dev instrumentation for the music lab: told about every note an ambience
   * schedules (not effects). Null in the app, where it costs one check a note.
   */
  noteListener: ((note: MusicNote) => void) | null = null;
  /** When the current room's ambience started, on the audio clock. See `wear`. */
  private ambienceStartedAt = 0;
  private meterNode: AnalyserNode | null = null;
  /** Rendered Karplus-Strong strings, keyed by pitch. See `pluckBuffer`. */
  private pluckCache = new Map<string, AudioBuffer>();
  private organWaves = new Map<OrganStop, PeriodicWave>();
  private organWaveCtx: AudioContext | null = null;
  /** The bowed lyre's sawtooth, built once per context. See `bowedWave`. */
  private bowedWaveCache: { ctx: AudioContext; wave: PeriodicWave } | null = null;

  private musicOn = loadFlag(MUSIC_KEY);
  private sfxOn = loadFlag(SFX_KEY);
  private venue: VenueId = "coastal";
  private booted = false;

  /** Live ambience teardown hooks (oscillator stops, timer clears). */
  private ambienceStops: Array<() => void> = [];
  private ambienceRunning = false;
  /**
   * The gain every node of the running ambience plays into, between it and the
   * music bus. One per ambience, so that stopping one can silence EVERYTHING it
   * had already scheduled - see `stopAmbience`.
   */
  private ambienceOut: GainNode | null = null;
  /**
   * EVERY ambience output still connected, not just the current one.
   *
   * Two rooms' music was heard playing together on an iPhone after "Use this
   * set", and never stopping - which the one-output bookkeeping above says
   * cannot happen, and which Chrome never did. Whatever route the phone took (a
   * context iOS handed back, a wake racing a venue change), this makes the rule
   * hold anyway: starting an ambience silences every other one still
   * connected, and stopping silences all of them.
   */
  private liveOuts = new Set<GainNode>();
  /** Every AudioContext built and not yet closed; only `ctx` should ever be here for long. */
  private contexts = new Set<AudioContext>();
  /** Wall clock when the page was last hidden; 0 while visible. */
  private hiddenAt = 0;
  /**
   * The page was away long enough that the ambience's timers cannot be trusted,
   * so it must be RESTARTED rather than resumed.
   *
   * Deliberately sticky: it stays true until an ambience actually starts again,
   * because the first wake after a long lock often cannot resume the context at
   * all (see `wake`), and clearing it there would mean the tap that finally does
   * work resumes a wrecked ambience instead of rebuilding it.
   */
  private ambienceStale = false;

  /** Call once at app start: arms the first-gesture unlock and visibility handling. */
  boot(venue: VenueId, opts: { buttonClicks?: boolean } = {}): void {
    this.venue = venue;
    if (this.booted) return;
    this.booted = true;
    audioDiagState(
      () =>
        `venue ${this.venue}  music ${this.musicOn ? "on" : "off"}  ambience ${this.ambienceRunning ? "running" : "stopped"}  tension ${this.tension.toFixed(2)}\n` +
        `contexts ${[...this.contexts].map((c) => (c === this.ctx ? `*${c.state}@${c.sampleRate}` : `${c.state}@${c.sampleRate}`)).join(",") || "none"}  ` +
        `live outputs ${this.liveOuts.size}  hidden ${this.hiddenAt ? `${Math.round((Date.now() - this.hiddenAt) / 1000)}s` : "no"}  ` +
        // Safari 16.4+: "play-and-record" is the call mode iOS enters while a mic is captured.
        `session ${(navigator as Navigator & { audioSession?: { type?: string } }).audioSession?.type ?? "n/a"}  ` +
        `latency ${this.ctx?.baseLatency !== undefined ? `${Math.round(this.ctx.baseLatency * 1000)}ms` : "n/a"}`,
    );
    const unlock = () => void this.wake();
    window.addEventListener("pointerdown", unlock, { passive: true });
    window.addEventListener("keydown", unlock);
    // Soft global click for every button — one delegate instead of wiring each screen.
    // The music lab turns this off: its buttons are test controls, and a click
    // on every one would land on top of the effect being auditioned.
    if (opts.buttonClicks !== false) {
      window.addEventListener(
        "pointerdown",
        (e) => {
          if ((e.target as HTMLElement | null)?.closest?.("button")) this.click();
        },
        { passive: true },
      );
    }
    // Deliberately NOT suspending on `hidden`. Both buses are already quiet, and
    // suspending is what broke iOS: see wake() for the full story.
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.markHidden();
      else void this.wake();
    });
    // pagehide is the one that fires when iOS backgrounds the whole app, which
    // is the case this is really for — a phone locked and picked up an hour later.
    window.addEventListener("pagehide", () => this.markHidden());
    // iOS fires these around interruptions (calls, Siri, lock) on the page itself.
    window.addEventListener("pageshow", unlock);
    window.addEventListener("focus", unlock);
  }

  /**
   * Brings audio back after the tab was hidden or the phone was locked.
   *
   * iOS does not merely *suspend* an AudioContext when the device locks — it
   * moves it to the non-standard `interrupted` state, and `resume()` from a
   * visibility handler is not a user gesture, so it quietly does nothing. Worse,
   * the oscillators and buffer sources feeding the ambience are torn down by the
   * system, so even a context that does come back is silent: `ambienceRunning`
   * was still true and `ambienceStops` still held handles to dead nodes, so the
   * ambience never restarted either. The net effect was "everything is silent
   * until I refresh the page".
   *
   * So: try a plain resume first, and if the context still isn't `running`,
   * throw it away entirely and build a fresh one. Rebuilding is cheap here
   * because every sound is synthesized — there are no buffers to reload beyond
   * the noise table.
   */
  private async wake(): Promise<void> {
    // Away long enough that the old context cannot be trusted even if it says
    // it is running (see contextNeedsRebuild): build a new one. The ambience is
    // marked stale so whichever wake gets the new context running - often the
    // next tap, since iOS only resumes inside a gesture - starts it afresh, and
    // hiddenAt is cleared so that later wakes do not rebuild it all over again.
    audioLog(`wake: ctx ${this.ctx?.state ?? "none"}, hidden ${this.hiddenAt ? `${Math.round((Date.now() - this.hiddenAt) / 1000)}s` : "no"}`);
    if (this.ctx && contextNeedsRebuild(this.hiddenAt, Date.now())) {
      audioLog("wake: away too long, rebuilding context");
      this.teardownContext();
      this.hiddenAt = 0;
      this.ambienceStale = true;
    }
    const ctx = this.ensureContext();
    if (!ctx) return;
    if (ctx.state !== "running") {
      try {
        await ctx.resume();
      } catch {
        /* resume can reject outside a gesture; the rebuild below covers it */
      }
    }
    if (ctx.state === "running") {
      this.resumeAmbience();
      return;
    }
    // Still not running (iOS `interrupted`, or a context closed under us):
    // tear the whole graph down and rebuild on the next gesture.
    this.teardownContext();
    const fresh = this.ensureContext();
    if (!fresh) return;
    try {
      await fresh.resume();
    } catch {
      return; // next gesture will try again
    }
    if (fresh.state === "running") this.resumeAmbience();
  }

  /** The context's sample rate, for the voice diagnostics; null before there is one. */
  get sampleRate(): number | null {
    return this.ctx?.sampleRate ?? null;
  }

  /** Notes that the page has gone away, so `wake` can judge how long it was out. */
  private markHidden(): void {
    if (this.hiddenAt === 0) this.hiddenAt = Date.now();
  }

  /**
   * Brings the ambience back after the page was away — restarting it outright if
   * it was away long enough to matter.
   *
   * THIS IS THE "SOUND POPS IN AND OUT AFTER THE PHONE WAS LOCKED" FIX, and the
   * cause is not the audio graph, it is `window.setTimeout`. Everything in the
   * ambience except the looping noise beds is timer-driven — the drifting bed
   * voices, the kithara's scheduler, every sparse one-shot — and a backgrounded
   * page has its timers throttled hard (Chrome drops hidden tabs to roughly one
   * firing a minute after five minutes) or frozen outright (iOS, on lock).
   *
   * A bed voice sounds for 62% of its own period and then waits for its timer.
   * Throttled to one firing a minute, a 30-second voice plays for twenty seconds
   * and is silent for forty — six of those, each out of step, IS the popping. The
   * noise bed underneath is a looping buffer with no timer, so it keeps playing
   * steadily throughout, which is why it sounds like parts of the mix dropping in
   * and out rather than like the audio failing.
   *
   * Resuming cannot fix that: the timers are still in whatever mangled schedule
   * the throttle left them in, and `startAmbienceIfWanted` returns immediately
   * while `ambienceRunning` is true — so before this, no amount of tapping
   * recovered it and only a reload did.
   *
   * Restarting is cheap and inaudible here. Every voice fades in over ten to
   * twenty seconds from a staggered start, so a rebuild sounds like the room
   * settling rather than like the music restarting.
   */
  private resumeAmbience(): void {
    const stale = ambienceIsStale(this.ambienceStale, this.hiddenAt, Date.now());
    this.hiddenAt = 0;
    // Dropped BEFORE the restart attempt rather than after: `stopAmbience` leaves
    // `ambienceRunning` false, so if the context is not ready yet the next
    // gesture starts a clean ambience anyway and nothing needs to remember why.
    this.ambienceStale = false;
    if (stale && this.ambienceRunning) this.stopAmbience();
    this.startAmbienceIfWanted();
    // Could not start (context still not running) and we had thrown the old one
    // away: stay stale so the gesture that does get through rebuilds rather than
    // resumes.
    if (stale && !this.ambienceRunning) this.ambienceStale = true;
  }

  /** Drops the entire audio graph so the next gesture can build a clean one. */
  private teardownContext(): void {
    this.stopAmbience();
    const old = this.ctx;
    this.ctx = null;
    this.master = null;
    this.sfxBus = null;
    this.musicBus = null;
    this.convolver = null;
    this.reverbReturn = null;
    this.musicSend = null;
    this.sfxSend = null;
    this.noiseBuffer = null; // buffers belong to the old context and can't be reused
    if (old) this.closeContext(old);
    audioLog(`context torn down (was ${old?.state ?? "none"})`);
  }

  private closeContext(ctx: AudioContext): void {
    this.contexts.delete(ctx);
    if (ctx.state !== "closed") void ctx.close().catch(() => undefined);
  }

  setVenue(venue: VenueId): void {
    if (this.venue === venue) return;
    audioLog(`venue ${this.venue} -> ${venue}${this.ambienceRunning ? " (restarting ambience)" : ""}`);
    this.venue = venue;
    // The room changes even when the ambience is off: `sfx` is sent to the same
    // reverb, so a muted-music player still hears the new venue in their cards.
    this.applyAcoustics();
    if (this.ambienceRunning) {
      this.stopAmbience();
      this.startAmbienceIfWanted();
    }
  }

  isMusicOn(): boolean {
    return this.musicOn;
  }
  isSfxOn(): boolean {
    return this.sfxOn;
  }
  setMusicOn(on: boolean): void {
    this.musicOn = on;
    saveFlag(MUSIC_KEY, on);
    if (on) this.startAmbienceIfWanted();
    else this.stopAmbience();
  }
  setSfxOn(on: boolean): void {
    this.sfxOn = on;
    saveFlag(SFX_KEY, on);
  }
  /** The player's music level, 0..1. */
  getMusicVolume(): number {
    return this.musicVolume;
  }
  /**
   * Sets the music level and moves the bus to it.
   *
   * RAMPED, NOT ASSIGNED, and over long enough to matter. A slider being dragged
   * fires this on every pixel, and writing straight to `gain.value` steps the
   * parameter — which on a sustained pad is a click per step and sounds like the
   * control is broken. 60ms is short enough to feel immediate and long enough to
   * be a fade.
   *
   * `cancelScheduledValues` first, because a `duck` may be in flight: without it
   * the duck's queued recovery ramp would still be there and would drag the bus
   * back to the OLD level a fraction of a second after the player let go.
   */
  setMusicVolume(value: number): void {
    this.musicVolume = Math.max(0, Math.min(1, value));
    localStorage.setItem(MUSIC_VOLUME_KEY, String(this.musicVolume));
    const ctx = this.ctx;
    if (!ctx || !this.musicBus) return;
    const t0 = ctx.currentTime;
    const g = this.musicBus.gain;
    g.cancelScheduledValues(t0);
    g.setValueAtTime(g.value, t0);
    g.linearRampToValueAtTime(this.musicLevel, t0 + 0.06);
  }
  /**
   * How close the round is to ending, 0..1, reported by the table.
   *
   * The free ambiences ignore it and play the same forever, which is the single
   * loudest tell that they are wallpaper rather than a score. A premium venue
   * reads it and tightens: more voices, less space between them.
   *
   * Deliberately a level rather than an event. The table can call this as often
   * as it likes with whatever it currently believes, and nothing has to be kept
   * in sync — a missed call is corrected by the next one.
   */
  setTension(value: number): void {
    this.tension = Math.max(0, Math.min(1, value));
  }
  getTension(): number {
    return this.tension;
  }

  /**
   * How much faster than at rest every pulse is playing now. See `startPulse`,
   * and `glideTension` for why it trails the tension rather than reading it.
   */
  private tempo(): number {
    const now = this.ctx?.currentTime ?? 0;
    this.tempoTension = glideTension(this.tempoTension, this.tension, now - this.tempoAt);
    this.tempoAt = now;
    return tensionTempo(this.tempoTension);
  }

  /**
   * Dev: an analyser hanging off the master output, for the music lab's meters.
   * A tap, not an insert - nothing it does reaches the speakers. Rebuilt when
   * the context is, because a node belongs to the context that made it.
   */
  meter(): AnalyserNode | null {
    const ctx = this.ctx;
    if (!ctx || !this.master) return null;
    if (this.meterNode?.context !== ctx) {
      const a = ctx.createAnalyser();
      a.fftSize = 8192;
      a.smoothingTimeConstant = 0.7;
      this.master.connect(a);
      this.meterNode = a;
    }
    return this.meterNode;
  }

  /** Reports an ambience note to `noteListener`. Effects play into the sfx bus and are skipped. */
  private noted(out: AudioNode, note: MusicNote): void {
    if (this.noteListener && out !== this.sfxBus) this.noteListener(note);
  }

  /**
   * Dips the music for a moment so something loud can land in the hole.
   *
   * This is why a slam suddenly sounds bigger without being any louder: the
   * effect is competing with less. Recovery is to `musicLevel` rather than to
   * the parameter's current value, so overlapping ducks cannot walk the music
   * down and leave it there — which is exactly what happens during a Five Out,
   * where several of these land inside a second.
   */
  private duck(depth: number, seconds: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.musicBus || !this.musicOn) return;
    const t0 = ctx.currentTime;
    const g = this.musicBus.gain;
    g.cancelScheduledValues(t0);
    g.setValueAtTime(g.value, t0);
    g.linearRampToValueAtTime(this.musicLevel * (1 - depth), t0 + 0.05);
    g.linearRampToValueAtTime(this.musicLevel, t0 + 0.05 + seconds);
  }

  /** HUD mute: anything on -> everything off; everything off -> everything on. */
  toggleAll(): boolean {
    const next = !(this.musicOn || this.sfxOn);
    this.setMusicOn(next);
    this.setSfxOn(next);
    return next;
  }

  // ---- plumbing -----------------------------------------------------------

  private ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctor) return null;
    // Only one context should exist; one still open here is left over from a
    // rebuild that did not finish closing it, and anything it still plays is
    // music from a room the player has left.
    for (const stale of [...this.contexts]) this.closeContext(stale);
    this.ctx = new Ctor();
    this.contexts.add(this.ctx);
    const created = this.ctx;
    created.addEventListener?.("statechange", () => audioLog(`context ${created === this.ctx ? "current" : "old"}: ${created.state}`));
    audioLog(`context created (${this.ctx.state})`);
    this.master = this.ctx.createGain();
    this.master.gain.value = 1;
    this.master.connect(this.ctx.destination);
    this.sfxBus = this.ctx.createGain();
    this.sfxBus.gain.value = 0.6;
    this.sfxBus.connect(this.master);
    this.musicBus = this.ctx.createGain();
    this.musicBus.gain.value = this.musicLevel;
    this.musicBus.connect(this.master);

    // Reverb, as a send rather than in-line: the dry signal keeps its transient
    // (a card flip has to stay crisp) and the room is added behind it. In-line
    // convolution would smear every one-shot the game is made of.
    this.convolver = this.ctx.createConvolver();
    this.reverbReturn = this.ctx.createGain();
    this.convolver.connect(this.reverbReturn).connect(this.master);
    this.musicSend = this.ctx.createGain();
    this.musicSend.connect(this.convolver);
    this.musicBus.connect(this.musicSend);
    this.sfxSend = this.ctx.createGain();
    this.sfxSend.connect(this.convolver);
    this.sfxBus.connect(this.sfxSend);
    this.applyAcoustics();
    return this.ctx;
  }

  /**
   * Points the reverb at the current venue's room.
   *
   * Called when the graph is built and on every venue change. The impulse is
   * regenerated rather than cached per venue: it is a fraction of a second of
   * arithmetic, it only happens when somebody changes rooms, and caching seven
   * of them would hold a few megabytes of buffer for a switch most players make
   * once.
   */
  private applyAcoustics(): void {
    const ctx = this.ctx;
    if (!ctx || !this.convolver || !this.reverbReturn || !this.musicSend || !this.sfxSend) return;
    const room = VENUE_ACOUSTICS[this.venue] ?? VENUE_ACOUSTICS.coastal;
    this.convolver.buffer = makeImpulse(ctx, room.seconds, room.bright);
    this.reverbReturn.gain.value = 1;
    this.musicSend.gain.value = room.send;
    // Effects sit further forward than the ambience does, always. A card slide
    // drowned in a cathedral stops reading as a card sliding — the room is
    // supposed to be behind the game, not on top of it.
    this.sfxSend.gain.value = room.send * 0.45;
  }

  private getNoise(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer;
    const len = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;
    return buf;
  }

  /** Filtered noise one-shot: the basis of all the "physical" sounds. */
  private noiseHit(opts: {
    duration: number;
    gain: number;
    type: BiquadFilterType;
    freqFrom: number;
    freqTo?: number;
    attack?: number;
    when?: number;
  }): void {
    if (!this.sfxOn) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxBus) return;
    const t0 = ctx.currentTime + (opts.when ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.getNoise(ctx);
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = opts.type;
    filter.frequency.setValueAtTime(opts.freqFrom, t0);
    if (opts.freqTo !== undefined) filter.frequency.exponentialRampToValueAtTime(Math.max(30, opts.freqTo), t0 + opts.duration);
    filter.Q.value = 0.9;
    const g = ctx.createGain();
    const attack = opts.attack ?? 0.008;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(opts.gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);
    src.connect(filter).connect(g).connect(this.sfxBus);
    src.start(t0);
    src.stop(t0 + opts.duration + 0.05);
  }

  /** Simple pitched one-shot. */
  private tone(opts: {
    freq: number;
    freqTo?: number;
    duration: number;
    gain: number;
    type?: OscillatorType;
    when?: number;
    attack?: number;
  }): void {
    if (!this.sfxOn) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxBus) return;
    const t0 = ctx.currentTime + (opts.when ?? 0);
    const osc = ctx.createOscillator();
    osc.type = opts.type ?? "triangle";
    osc.frequency.setValueAtTime(opts.freq, t0);
    if (opts.freqTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.freqTo), t0 + opts.duration);
    const g = ctx.createGain();
    const attack = opts.attack ?? 0.01;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(opts.gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);
    osc.connect(g).connect(this.sfxBus);
    osc.start(t0);
    osc.stop(t0 + opts.duration + 0.05);
  }

  // ---- one-shot effects ---------------------------------------------------

  /**
   * Card sliding across felt: a soft friction swish.
   *
   * Also what a screen sliding in or out of the tray sounds like, which is not a
   * reach: both are one flat thing dragging over another, and the sound of that
   * is friction rather than the object. Keeping it one method means the menus
   * and the table cannot end up with two different ideas of what sliding sounds
   * like in the same room.
   */
  cardSlide(): void {
    this.noiseHit({ duration: 0.16, gain: 0.1, type: "bandpass", freqFrom: 1900, freqTo: 700, attack: 0.02 });
  }

  /** Card flip: a crisp little snap. */
  cardFlip(): void {
    this.noiseHit({ duration: 0.05, gain: 0.09, type: "highpass", freqFrom: 2400 });
    this.tone({ freq: 620, duration: 0.05, gain: 0.03, type: "square" });
  }

  /**
   * The brass slider that pushes a dealt spread off the screen between rounds
   * (`ui/transitions`): metal travelling over wood, with a faint ring on it.
   *
   * Built from the same parts as `lockThud`, which is the sound of the brass
   * ARRIVING — a slider that stopped with an unrelated noise would read as two
   * objects rather than one moving and landing. The band is deliberately narrow:
   * this plays on a navigation rather than on a move, so it has to sit under
   * whatever the player is already listening to.
   */
  railSlide(): void {
    this.noiseHit({ duration: 0.42, gain: 0.055, type: "bandpass", freqFrom: 700, freqTo: 2100, attack: 0.09 });
    this.tone({ freq: 210, freqTo: 320, duration: 0.4, gain: 0.028, type: "triangle", attack: 0.1 });
  }

  /**
   * A pack of cards blown off the table in one gust: broad air, no transient.
   *
   * The absence of an edge is the whole of it. Every other card sound in here is
   * an impact of some kind, and a bright attack on this one would read as
   * something landing at the exact moment the screen is being uncovered.
   */
  packSweep(): void {
    this.noiseHit({ duration: 0.46, gain: 0.07, type: "bandpass", freqFrom: 2400, freqTo: 380, attack: 0.07 });
  }

  /** Correct guess: a small upward two-note chime. */
  correct(): void {
    this.tone({ freq: 659, duration: 0.22, gain: 0.09 });
    this.tone({ freq: 988, duration: 0.28, gain: 0.08, when: 0.09 });
  }

  /** Wrong guess: dull descending thud — clear but not punishing. */
  wrong(): void {
    this.tone({ freq: 233, freqTo: 165, duration: 0.3, gain: 0.11, type: "sine" });
    this.noiseHit({ duration: 0.12, gain: 0.06, type: "lowpass", freqFrom: 500 });
  }

  /** Bonus placements / off-by-one rewards: a light sparkle. */
  sparkle(): void {
    this.tone({ freq: 880, duration: 0.12, gain: 0.05 });
    this.tone({ freq: 1109, duration: 0.12, gain: 0.05, when: 0.06 });
    this.tone({ freq: 1319, duration: 0.16, gain: 0.05, when: 0.12 });
  }

  /** Bonus card spinning through the air. */
  spin(): void {
    this.noiseHit({ duration: 0.34, gain: 0.06, type: "bandpass", freqFrom: 900, freqTo: 2400, attack: 0.06 });
  }

  /**
   * One link of a combo run landing: a bell note a step higher each time.
   *
   * Climbs a major pentatonic from the E the `correct` chime ends near, so a run
   * is heard as one phrase going up rather than the same note repeated - that
   * rising is what makes the fourth card of a chain feel different from the
   * first. Pentatonic because any two steps of it sound fine together, which
   * matters when a landing and the next spin overlap. Past two octaves it holds
   * the top note rather than squeaking off into dog-whistle range.
   */
  comboStep(link: number): void {
    const steps = [0, 2, 4, 7, 9];
    const n = Math.min(Math.max(link - 2, 0), 10);
    const semis = steps[n % 5]! + 12 * Math.floor(n / 5);
    const freq = 659 * Math.pow(2, semis / 12);
    this.tone({ freq, duration: 0.2, gain: 0.07 });
    this.tone({ freq: freq * 2, duration: 0.12, gain: 0.025, when: 0.02 });
  }

  /**
   * A combo run closing. `maxed` is a chain that ran into 13 or 1, and gets the
   * whole fanfare with a shimmer on top; an ordinary finish is one resolved
   * chord, a full stop rather than a celebration.
   */
  comboFinish(maxed: boolean): void {
    if (maxed) {
      this.duck(0.45, 1);
      [784, 988, 1175, 1568].forEach((f, i) => this.tone({ freq: f, duration: 0.34, gain: 0.07, when: i * 0.06 }));
      this.noiseHit({ duration: 0.6, gain: 0.05, type: "highpass", freqFrom: 3200, attack: 0.04, when: 0.22 });
      return;
    }
    [523, 659, 784].forEach((f) => this.tone({ freq: f, duration: 0.32, gain: 0.05, attack: 0.006 }));
  }

  /** A combo cut short by a wrong call: the counter cracking. */
  comboBreak(): void {
    this.noiseHit({ duration: 0.09, gain: 0.07, type: "highpass", freqFrom: 2800 });
    this.tone({ freq: 740, freqTo: 330, duration: 0.18, gain: 0.04, type: "square" });
  }

  /** Five Out celebration fanfare. */
  fiveOut(): void {
    this.duck(0.45, 1.1);
    const notes = [523, 659, 784, 1047, 1319];
    notes.forEach((f, i) => this.tone({ freq: f, duration: 0.3, gain: 0.07, when: i * 0.07 }));
    this.noiseHit({ duration: 0.5, gain: 0.05, type: "highpass", freqFrom: 3000, attack: 0.05, when: 0.28 });
  }

  /**
   * Hallowed Ground's Five Out reaction: a flurry of bats bursting across the
   * table — leathery wing-beats and a few tiny squeaks, over in under a second.
   *
   * It lands ON TOP of `fiveOut`, which is why it is quiet and why it does not
   * duck: the fanfare has already pulled the music down, and a second duck here
   * would cancel that one's recovery and restart it, holding the room in a hole
   * for longer than the moment deserves. This is garnish on the fanfare, not a
   * second fanfare.
   *
   * The wings are what sell it, and they are a RHYTHM rather than a sound: a
   * bat beats its wings ten-odd times a second, so each bat is a short run of
   * soft mid-band pops about 70ms apart, and several runs overlap slightly out
   * of step. One run is a moth; four staggered ones are a flurry. The pops are
   * mid-band and short on purpose — a membrane wing is a flap, not a whoosh, and
   * anything longer or brighter reads as the card-slide sounds this table is
   * already full of.
   *
   * The chirps sit up at 6-9kHz, very short and very quiet, sweeping DOWN the
   * way a bat's call does. Nobody hears a real one (they are mostly ultrasonic),
   * but everybody knows what a cartoon bat sounds like, and that is the room
   * this is: creepy for fun.
   */
  batFlurry(): void {
    const bats = 3 + Math.floor(Math.random() * 2);
    for (let b = 0; b < bats; b++) {
      const start = b * 0.11 + Math.random() * 0.06;
      const beat = 0.06 + Math.random() * 0.025;
      const flaps = 3 + Math.floor(Math.random() * 3);
      const band = 700 + Math.random() * 700;
      for (let i = 0; i < flaps; i++) {
        this.noiseHit({
          duration: 0.035 + Math.random() * 0.02,
          // Each bat fades as it passes and is gone, rather than every flap
          // landing at one level, which is a clock ticking.
          gain: (0.03 + Math.random() * 0.012) * (1 - i / (flaps + 1)),
          type: "bandpass",
          freqFrom: band,
          freqTo: band * 0.7,
          attack: 0.006,
          when: start + i * beat,
        });
      }
    }
    const chirps = 3 + Math.floor(Math.random() * 3);
    for (let i = 0; i < chirps; i++) {
      const f = 6000 + Math.random() * 3000;
      this.tone({
        freq: f,
        freqTo: f * 0.8,
        duration: 0.03 + Math.random() * 0.02,
        gain: 0.008 + Math.random() * 0.006,
        type: "sine",
        attack: 0.004,
        when: 0.05 + Math.random() * 0.72,
      });
    }
  }

  /**
   * The Mead Hall's Five Out reaction: fire catching along a line of carved
   * runes. A soft whoosh climbing from the low mids to the top of the room's
   * range and falling away, with one low bowed fifth swelling underneath it -
   * the lyre's open strings, E and B, so the moment is in the room's key.
   *
   * Like `batFlurry` it lands ON TOP of `fiveOut`, and it does not duck for the
   * reason that one gives: `duck` cancels whatever is scheduled and starts from
   * the bus's current level, so a second duck issued while the fanfare's is
   * still running replaces its hole with a shallower one and shortens it.
   * The fanfare has already cleared the room for this.
   *
   * The whoosh is bandpassed noise rather than a tone because fire catching has
   * no pitch, and it stays under 1.2kHz because the fanfare above it already
   * owns the 2-5kHz band. It rises for most of a second and falls over the rest
   * (the envelope peaks late, the sweep keeps going), which is what a flame
   * running out along a groove does: it arrives faster than it leaves.
   */
  runeKindle(): void {
    this.noiseHit({ duration: 1.5, gain: 0.07, type: "bandpass", freqFrom: 200, freqTo: 1200, attack: 0.8 });
    if (!this.sfxOn) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxBus) return;
    const t0 = ctx.currentTime;
    this.bowed(ctx, this.sfxBus, t0, { freq: 82.41, gain: 0.05, seconds: 1, attack: 0.5, release: 0.6, bright: 0.5 });
    this.bowed(ctx, this.sfxBus, t0 + 0.08, { freq: 123.47, gain: 0.04, seconds: 0.95, attack: 0.5, release: 0.6, bright: 0.5 });
  }

  /**
   * The Singularity's Five Out reaction: a black hole opening, drinking the
   * starlight for about a second and a third, collapsing, and a gravitational
   * wave rolling out of it. An inhaled swell of noise and a sine falling from
   * 140Hz to 30Hz are the drinking; a deep soft boom with a long tail, a beat
   * after the fall ends, is the wave. Under two and a half seconds in all.
   *
   * Like `runeKindle` it lands ON TOP of `fiveOut` and does not duck, for the
   * reason that one gives: `duck` cancels whatever is scheduled and starts from
   * the bus's current level, so a second duck here would replace the fanfare's
   * hole with a shallower one and cut its recovery short.
   *
   * The inhale is bandpassed noise whose envelope peaks late, so it is a breath
   * drawn IN and then let go, not a whoosh that starts at full strength. It
   * climbs only to 1.1kHz because the fanfare above it owns the 2-5kHz band.
   * Everything that is meant to be felt sits under 150Hz, which a phone speaker
   * cannot play, so the fall and the boom each carry a quieter octave above
   * (the trick `call` and `frameDrum` use) or the moment would vanish on exactly
   * the devices most rounds are played on.
   *
   * The boom's pitch is C, the room's tonic, and it falls a little as it dies:
   * a wave passing, not a drum hit.
   */
  singularity(): void {
    this.noiseHit({ duration: 1.35, gain: 0.06, type: "bandpass", freqFrom: 220, freqTo: 1100, attack: 0.95 });
    this.tone({ freq: 140, freqTo: 30, duration: 1.3, gain: 0.1, type: "sine", attack: 0.35 });
    this.tone({ freq: 280, freqTo: 60, duration: 1.3, gain: 0.032, type: "sine", attack: 0.35 });
    this.tone({ freq: 65.41, freqTo: 44, duration: 1.0, gain: 0.14, type: "sine", attack: 0.02, when: 1.4 });
    this.tone({ freq: 130.81, freqTo: 88, duration: 0.5, gain: 0.04, type: "sine", attack: 0.02, when: 1.4 });
    this.noiseHit({ duration: 0.4, gain: 0.05, type: "lowpass", freqFrom: 220, freqTo: 60, attack: 0.03, when: 1.4 });
  }

  /** Wild card lifting off before a slam: rising whoosh. */
  slamRise(): void {
    this.noiseHit({ duration: 0.3, gain: 0.09, type: "bandpass", freqFrom: 400, freqTo: 2300, attack: 0.08 });
  }

  /** Wild card impact: a real boom, still capped so it never blows out ears. */
  slamImpact(): void {
    this.duck(0.55, 0.5);
    this.tone({ freq: 150, freqTo: 42, duration: 0.22, gain: 0.28, type: "sine", attack: 0.004 });
    this.noiseHit({ duration: 0.16, gain: 0.16, type: "lowpass", freqFrom: 900, freqTo: 200, attack: 0.004 });
  }

  /**
   * A Vinny's Rule block landing on a pile it closes: a dull, deep thud, all
   * weight and no snap.
   *
   * Three layers, and which one does what matters if these numbers are ever
   * retuned:
   *
   *  - The fundamental carries the weight, sweeping down to near-subsonic.
   *  - A low-mid body layer sits above it, because the fundamental alone is
   *    inaudible on a phone speaker — without this the thud simply vanishes on
   *    the devices most of these rounds are played on.
   *  - A heavily damped noise burst is the contact itself. Its ATTACK is the
   *    setting that decides whether this reads as a thud or a click; a near-zero
   *    onset here is what made an earlier version sound sharp and plasticky, so
   *    it is deliberately slow.
   *
   * Nothing above ~250Hz survives the filter, which is the point: a bright tail
   * on top of this reads as a tap on a hard surface rather than a weight landing.
   *
   * Levels stay under slamImpact's. Up to four of these land within a
   * quarter-second of each other, so what is a satisfying single boom for a
   * Smash Card would be a pile-up here — and unlike a slam this is incidental
   * furniture, not the point of the turn.
   */
  lockThud(): void {
    this.tone({ freq: 96, freqTo: 33, duration: 0.3, gain: 0.18, type: "sine", attack: 0.011 });
    this.tone({ freq: 184, freqTo: 76, duration: 0.13, gain: 0.07, type: "sine", attack: 0.011 });
    this.noiseHit({ duration: 0.085, gain: 0.05, type: "lowpass", freqFrom: 240, freqTo: 70, attack: 0.015 });
  }

  /**
   * A deck catching fire on its fifth clean turn (Jourdan's Streaks): a rising
   * whoosh with a warm swell under it.
   *
   * Deliberately quieter and softer-edged than any of the card effects. It fires
   * on a state change nobody asked for, at a moment when something else has just
   * finished resolving, so it has to read as "look over here" rather than as an
   * impact of its own — a bright transient here would land on top of the sound
   * of the turn that earned it.
   */
  deckIgnite(): void {
    this.noiseHit({ duration: 0.55, gain: 0.07, type: "bandpass", freqFrom: 300, freqTo: 2600, attack: 0.16 });
    this.tone({ freq: 180, freqTo: 520, duration: 0.5, gain: 0.05, type: "sine", attack: 0.14 });
  }

  /**
   * A deck wrapped in Drought's mist: a cold breath rather than `deckIgnite`'s
   * rush - a slow airy hiss falling instead of rising, under a low hollow note.
   * Quiet, for the ignite's reason: it lands on the end of the turn that earned
   * it, and that turn was the player's third bad one.
   */
  deckMist(): void {
    this.noiseHit({ duration: 0.8, gain: 0.06, type: "bandpass", freqFrom: 2200, freqTo: 500, attack: 0.25 });
    this.tone({ freq: 330, freqTo: 247, duration: 0.7, gain: 0.04, type: "sine", attack: 0.2 });
  }

  /**
   * The mist firing at a pile and bursting it, scheduled on the times
   * `animateDroughtMist` draws to: a gust out of the deck, then at `burst`
   * seconds a soft, wide pop - air, not an explosion - with a cool two-note
   * chord rising out of it, since this is help arriving.
   */
  droughtMist(burst: number): void {
    this.noiseHit({ duration: burst + 0.05, gain: 0.07, type: "bandpass", freqFrom: 600, freqTo: 3000, attack: burst * 0.8 });
    this.tone({ freq: 110, freqTo: 40, duration: 0.5, gain: 0.16, type: "sine", attack: 0.004, when: burst });
    this.noiseHit({ duration: 0.7, gain: 0.1, type: "lowpass", freqFrom: 3200, freqTo: 300, attack: 0.01, when: burst });
    [587.33, 880].forEach((freq, i) => {
      this.tone({ freq, duration: 0.9, gain: 0.03, type: "sine", attack: 0.03, when: burst + 0.04 + i * 0.07 });
    });
  }

  /**
   * A Burning Deck's top card burning away: `fireCrackle`'s paper fire for the
   * length of the burn (`animateDeckBurn`), and a low breath under it as the
   * flame takes hold. Quieter than a meteor's roar - nothing struck the deck, it
   * just caught.
   */
  deckBurn(seconds: number): void {
    this.tone({ freq: 150, freqTo: 70, duration: seconds, gain: 0.07, type: "sine", attack: 0.05 });
    this.fireCrackle(seconds, 0.7);
  }

  /** Spending a streak: the fire going out, in one short downward gust. */
  streakSpent(): void {
    this.noiseHit({ duration: 0.34, gain: 0.09, type: "bandpass", freqFrom: 2800, freqTo: 420, attack: 0.03 });
    this.tone({ freq: 520, freqTo: 140, duration: 0.3, gain: 0.06, type: "sine", attack: 0.02 });
  }

  /** Lightning card charging up before the bolts fly: a rising electric whine. */
  lightningCharge(): void {
    this.tone({ freq: 320, freqTo: 1800, duration: 0.26, gain: 0.07, type: "sawtooth", attack: 0.06 });
    this.noiseHit({ duration: 0.26, gain: 0.05, type: "highpass", freqFrom: 2200, freqTo: 5200, attack: 0.08 });
  }

  /** One bolt landing: a bright crack with a short vaporizing hiss. */
  lightningZap(): void {
    this.duck(0.4, 0.35);
    this.noiseHit({ duration: 0.09, gain: 0.13, type: "highpass", freqFrom: 4200, attack: 0.003 });
    this.tone({ freq: 1760, freqTo: 420, duration: 0.14, gain: 0.07, type: "square", attack: 0.003 });
    this.noiseHit({ duration: 0.22, gain: 0.05, type: "bandpass", freqFrom: 3200, freqTo: 900, attack: 0.02, when: 0.05 });
  }

  /**
   * A bolt glancing off a wild: the same crack, but it RINGS instead of hissing
   * away — a struck bell held over a hiss that climbs and scatters rather than
   * one that falls and dies.
   *
   * The two have to be told apart with a pile of cards in the way, so the
   * difference is carried by the tail, which is the part still sounding after
   * the transient: `lightningZap` sweeps down (1760 → 420) into a decaying
   * filter, and this holds two steady partials a fifth apart for three times as
   * long. Pitch that stays put is the signal — nothing else on this table rings.
   */
  lightningResist(): void {
    this.duck(0.3, 0.4);
    // The impact itself, thinner and shorter than the zap's: the bolt arrives
    // but stops dead, so there is no body behind the crack.
    this.noiseHit({ duration: 0.05, gain: 0.1, type: "highpass", freqFrom: 5200, attack: 0.002 });
    // The ring: a struck-metal fifth, the upper partial a touch quieter and
    // fading first, the way a real one loses its overtones on the way out.
    this.tone({ freq: 1245, duration: 0.42, gain: 0.06, type: "triangle", attack: 0.004 });
    this.tone({ freq: 1868, duration: 0.3, gain: 0.035, type: "sine", attack: 0.004 });
    // The deflected energy going somewhere else: a short hiss sweeping UP and
    // out, the mirror of the zap's dying fall.
    this.noiseHit({ duration: 0.26, gain: 0.05, type: "bandpass", freqFrom: 1400, freqTo: 5000, attack: 0.03, when: 0.03 });
  }

  /**
   * A Swap Card: two cards passing each other in the air.
   *
   * Two short gusts sweeping in OPPOSITE directions, one rising and one falling,
   * overlapped by a few milliseconds — the sound of two things crossing rather
   * than one thing moving, which is what tells it apart from `cardSlide` and the
   * bonus `spin`. Plays both when the Swap is owed and when the tops actually
   * trade places, so the two halves of the one card sound like the same card.
   * No transient: nothing lands on anything.
   */
  swapWhoosh(): void {
    this.noiseHit({ duration: 0.26, gain: 0.07, type: "bandpass", freqFrom: 700, freqTo: 2600, attack: 0.05 });
    this.noiseHit({ duration: 0.26, gain: 0.07, type: "bandpass", freqFrom: 2600, freqTo: 700, attack: 0.05, when: 0.07 });
    this.tone({ freq: 440, freqTo: 660, duration: 0.18, gain: 0.025, type: "sine", attack: 0.04, when: 0.04 });
  }

  /**
   * A Peek Card: a soft, glassy two-note chime, the second note held a touch.
   *
   * A chime rather than a hit because nothing happens on the table — the card
   * changes what one player knows, not where anything is — and quiet because
   * the only person it matters to is the one who played it. A rising fourth,
   * high and in sine: `correct` is a rising fifth in triangle an octave lower,
   * and the two can land in the same turn, so they must not be mistaken.
   */
  peekChime(): void {
    this.tone({ freq: 1319, duration: 0.2, gain: 0.05, type: "sine", attack: 0.008 });
    this.tone({ freq: 1760, duration: 0.34, gain: 0.045, type: "sine", attack: 0.01, when: 0.08 });
    this.noiseHit({ duration: 0.18, gain: 0.02, type: "highpass", freqFrom: 5200, attack: 0.03, when: 0.05 });
  }

  /**
   * A wrapped gift landing on the score pad: a soft, curious two-note lift.
   *
   * Quiet and unhurried on purpose. It arrives after the round's own celebration
   * has played, on a screen the player is reading, and its job is to make them
   * look — not to be a third bang on top of the podium and the fireworks.
   */
  giftAppear(): void {
    this.tone({ freq: 587, duration: 0.26, gain: 0.055, attack: 0.04 });
    this.tone({ freq: 880, duration: 0.34, gain: 0.05, attack: 0.05, when: 0.12 });
    this.noiseHit({ duration: 0.3, gain: 0.03, type: "highpass", freqFrom: 3600, attack: 0.1, when: 0.1 });
  }

  /**
   * The gift being torn open: paper and ribbon, then the prize.
   *
   * Two halves, and the gap between them is the point — the rip is the tap being
   * answered, and the chord is what the player waited to hear. `duck` pulls the
   * ambience down under both so the fanfare is not fighting a room.
   */
  giftOpen(): void {
    this.duck(0.5, 1.2);
    // The wrapping: a short, bright tear with no pitch to it.
    this.noiseHit({ duration: 0.22, gain: 0.11, type: "bandpass", freqFrom: 2600, freqTo: 5200, attack: 0.01 });
    this.noiseHit({ duration: 0.16, gain: 0.06, type: "highpass", freqFrom: 4200, attack: 0.02, when: 0.09 });
    // The prize: a major triad opening upward, an octave above the arrival chime.
    const notes = [523, 659, 784, 1047];
    notes.forEach((f, i) => this.tone({ freq: f, duration: 0.5, gain: 0.07, attack: 0.02, when: 0.18 + i * 0.06 }));
    this.tone({ freq: 1568, duration: 0.6, gain: 0.05, attack: 0.06, when: 0.42 });
    // The sparks going up after it.
    this.noiseHit({ duration: 0.7, gain: 0.04, type: "highpass", freqFrom: 3000, attack: 0.12, when: 0.36 });
  }

  /**
   * A level gained, on the score pad: a brass fanfare climbing two octaves, a
   * held top note, and sparks. Bigger than `giftOpen` on purpose - a level is
   * the long game paying off, and it should sound like the biggest thing the
   * score pad does.
   */
  levelUp(): void {
    this.duck(0.45, 1.8);
    const climb = [392, 523, 659, 784, 1047];
    climb.forEach((f, i) => this.tone({ freq: f, duration: 0.22, gain: 0.065, attack: 0.01, when: i * 0.07 }));
    // The landing chord, held.
    [1047, 1319, 1568].forEach((f) => this.tone({ freq: f, duration: 0.9, gain: 0.05, attack: 0.03, when: 0.38 }));
    this.tone({ freq: 2093, duration: 0.7, gain: 0.03, attack: 0.08, when: 0.5 });
    this.noiseHit({ duration: 0.9, gain: 0.045, type: "highpass", freqFrom: 3200, attack: 0.1, when: 0.4 });
  }
  /**
   * The red cross landing on the card that lost the pile: a flat rubber-stamp
   * smack, low and dry. Deliberately not a buzzer - `wrong` has already said
   * "no" a moment earlier, and this is the table filing the paperwork.
   */
  verdictStamp(): void {
    this.noiseHit({ duration: 0.07, gain: 0.09, type: "lowpass", freqFrom: 900, freqTo: 260, attack: 0.003 });
    this.tone({ freq: 140, freqTo: 90, duration: 0.12, gain: 0.08, type: "sine", attack: 0.004 });
  }

  /**
   * A taken pile arriving on its new owner's deck. `weight` is the pile's size
   * (capped, see STACK_WEIGHT_CARD_CAP), and it sets how deep and how long the
   * thud is - two cards drop onto a deck, a dozen land on it.
   */
  pileLanded(weight: number): void {
    const w = Math.min(Math.max(weight, 1), 12) / 12;
    this.tone({ freq: 150 - 60 * w, freqTo: 45, duration: 0.14 + 0.2 * w, gain: 0.08 + 0.12 * w, type: "sine", attack: 0.006 });
    this.noiseHit({ duration: 0.08 + 0.1 * w, gain: 0.04 + 0.04 * w, type: "lowpass", freqFrom: 420, freqTo: 90, attack: 0.01 });
  }

  /**
   * A card hanging in suspense before it turns: two heartbeats, lub-dub, low
   * enough to sit under the room rather than on top of it. Played once per
   * suspense hold, which is timed to fit exactly one pair.
   */
  suspenseBeat(): void {
    this.duck(0.25, 0.6);
    for (const [when, gain] of [
      [0, 0.16],
      [0.17, 0.11],
    ] as const) {
      this.tone({ freq: 72, freqTo: 40, duration: 0.16, gain, type: "sine", attack: 0.008, when });
      this.noiseHit({ duration: 0.05, gain: gain * 0.25, type: "lowpass", freqFrom: 220, freqTo: 80, attack: 0.006, when });
    }
  }

  /** Taking a stack into your deck: a quick shuffle of slides. */
  stackTaken(): void {
    this.noiseHit({ duration: 0.12, gain: 0.07, type: "bandpass", freqFrom: 1600, freqTo: 800 });
    this.noiseHit({ duration: 0.12, gain: 0.07, type: "bandpass", freqFrom: 1900, freqTo: 900, when: 0.07 });
    this.noiseHit({ duration: 0.14, gain: 0.07, type: "bandpass", freqFrom: 1400, freqTo: 700, when: 0.15 });
  }

  // ---- Starfall (docs/EVENTS.md) ------------------------------------------
  //
  // One family, because it is one theme on the table: everything here is high,
  // glassy and sine-built, and nothing else in the game rings like that. The
  // card sounds are noise and triangle; a player has to hear "the sky" rather
  // than "another card".

  /**
   * A star lit: a constellation drawing itself, then the star landing in its
   * socket. Pitched by `stars`, the meter after it - each star a step up a major
   * triad, so a filling meter is heard climbing towards the event. The landing
   * ping is timed to the flight's arrival (~0.8s in), not to the call.
   */
  starCharge(stars: number, steps = 0): void {
    const rise = [0, 4, 7, 12][Math.min(Math.max(stars - 1, 0), 3)];
    const base = 784 * Math.pow(2, rise / 12);
    // The lines drawing: three quick, soft notes a fifth apart - or, for a
    // staircase of `steps` cards, one note a card, climbing a major scale in
    // time with the cards lighting low to high (the renderer's climb is 0.33s).
    const notes = steps > 0 ? [0, 2, 4, 5, 7].slice(0, steps).map((semi) => Math.pow(2, semi / 12)) : [1, 1.5, 2];
    const gap = steps > 1 ? 0.33 / (steps - 1) : 0.1;
    notes.forEach((k, i) => this.tone({ freq: base * k * 0.5, duration: 0.18, gain: 0.035, type: "sine", attack: 0.01, when: i * gap }));
    this.noiseHit({ duration: 0.4, gain: 0.02, type: "highpass", freqFrom: 5200, attack: 0.08, when: 0.3 });
    // The star arriving: a clear bell with its octave.
    this.tone({ freq: base, duration: 0.5, gain: 0.07, type: "sine", attack: 0.005, when: 0.82 });
    this.tone({ freq: base * 2, duration: 0.3, gain: 0.03, type: "sine", attack: 0.005, when: 0.83 });
  }

  /**
   * A full meter firing: the room drawing breath. A slow shimmering swell - a
   * stack of detuned sines rising together under a hiss of air - that peaks as
   * the event card turns. The only Starfall sound that ducks the music, the same
   * way `fiveOut` does: this is the table stopping for something.
   */
  starfallReveal(): void {
    this.duck(0.5, 1.6);
    [392, 494, 587, 784].forEach((f, i) => {
      this.tone({ freq: f * 0.5, freqTo: f, duration: 0.9, gain: 0.04, type: "sine", attack: 0.45, when: i * 0.03 });
      this.tone({ freq: f * 1.005 * 0.5, freqTo: f * 1.005, duration: 0.9, gain: 0.02, type: "sine", attack: 0.45, when: i * 0.03 });
    });
    this.noiseHit({ duration: 0.8, gain: 0.05, type: "bandpass", freqFrom: 600, freqTo: 4200, attack: 0.5 });
    // The card turning over at the top of the swell.
    [1175, 1568].forEach((f, i) => this.tone({ freq: f, duration: 0.6, gain: 0.05, type: "sine", attack: 0.005, when: 0.5 + i * 0.05 }));
  }

  /** A comet leaving its deck: a rising rush that catches fire half-way. */
  cometWhoosh(): void {
    this.noiseHit({ duration: 0.5, gain: 0.08, type: "bandpass", freqFrom: 500, freqTo: 3400, attack: 0.12 });
    this.noiseHit({ duration: 0.3, gain: 0.04, type: "highpass", freqFrom: 3000, freqTo: 6000, attack: 0.05, when: 0.14 });
  }

  /** A comet landing: a heavy thump with a bright crackle, lighter than a slam. */
  cometImpact(): void {
    this.tone({ freq: 190, freqTo: 55, duration: 0.2, gain: 0.16, type: "sine", attack: 0.004 });
    this.noiseHit({ duration: 0.14, gain: 0.1, type: "lowpass", freqFrom: 1400, freqTo: 250, attack: 0.004 });
    this.noiseHit({ duration: 0.3, gain: 0.04, type: "highpass", freqFrom: 4500, attack: 0.01, when: 0.03 });
  }

  /**
   * The whole Card Storm, scheduled from its first frame: wind rising as the
   * vortex forms, a rumble under the orbit, and a patter as the cards rain down.
   * One call so the three halves cannot drift apart from each other; the times
   * are the ones `animateCardStorm` draws to.
   */
  cardStorm(): void {
    this.duck(0.45, 3);
    // Wind: two bands sweeping up, the second a touch behind.
    this.noiseHit({ duration: 1.9, gain: 0.07, type: "bandpass", freqFrom: 300, freqTo: 2400, attack: 0.9 });
    this.noiseHit({ duration: 1.7, gain: 0.04, type: "bandpass", freqFrom: 900, freqTo: 4200, attack: 0.8, when: 0.15 });
    // Rumble under the orbit.
    this.tone({ freq: 55, freqTo: 42, duration: 1.4, gain: 0.12, type: "sine", attack: 0.5, when: 0.4 });
    this.noiseHit({ duration: 1.2, gain: 0.06, type: "lowpass", freqFrom: 260, freqTo: 120, attack: 0.5, when: 0.5 });
    // The burst.
    this.tone({ freq: 160, freqTo: 48, duration: 0.3, gain: 0.14, type: "sine", attack: 0.004, when: 1.85 });
    this.noiseHit({ duration: 0.4, gain: 0.07, type: "highpass", freqFrom: 2600, attack: 0.01, when: 1.85 });
    // The rain: soft card taps, closer together as it thins out.
    for (let i = 0; i < 14; i++) {
      this.noiseHit({
        duration: 0.05,
        gain: 0.05 * (0.6 + Math.random() * 0.4),
        type: "bandpass",
        freqFrom: 1500 + Math.random() * 900,
        freqTo: 700,
        attack: 0.004,
        when: 2.3 + i * 0.04 + Math.random() * 0.02,
      });
    }
  }

  /**
   * A meteor coming in: a roar that falls in pitch as it nears, with a crackle
   * riding on it. `seconds` is its flight, so the roar peaks as it lands. The
   * big one is a longer, lower roar with a rumble under it, starting quieter:
   * it is heard coming before it is loud.
   */
  meteorRoar(big: boolean, seconds: number): void {
    const k = big ? 1.5 : 1;
    this.noiseHit({ duration: seconds + 0.05, gain: 0.06 * k, type: "bandpass", freqFrom: big ? 1600 : 2600, freqTo: big ? 240 : 520, attack: seconds * 0.85 });
    this.noiseHit({ duration: seconds * 0.8, gain: 0.025 * k, type: "highpass", freqFrom: 5200, freqTo: 2600, attack: seconds * 0.6, when: seconds * 0.2 });
    if (big) this.tone({ freq: 68, freqTo: 40, duration: seconds + 0.1, gain: 0.1, type: "sine", attack: seconds * 0.9 });
  }

  /**
   * A meteor landing. A small one is a heavy thump with a bright crack, a notch
   * above a comet. The big one is the loudest thing on this table - a deep boom
   * that keeps falling, a long blast of air, a second rumble as the cards come
   * down - and it ducks the music hard to make room.
   */
  meteorImpact(big: boolean): void {
    if (!big) {
      this.tone({ freq: 170, freqTo: 48, duration: 0.26, gain: 0.2, type: "sine", attack: 0.003 });
      this.noiseHit({ duration: 0.2, gain: 0.12, type: "lowpass", freqFrom: 1800, freqTo: 220, attack: 0.003 });
      this.noiseHit({ duration: 0.32, gain: 0.05, type: "highpass", freqFrom: 4200, attack: 0.006, when: 0.02 });
      return;
    }
    this.duck(0.7, 1.4);
    this.tone({ freq: 120, freqTo: 26, duration: 1.1, gain: 0.32, type: "sine", attack: 0.004 });
    this.tone({ freq: 240, freqTo: 60, duration: 0.35, gain: 0.12, type: "triangle", attack: 0.003 });
    this.noiseHit({ duration: 1.3, gain: 0.2, type: "lowpass", freqFrom: 2400, freqTo: 90, attack: 0.004 });
    this.noiseHit({ duration: 0.6, gain: 0.07, type: "highpass", freqFrom: 3800, freqTo: 1600, attack: 0.008, when: 0.03 });
    this.tone({ freq: 52, freqTo: 34, duration: 0.8, gain: 0.12, type: "sine", attack: 0.12, when: 0.35 });
  }

  /**
   * A Mercy burst going off, scheduled from its first frame on the times
   * `animateMercyBurst` draws to: pressure building into the deck (a rising
   * whine and hiss over a quickening heartbeat), then one deep blow at `blow`
   * seconds with a bright major chord ringing out of it - the burst is a gift,
   * and should sound like one. Its cards land later, each with `mercyLand`.
   */
  mercyBurst(blow: number): void {
    this.duck(0.6, blow + 1.6);
    this.tone({ freq: 180, freqTo: 720, duration: blow, gain: 0.06, type: "triangle", attack: blow * 0.9 });
    this.noiseHit({ duration: blow + 0.05, gain: 0.06, type: "bandpass", freqFrom: 400, freqTo: 3800, attack: blow * 0.92 });
    for (const at of [0.22, 0.5, 0.72, 0.87]) {
      this.tone({ freq: 72, freqTo: 44, duration: 0.14, gain: 0.14, type: "sine", attack: 0.006, when: at * blow });
    }
    this.tone({ freq: 140, freqTo: 30, duration: 0.9, gain: 0.3, type: "sine", attack: 0.004, when: blow });
    this.noiseHit({ duration: 0.9, gain: 0.16, type: "lowpass", freqFrom: 2600, freqTo: 120, attack: 0.004, when: blow });
    this.noiseHit({ duration: 0.5, gain: 0.06, type: "highpass", freqFrom: 4200, freqTo: 1800, attack: 0.006, when: blow + 0.02 });
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      this.tone({ freq, duration: 1.3, gain: 0.035, type: "triangle", attack: 0.02, when: blow + 0.05 + i * 0.05 });
    });
  }

  /** A Mercy burst card landing: a comet's thump with a bright chime on top, one note of the burst's chord. */
  mercyLand(): void {
    this.tone({ freq: 170, freqTo: 52, duration: 0.2, gain: 0.15, type: "sine", attack: 0.004 });
    this.noiseHit({ duration: 0.12, gain: 0.08, type: "lowpass", freqFrom: 1500, freqTo: 260, attack: 0.004 });
    this.tone({ freq: 1046.5, duration: 0.5, gain: 0.03, type: "triangle", attack: 0.01, when: 0.02 });
  }

  /**
   * A card heating and shattering: a rising hiss as it cracks, then one sharp
   * break with a spray of small crackles as the pieces fly apart. `crackFor` is
   * the seconds before the break, so the hiss peaks into it.
   */
  cardShatter(crackFor: number): void {
    this.noiseHit({ duration: crackFor + 0.05, gain: 0.05, type: "bandpass", freqFrom: 900, freqTo: 3600, attack: crackFor * 0.9 });
    this.noiseHit({ duration: 0.18, gain: 0.14, type: "highpass", freqFrom: 2600, freqTo: 900, attack: 0.002, when: crackFor });
    this.tone({ freq: 220, freqTo: 70, duration: 0.2, gain: 0.12, type: "sine", attack: 0.003, when: crackFor });
    for (let i = 0; i < 8; i++) {
      this.noiseHit({
        duration: 0.03,
        gain: 0.04 + Math.random() * 0.03,
        type: "highpass",
        freqFrom: 3000 + Math.random() * 3000,
        attack: 0.002,
        when: crackFor + 0.02 + Math.random() * 0.18,
      });
    }
  }

  /**
   * Paper burning: a soft bed of fire with dry crackles scattered across it,
   * thinning out as it ends. `intensity` scales both, for the big one's blaze.
   */
  fireCrackle(seconds: number, intensity = 1): void {
    this.noiseHit({ duration: seconds, gain: 0.03 * intensity, type: "bandpass", freqFrom: 900, freqTo: 400, attack: 0.08 });
    const pops = Math.round(seconds * 22 * intensity);
    for (let i = 0; i < pops; i++) {
      const at = Math.pow(Math.random(), 1.6) * seconds;
      this.noiseHit({
        duration: 0.018 + Math.random() * 0.03,
        gain: (0.02 + Math.random() * 0.035) * intensity,
        type: "highpass",
        freqFrom: 2200 + Math.random() * 3800,
        attack: 0.002,
        when: at,
      });
    }
  }

  /**
   * Your turn has come round: two glass notes rising a sixth, G5 then E6.
   *
   * For the player who looked away while the others played, so it has to be
   * recognisable from across a room without being an alarm. Glass rather than
   * a tone because glass HANGS - the second note is still in the air a second
   * later, which is what makes it read as an invitation and not as an event on
   * the table. A sixth because every interval near it is taken: `correct` is a
   * rising fifth, the Peek a rising fourth, the bounty bell a struck fifth.
   * Quieter than any of them, and never louder with tension: it is the one
   * sound here that has to stay polite however tight the round is.
   */
  yourTurn(): void {
    if (!this.sfxOn) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxBus) return;
    const t0 = ctx.currentTime;
    const chime = VENUE_CHIME[this.venue] ?? VENUE_CHIME.coastal;
    this.struck(ctx, this.sfxBus, t0, { freq: chime.notes[0], gain: 0.05, seconds: 1.1, partials: chime.partials });
    this.struck(ctx, this.sfxBus, t0 + 0.13, { freq: chime.notes[1], gain: 0.042, seconds: 1.5, partials: chime.partials });
  }

  /**
   * Your turn has come round at YOUR table while you are watching another one.
   *
   * The ordinary chime would be wrong twice over: it is deliberately polite, and
   * at the table being watched it is somebody else's sound - so a player could
   * not tell "my turn, over there" from the room around them. This is the same
   * room's two notes struck twice and then answered an octave up, louder: still
   * glass, still the room's key, but a call rather than an invitation.
   */
  yourTurnAway(): void {
    if (!this.sfxOn) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.sfxBus) return;
    const t0 = ctx.currentTime;
    const chime = VENUE_CHIME[this.venue] ?? VENUE_CHIME.coastal;
    const [lo, hi] = chime.notes;
    for (const at of [0, 0.42]) {
      this.struck(ctx, this.sfxBus, t0 + at, { freq: lo, gain: 0.08, seconds: 1.0, partials: chime.partials });
      this.struck(ctx, this.sfxBus, t0 + at + 0.12, { freq: hi, gain: 0.07, seconds: 1.2, partials: chime.partials });
    }
    this.struck(ctx, this.sfxBus, t0 + 0.9, { freq: hi * 2, gain: 0.05, seconds: 1.6, partials: chime.partials });
  }

  /** A Bounty Pile lit: a struck bell and its fifth, left to ring over the table. */
  bountyBell(): void {
    this.tone({ freq: 880, duration: 1.1, gain: 0.08, type: "sine", attack: 0.004 });
    this.tone({ freq: 1320, duration: 0.8, gain: 0.04, type: "sine", attack: 0.004 });
    this.tone({ freq: 2217, duration: 0.4, gain: 0.015, type: "sine", attack: 0.004 });
    this.noiseHit({ duration: 0.5, gain: 0.02, type: "highpass", freqFrom: 5200, attack: 0.1, when: 0.05 });
  }

  /** A bounty claimed: the bell again, smaller, under a falling shimmer of sparks. */
  bountyClaim(): void {
    this.tone({ freq: 1320, duration: 0.5, gain: 0.05, type: "sine", attack: 0.004 });
    [2093, 1760, 1568, 1319].forEach((f, i) => this.tone({ freq: f, duration: 0.14, gain: 0.025, type: "sine", attack: 0.004, when: 0.06 + i * 0.05 }));
  }

  /** A bounty's lap over: the star going out, one soft falling note. */
  bountyEnd(): void {
    this.tone({ freq: 880, freqTo: 440, duration: 0.45, gain: 0.04, type: "sine", attack: 0.02 });
  }

  /** Tiny UI tick for buttons (wired globally in boot()). */
  private click(): void {
    this.noiseHit({ duration: 0.03, gain: 0.035, type: "highpass", freqFrom: 3000 });
  }

  // ---- generative ambience ------------------------------------------------

  private startAmbienceIfWanted(): void {
    if (!this.musicOn || this.ambienceRunning) return;
    const ctx = this.ensureContext();
    if (!ctx || !this.musicBus || ctx.state !== "running") return;
    this.ambienceRunning = true;
    // Nothing else may still be sounding when a room starts (see liveOuts).
    if (this.liveOuts.size) audioLog(`start: silencing ${this.liveOuts.size} stray output(s)`);
    for (const stray of this.liveOuts) this.fadeOutAndDrop(stray);
    const out = ctx.createGain();
    out.connect(this.musicBus);
    this.ambienceOut = out;
    this.ambienceStartedAt = ctx.currentTime;
    this.liveOuts.add(out);
    audioLog(`ambience start: ${this.venue}`);
    /*
     * A Record rather than a switch, so that adding a venue without giving it a
     * soundscape is a COMPILE error rather than a silent fallback.
     *
     * The old switch ended `case "coastal": default:`, which meant a new venue
     * quietly played the Coastal Terrace's waves — worse than silence, because a
     * room that sounds like a different room reads as a bug in the venue the
     * player just chose rather than as something missing.
     *
     * The `??` is still a real fallback and is not the same thing: `this.venue`
     * comes from localStorage, so it can hold an id that no longer exists after
     * a venue is renamed or removed. That case wants waves; a case the type
     * system can see wants a compiler complaint.
     */
    const ambiences: Record<VenueId, (ctx: AudioContext, out: GainNode) => void> = {
      coastal: (c, o) => this.startCoastal(c, o),
      alpine: (c, o) => this.startAlpine(c, o),
      library: (c, o) => this.startLibrary(c, o),
      pavilion: (c, o) => this.startPavilion(c, o),
      observatory: (c, o) => this.startObservatory(c, o),
      velvet: (c, o) => this.startVelvet(c, o),
      olympian: (c, o) => this.startOlympian(c, o),
      biolume: (c, o) => this.startBiolume(c, o),
      porch: (c, o) => this.startPorch(c, o),
      hallowed: (c, o) => this.startHallowed(c, o),
      meadhall: (c, o) => this.startMeadHall(c, o),
      singularity: (c, o) => this.startSingularity(c, o),
    };
    (ambiences[this.venue] ?? ambiences.coastal)(ctx, out);
  }

  /**
   * A quiet filtered-noise bed with a slow LFO swell — the shared basis of most
   * venue ambiences (waves, wind, room tone). Returns nothing; registers its own
   * teardown.
   */
  private startNoiseBed(
    ctx: AudioContext,
    out: GainNode,
    opts: { filter: BiquadFilterType; freq: number; gain: number; lfoRate: number; lfoGainDepth: number; lfoFilterDepth?: number },
  ): void {
    const src = ctx.createBufferSource();
    src.buffer = this.getNoise(ctx);
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = opts.filter;
    filter.frequency.value = opts.freq;
    const bedGain = ctx.createGain();
    bedGain.gain.value = opts.gain;
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = opts.lfoRate;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = opts.lfoGainDepth;
    lfo.connect(lfoDepth).connect(bedGain.gain);
    if (opts.lfoFilterDepth) {
      const lfoFilter = ctx.createGain();
      lfoFilter.gain.value = opts.lfoFilterDepth;
      lfo.connect(lfoFilter).connect(filter.frequency);
    }
    src.connect(filter).connect(bedGain).connect(out);
    src.start();
    lfo.start();
    this.ambienceStops.push(() => {
      src.stop();
      lfo.stop();
      bedGain.disconnect();
    });
  }

  /**
   * Sparse randomly-timed one-shots (fire crackles, page turns, birdsong, ticks).
   * `play` receives the scheduling time; `gapMs` returns the wait until the next.
   */
  /**
   * A lookahead scheduler, for anything that has to land IN TIME.
   *
   * `startSparse` above is fine for material with no pulse — a crackle, a bird —
   * because nobody can hear a crackle being forty milliseconds late. A plucked
   * phrase is different: `setTimeout` drifts against the audio clock, and over a
   * minute the drift is plainly audible as the part stumbling. So this runs a
   * short polling loop and schedules every note AHEAD of time against
   * `ctx.currentTime`, which is the only clock the audio hardware actually obeys.
   *
   * `interval` is asked for a fresh value per note rather than fixed, so a part
   * can breathe without restarting the scheduler.
   *
   * It is in seconds AT REST, and this divides it by `tempo()`: every room with
   * a pulse speeds up by the same `TENSION_TEMPO_LIFT` as the round closes, in
   * one place, rather than each room tightening by its own hand-picked amount
   * (they had drifted to anywhere from none at all to over 40%). So a room
   * writes its beat as though the table were calm, and an offset it schedules
   * INSIDE a beat - a swung "and", a motif's later notes - divides by
   * `tempo()` too, or that figure would stay slow while the beat around it
   * sped up.
   */
  private startPulse(ctx: AudioContext, interval: () => number, play: (t0: number, beat: number) => void): void {
    const LOOKAHEAD = 0.2;
    const POLL_MS = 25;
    let stopped = false;
    let beat = 0;
    let next = ctx.currentTime + 0.15;
    let timer = 0;
    const tick = () => {
      if (stopped) return;
      /*
       * A hidden tab throttles setTimeout to about once a second, so `next` can
       * fall a long way behind the audio clock while the player is in another
       * app. Without this the catch-up loop would schedule every missed note at
       * a time already in the past, and the Web Audio spec plays those
       * immediately — the part comes back as a single chord of everything that
       * was missed. Skipping forward drops them instead, which is what a
       * listener returning to the tab expects to hear.
       */
      if (next < ctx.currentTime) next = ctx.currentTime + 0.05;
      while (next < ctx.currentTime + LOOKAHEAD) {
        play(next, beat);
        beat++;
        next += Math.max(0.05, interval() / this.tempo());
      }
      timer = window.setTimeout(tick, POLL_MS);
    };
    tick();
    this.ambienceStops.push(() => {
      stopped = true;
      window.clearTimeout(timer);
    });
  }

  /**
   * A plucked string, by Karplus–Strong: a burst of noise in a delay line one
   * period long, low-pass filtered every time it recirculates.
   *
   * This is the difference between a venue that sounds synthesized and one that
   * sounds like an instrument, and it costs nothing but arithmetic — filtered
   * oscillators all sound like filtered oscillators, whereas this is a physical
   * model of a string and is heard as one. It is the nearest thing to a sampled
   * kithara that can exist without shipping a sample.
   *
   * Rendered into a buffer rather than built from live nodes because the
   * feedback loop needs a per-sample delay of a few dozen samples, which the
   * node graph cannot express (DelayNode quantises to a render block). Buffers
   * are cached per PITCH ONLY, at one length and damping - per-note length and
   * damping are applied at playback (see `pluckVoice`). Keyed by all three, the
   * kithara's random lengths meant a new ~0.6MB buffer for every note, kept
   * forever: ~860MB after a long session, and Safari killing the page.
   */
  private pluckBuffer(ctx: AudioContext, freq: number, key: string): AudioBuffer {
    const cached = this.pluckCache.get(key);
    if (cached) return cached;
    if (this.pluckCache.size >= PLUCK_CACHE_MAX) this.pluckCache.clear();
    const seconds = PLUCK_RENDER_SECONDS;
    const damping = PLUCK_RENDER_DAMPING;
    const rate = ctx.sampleRate;
    const n = Math.max(2, Math.round(rate / freq));
    const len = Math.max(n + 2, Math.floor(rate * seconds));
    const buf = ctx.createBuffer(1, len, rate);
    const out = buf.getChannelData(0);
    const line = new Float32Array(n);
    for (let i = 0; i < n; i++) line[i] = Math.random() * 2 - 1;
    let idx = 0;
    for (let i = 0; i < len; i++) {
      const cur = line[idx];
      const next = line[(idx + 1) % n];
      out[i] = cur;
      line[idx] = (cur + next) * 0.5 * damping;
      idx = (idx + 1) % n;
    }
    // Ends have to be silent at both edges or the buffer clicks: an abrupt start
    // is a DC step, and an abrupt stop is the string being cut rather than dying.
    const fadeIn = Math.min(64, len);
    for (let i = 0; i < fadeIn; i++) out[i] *= i / fadeIn;
    const fadeOut = Math.floor(len * 0.12);
    for (let i = 0; i < fadeOut; i++) out[len - 1 - i] *= i / fadeOut;
    this.pluckCache.set(key, buf);
    return buf;
  }

  /**
   * Plays a `pluckBuffer` at a time, level and stereo position. The note's own
   * length is a fade and stop on the shared buffer, and its damping a low-pass:
   * the variety the ear hears, without a buffer per note.
   */
  private pluck(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { freq: number; gain: number; seconds?: number; damping?: number; pan?: number },
  ): void {
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "pluck" });
    const voice = pluckVoice(opts.freq, opts.seconds ?? 2.6, opts.damping ?? 0.995);
    const src = ctx.createBufferSource();
    src.buffer = this.pluckBuffer(ctx, opts.freq, voice.key);
    const tone = ctx.createBiquadFilter();
    tone.type = "lowpass";
    tone.frequency.value = voice.cutoffHz;
    const g = ctx.createGain();
    g.gain.setValueAtTime(opts.gain, t0);
    g.gain.setValueAtTime(opts.gain, t0 + voice.fadeFrom);
    g.gain.linearRampToValueAtTime(0, t0 + voice.stopAt);
    src.connect(tone).connect(g).connect(this.panned(ctx, out, opts.pan));
    src.start(t0);
    src.stop(t0 + voice.stopAt + 0.02);
  }

  /**
   * Struck metal — a bronze bowl or a small bell.
   *
   * Bells are INHARMONIC: their partials are not whole-number multiples of the
   * fundamental, which is exactly why a bell cannot be made out of a sawtooth.
   * These ratios are the standard idealised bell set, and the higher partials
   * are given shorter decays because that is what makes a strike ring out into a
   * hum rather than staying bright.
   *
   * `partials` is what makes this a struck-OBJECT function rather than a bell
   * one. Timbre is where a venue is recognisable (docs/PREMIUM_VENUES.md), and
   * two premium venues sharing one partial set would share an instrument — the
   * abyss would be Olympus' bronze bowl in a darker room, which is precisely the
   * "recolor" failure the whole document exists to prevent.
   *
   * Returns its own stop, like `call`. Nearly every caller ignores it — a
   * glass flash or a tine is over in seconds — but Hallowed Ground's church
   * bell schedules a whole toll of long strikes at once, and that needs to be
   * silenced with the room rather than ringing on over the next one.
   */
  private struck(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { freq: number; gain: number; seconds: number; pan?: number; partials?: readonly Partial_[]; bright?: number },
  ): () => void {
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "struck" });
    const target = this.panned(ctx, out, opts.pan);
    const partials = opts.partials ?? BRONZE_PARTIALS;
    const oscs: OscillatorNode[] = [];
    for (const p of partials) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = opts.freq * p.ratio;
      const g = ctx.createGain();
      const life = opts.seconds * p.decay;
      // `bright` scales the overtones and leaves the fundamental: a softer
      // mallet, which is what a quieter strike actually is.
      const level = p.ratio === 1 ? p.gain : p.gain * Math.max(0.05, Math.min(1, opts.bright ?? 1));
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0001, opts.gain * level), t0 + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + life);
      osc.connect(g).connect(target);
      osc.start(t0);
      osc.stop(t0 + life + 0.05);
      oscs.push(osc);
    }
    return () => {
      for (const osc of oscs) {
        try {
          osc.stop();
        } catch {
          /* already finished on its own */
        }
      }
    };
  }

  /**
   * One pipe of a church organ, held and then let go.
   *
   * Every other pitched voice in this file is an EVENT: struck or plucked, and
   * then nothing but decay. A pipe is the opposite: it speaks, it holds at one
   * level for exactly as long as the key is down, and then the cathedral is
   * what fades it. So `seconds` is how long the key is held and `release` is
   * how long the sound takes to die after it - the tail the user asked for on
   * the pedal notes, and what keeps a chord from stopping dead.
   *
   * Three details make it a pipe rather than an oscillator:
   *
   *  - THE STOP IS A HARMONIC SPECTRUM (`ORGAN_STOPS`, as a PeriodicWave). A
   *    flue pipe is harmonic, where a bar or a bell is not, and the stops differ
   *    only in how much of each harmonic they carry: a flute nearly none, a
   *    principal a long row of them. One oscillator per rank, whatever the stop.
   *  - TWO RANKS, A HAIR APART. Real ranks are never perfectly in tune with each
   *    other, and the slow beating between two of them is the shimmer of a big
   *    organ in a big room. Without it a held chord is a test tone.
   *  - IT SPEAKS SLOWLY, and a flute CHIFFS. A pipe takes tens of milliseconds
   *    to fill with wind (a pedal pipe longer), and a flute's speech starts
   *    with a breath of noise before the tone settles. That breath is most of
   *    what tells an ear "pipe" and not "synth pad".
   *
   * Through a lowpass at the stop's own ceiling, because an organ heard from the
   * nave has lost its edge to the distance, and because 2-5kHz is the band the
   * cards' own sounds need (docs/PREMIUM_VENUES.md).
   *
   * Returns its own stop, like `struck`: a pedal note can be held for most of
   * ten seconds, and it must not outlive the room that started it.
   */
  private organ(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { freq: number; gain: number; seconds: number; release: number; stop: OrganStop; pan?: number },
  ): () => void {
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "organ" });
    const stop = ORGAN_STOPS[opts.stop];
    const tone = ctx.createBiquadFilter();
    tone.type = "lowpass";
    tone.frequency.value = Math.min(stop.ceiling, opts.freq * stop.harmonics.length);
    tone.Q.value = 0.5;
    const g = ctx.createGain();
    const held = t0 + Math.max(stop.speech, opts.seconds);
    const end = held + opts.release;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(opts.gain, t0 + stop.speech);
    g.gain.setValueAtTime(opts.gain, held);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    tone.connect(g).connect(this.panned(ctx, out, opts.pan));

    const wave = this.organWave(ctx, opts.stop);
    const sources: AudioScheduledSourceNode[] = [];
    for (const cents of [0, stop.celeste]) {
      const osc = ctx.createOscillator();
      osc.setPeriodicWave(wave);
      osc.frequency.value = opts.freq * Math.pow(2, cents / 1200);
      osc.connect(tone);
      osc.start(t0);
      osc.stop(end + 0.05);
      sources.push(osc);
    }

    if (stop.chiff > 0) {
      const breath = ctx.createBufferSource();
      breath.buffer = this.getNoise(ctx);
      const band = ctx.createBiquadFilter();
      band.type = "bandpass";
      band.frequency.value = Math.min(3000, opts.freq * 3);
      band.Q.value = 1.2;
      const bg = ctx.createGain();
      bg.gain.setValueAtTime(0.0001, t0);
      bg.gain.exponentialRampToValueAtTime(opts.gain * stop.chiff, t0 + 0.012);
      bg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.09);
      breath.connect(band).connect(bg).connect(tone);
      breath.start(t0, Math.random() * 1.5);
      breath.stop(t0 + 0.1);
      sources.push(breath);
    }

    return () => {
      for (const s of sources) {
        try {
          s.stop();
        } catch {
          /* already finished on its own */
        }
      }
    };
  }

  /** A stop's spectrum as a PeriodicWave, built once per context. */
  private organWave(ctx: AudioContext, id: OrganStop): PeriodicWave {
    if (this.organWaveCtx !== ctx) {
      this.organWaves.clear();
      this.organWaveCtx = ctx;
    }
    let wave = this.organWaves.get(id);
    if (!wave) {
      const h = ORGAN_STOPS[id].harmonics;
      const real = new Float32Array(h.length + 1);
      const imag = new Float32Array([0, ...h]);
      wave = ctx.createPeriodicWave(real, imag);
      this.organWaves.set(id, wave);
    }
    return wave;
  }

  /**
   * A bowed string's spectrum: a sawtooth (harmonics falling as 1/n, which is
   * what a string pulled sideways by rosined hair does) with the top rolled off
   * early so the oscillator is band-limited with room to spare at E5.
   */
  private bowedWave(ctx: AudioContext): PeriodicWave {
    if (this.bowedWaveCache?.ctx === ctx) return this.bowedWaveCache.wave;
    const harmonics = 16;
    const real = new Float32Array(harmonics + 1);
    const imag = new Float32Array(harmonics + 1);
    for (let n = 1; n <= harmonics; n++) imag[n] = (1 / n) * Math.exp(-(n - 1) / 9);
    const wave = ctx.createPeriodicWave(real, imag);
    this.bowedWaveCache = { ctx, wave };
    return wave;
  }

  /**
   * One note of a bowed lyre - the tagelharpa, a horsehair-strung bowl lyre of
   * the north - swelling in under the bow, held, and let go.
   *
   * Like `organ` it is a voice with a HOLD, not an event with a decay, but it
   * is not a pipe: a bow takes a quarter of a second or more to get a string
   * speaking, so the note SWELLS in rather than starting, and the bow pressure
   * that builds with the swell is what brightens it (the lowpass opens through
   * the attack). Four details turn a sawtooth into a string:
   *
   *  - THE BODY. Two resonances, near 300Hz and 1kHz, are what a wooden bowl
   *    does to every note whatever its pitch; without them it is a synth pad.
   *  - A CEILING AT ABOUT 1.9kHz, two lowpasses deep. The cards' own sounds need
   *    2-5kHz (docs/PREMIUM_VENUES.md), and a bowed sawtooth is exactly the
   *    kind of source that fills it.
   *  - VIBRATO THAT ARRIVES LATE. A few cents at 5Hz, faded in only once the
   *    bow has the string: a player starts a note plain and warms it, and
   *    vibrato from the first instant is a theremin.
   *  - HORSEHAIR. A whisper of bandpassed noise as the bow bites. It goes in
   *    after the envelope, so it is heard at the attack and not swelled with
   *    the note.
   *
   * `bright` is 0..1 and scales the ceiling; `attack` and `release` are for the
   * rare caller that needs to fix them (the Five Out swell), where the line
   * lets each note draw its own.
   *
   * Returns its own stop, like `organ`.
   */
  private bowed(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { freq: number; gain: number; seconds: number; pan?: number; bright?: number; attack?: number; release?: number },
  ): () => void {
    // Reported as the organ's kind: the music lab counts melody by kind, and a
    // sustained pitched voice is what that counter means by "organ".
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "organ" });
    const attack = opts.attack ?? 0.25 + Math.random() * 0.2;
    const release = opts.release ?? 0.7;
    const bright = Math.max(0, Math.min(1, opts.bright ?? 0.6));
    const target = this.panned(ctx, out, opts.pan);

    const bodyLow = ctx.createBiquadFilter();
    bodyLow.type = "peaking";
    bodyLow.frequency.value = 300;
    bodyLow.Q.value = 1.6;
    bodyLow.gain.value = 5;
    const bodyHigh = ctx.createBiquadFilter();
    bodyHigh.type = "peaking";
    bodyHigh.frequency.value = 1000;
    bodyHigh.Q.value = 2.2;
    bodyHigh.gain.value = 4;
    const ceiling = 1500 + 450 * bright;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.Q.value = 0.6;
    lowpass.frequency.setValueAtTime(ceiling * 0.5, t0);
    lowpass.frequency.linearRampToValueAtTime(ceiling, t0 + attack * 1.4);
    const lowpass2 = ctx.createBiquadFilter();
    lowpass2.type = "lowpass";
    lowpass2.Q.value = 0.5;
    lowpass2.frequency.value = ceiling * 1.1;

    const g = ctx.createGain();
    const held = t0 + Math.max(attack + 0.3, opts.seconds);
    const end = held + release;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(opts.gain, t0 + attack);
    // The bow eases off a little once the string is speaking.
    g.gain.linearRampToValueAtTime(opts.gain * 0.85, held);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    bodyLow.connect(bodyHigh).connect(lowpass).connect(lowpass2).connect(g).connect(target);

    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 4.8 + Math.random() * 0.9;
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(0, t0);
    depth.gain.setValueAtTime(0, t0 + attack);
    depth.gain.linearRampToValueAtTime(3 + Math.random() * 3, t0 + attack + 0.6);
    lfo.connect(depth);
    lfo.start(t0);
    lfo.stop(end + 0.05);

    const sources: AudioScheduledSourceNode[] = [lfo];
    const wave = this.bowedWave(ctx);
    // Two strings a hair apart, as a bowed lyre's drone-and-melody pair lie.
    for (const cents of [-2.5, 2.5]) {
      const osc = ctx.createOscillator();
      osc.setPeriodicWave(wave);
      osc.frequency.value = opts.freq;
      osc.detune.value = cents;
      depth.connect(osc.detune);
      osc.connect(bodyLow);
      osc.start(t0);
      osc.stop(end + 0.05);
      sources.push(osc);
    }

    const grit = ctx.createBufferSource();
    grit.buffer = this.getNoise(ctx);
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = Math.min(1800, opts.freq * 4);
    band.Q.value = 1.5;
    const gg = ctx.createGain();
    gg.gain.setValueAtTime(0.0001, t0);
    gg.gain.exponentialRampToValueAtTime(opts.gain * 0.14, t0 + 0.05);
    gg.gain.exponentialRampToValueAtTime(0.0001, t0 + attack * 0.9);
    grit.connect(band).connect(gg).connect(target);
    grit.start(t0, Math.random() * 1.5);
    grit.stop(t0 + attack);
    sources.push(grit);

    return () => {
      for (const s of sources) {
        try {
          s.stop();
        } catch {
          /* already finished on its own */
        }
      }
    };
  }

  /**
   * A long, low, gliding call — a large animal a long way off.
   *
   * This is the Midnight Zone's signature voice, and it is a genuinely different
   * KIND of sound from everything else in this file. Every other source here is
   * an event: a note is struck or plucked or popped, and what follows is decay.
   * A call has no attack at all. It fades up, bends through a shape over several
   * seconds and fades away, so it is heard as something that was already there
   * and came within earshot — which is exactly what the venue needs, because a
   * room with nothing alive in it is just a dark color.
   *
   * Three details do the work:
   *
   *  - IT GLIDES rather than stepping. `exponentialRampToValueAtTime` through a
   *    turning point is the whole gesture; a fixed pitch here is a foghorn.
   *  - THE SECOND HARMONIC RIDES ALONG. A pure sine at 60Hz on a phone speaker
   *    is inaudible, so the octave above is what actually carries the call on
   *    hardware that cannot reproduce the fundamental at all.
   *  - A SLOW VIBRATO. Under 5Hz and shallow, it is the difference between an
   *    oscillator and a throat.
   *
   * Returns its own stop, because a four-second call outliving the venue that
   * started it would moan over the top of whatever the player just switched to.
   */
  private call(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { from: number; peak: number; to: number; gain: number; seconds: number; pan?: number },
  ): () => void {
    this.noted(out, { at: t0, freq: opts.from, gain: opts.gain, kind: "call" });
    // Distance is a lowpass. The sea between here and whatever made this has
    // already taken the top off it, so nothing above the harmonic survives.
    const far = ctx.createBiquadFilter();
    far.type = "lowpass";
    far.frequency.value = opts.peak * 3.2;
    far.Q.value = 0.6;
    far.connect(this.panned(ctx, out, opts.pan));

    const vibrato = ctx.createOscillator();
    vibrato.type = "sine";
    vibrato.frequency.value = 4.2 + Math.random() * 1.1;

    const oscs: OscillatorNode[] = [];
    for (const [mult, level] of [[1, 1], [2, 0.34]] as const) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(opts.from * mult, t0);
      osc.frequency.exponentialRampToValueAtTime(opts.peak * mult, t0 + opts.seconds * 0.42);
      osc.frequency.exponentialRampToValueAtTime(opts.to * mult, t0 + opts.seconds);
      const depth = ctx.createGain();
      depth.gain.value = opts.peak * mult * 0.012;
      vibrato.connect(depth).connect(osc.frequency);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.linearRampToValueAtTime(opts.gain * level, t0 + opts.seconds * 0.3);
      g.gain.setValueAtTime(opts.gain * level, t0 + opts.seconds * 0.62);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.seconds);
      osc.connect(g).connect(far);
      osc.start(t0);
      osc.stop(t0 + opts.seconds + 0.1);
      oscs.push(osc);
    }
    vibrato.start(t0);
    vibrato.stop(t0 + opts.seconds + 0.1);

    return () => {
      for (const osc of [...oscs, vibrato]) {
        try {
          osc.stop();
        } catch {
          /* already finished on its own */
        }
      }
    };
  }

  private startSparse(
    ctx: AudioContext,
    play: (t0: number) => void,
    gapMs: () => number,
    startDelayMs = 0,
    opts: { wears?: boolean } = {},
  ): void {
    let stopped = false;
    let timer = 0;
    const tick = () => {
      if (stopped) return;
      play(ctx.currentTime);
      // One-shots thin out over a session (`wear`); texture that IS the room,
      // like a fire's ordinary crackle, opts out.
      timer = window.setTimeout(tick, gapMs() * (opts.wears === false ? 1 : this.wear(ctx)));
    };
    /*
     * `startDelayMs` defaults to 0, which fires the first one-shot immediately —
     * what the six free venues were tuned on, and right for their material,
     * where the first crackle or bird arriving at once is simply the room being
     * already occupied.
     *
     * It is wrong for anything RARE and dramatic. A thunder roll timed to land
     * every forty seconds should not also greet you the instant the ambience
     * starts: heard once at launch it reads as a UI sound acknowledging the
     * screen, which is the opposite of weather happening somewhere else.
     */
    if (startDelayMs > 0) timer = window.setTimeout(tick, startDelayMs);
    else tick();
    this.ambienceStops.push(() => {
      stopped = true;
      window.clearTimeout(timer);
    });
  }

  /** A short filtered-noise pop, used for crackles, page turns and taps. */
  private ambientPop(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { filter: BiquadFilterType; freq: number; peak: number; duration: number; pan?: number },
  ): void {
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.peak, kind: "pop" });
    const pop = ctx.createBufferSource();
    pop.buffer = this.getNoise(ctx);
    const f = ctx.createBiquadFilter();
    f.type = opts.filter;
    f.frequency.value = opts.freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(opts.peak, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);
    pop.connect(f).connect(g).connect(this.panned(ctx, out, opts.pan));
    pop.start(t0);
    pop.stop(t0 + opts.duration + 0.02);
  }

  /** A soft sine blip, used for birdsong and the observatory's distant chimes. */
  private ambientBlip(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { freq: number; freqTo?: number; gain: number; duration: number; type?: OscillatorType; pan?: number },
  ): void {
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "blip" });
    const osc = ctx.createOscillator();
    osc.type = opts.type ?? "sine";
    osc.frequency.setValueAtTime(opts.freq, t0);
    if (opts.freqTo) osc.frequency.exponentialRampToValueAtTime(opts.freqTo, t0 + opts.duration);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(opts.gain, t0 + opts.duration * 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);
    osc.connect(g).connect(this.panned(ctx, out, opts.pan));
    osc.start(t0);
    osc.stop(t0 + opts.duration + 0.05);
  }

  /**
   * A stereo position in front of `out`, or `out` itself when none was asked for.
   *
   * Returning the bus unchanged for the undefined case matters: it keeps the six
   * existing soundscapes on exactly the graph they were tuned on, so adding
   * stereo could not quietly re-voice them.
   */
  private panned(ctx: AudioContext, out: GainNode, pan?: number): AudioNode {
    if (pan === undefined || pan === 0) return out;
    const p = ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    p.connect(out);
    return p;
  }

  /**
   * Stops the ambience - and silences everything it had already scheduled.
   *
   * The teardown hooks stop timers and oscillators, but a generative ambience
   * schedules AHEAD on the audio clock: the kithara's next notes (3.6-second
   * plucks), the bed voices' slow releases. Those were already queued, carried
   * on regardless, and were heard as the old room overlapping the new one when
   * switching venues, and as music carrying on for seconds after it was
   * switched off. So the ambience's own bus is faded to nothing and then
   * disconnected: whatever is still queued underneath it plays into nothing.
   * The next ambience gets a fresh bus, so the fade never touches it.
   */
  private stopAmbience(): void {
    for (const stop of this.ambienceStops) {
      try {
        stop();
      } catch {
        /* already stopped */
      }
    }
    this.ambienceStops = [];
    if (this.ambienceRunning || this.liveOuts.size) audioLog(`ambience stop: ${this.venue} (${this.liveOuts.size} output(s))`);
    this.ambienceRunning = false;
    this.ambienceOut = null;
    for (const out of this.liveOuts) this.fadeOutAndDrop(out);
  }

  /**
   * Fades one ambience output to nothing and disconnects it.
   *
   * Timed on the output's OWN context clock, not `this.ctx`'s: an output left
   * on an older context (the case liveOuts exists for) is on a different clock
   * entirely, and a new context's starts at zero.
   */
  private fadeOutAndDrop(out: GainNode): void {
    this.liveOuts.delete(out);
    try {
      const now = out.context.currentTime;
      out.gain.cancelScheduledValues(now);
      out.gain.setValueAtTime(out.gain.value, now);
      out.gain.linearRampToValueAtTime(0, now + AMBIENCE_FADE_S);
    } catch {
      /* context closed: it is already silent */
    }
    // A timer rather than scheduling on the audio clock, because a node cannot
    // disconnect itself at a time. If the context is closed first (a rebuild),
    // disconnecting a dead node is harmless.
    window.setTimeout(() => {
      try {
        out.disconnect();
      } catch {
        /* context already gone */
      }
    }, AMBIENCE_FADE_S * 1000 + 50);
  }

  /**
   * Sustained voices, each looping on its OWN cycle length — the Music for
   * Airports trick, and the answer to a bed that "repeats too often".
   *
   * It replaced `startPad`, which alternated a fixed list of chords on a fixed
   * timer: its period was exactly (chords x interval) — 22 seconds for every
   * venue in this file — and a listener learns that in a couple of passes. That
   * function is gone rather than left available, because the next soundscape
   * would have reached for it. This instead gives every voice its own period and
   * lets them drift against each other. Eno's 2/1 is seven tape loops at
   * deliberately
   * *incommensurable* lengths — 23 1/2 seconds, 25 7/8, 29 15/16 — so the
   * vertical combination does not recur in any listening session. Nothing about
   * the parts is complicated; the non-repetition is a property of the periods.
   *
   * Two rules when choosing them, and both matter more than the pitches:
   *
   *  - The periods must share no useful common factor. 20/30/40 is one loop with
   *    extra steps; 23.5/25.875/29.9375 is effectively endless.
   *  - Every voice must be consonant with every other, because any of them can
   *    sound against any other. Pick pitches from one chord and the system cannot
   *    produce a combination that needs resolving.
   */
  private startDriftingVoices(
    ctx: AudioContext,
    out: GainNode,
    voices: readonly DriftVoice[],
  ): void {
    for (const v of voices) {
      let stopped = false;
      let timer = 0;
      const sound = () => {
        if (stopped) return;
        const t0 = ctx.currentTime;
        // Sounds for most of its cycle and rests for the remainder, so the bed
        // thins and thickens on its own without anything deciding that it should.
        const hold = v.periodSec * 0.62;
        this.noted(out, { at: t0, freq: v.freq, gain: v.gain, kind: "bed" });
        for (const detune of [-4, 4]) {
          const osc = ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.value = v.freq;
          // Re-detuned on every entry: the beating between the pair shifts a
          // little each cycle, so even one voice heard alone is never twice
          // exactly the same sound.
          osc.detune.value = detune + (Math.random() - 0.5) * 3;
          const g = ctx.createGain();
          const peak = v.gain * (0.8 + Math.random() * 0.4);
          g.gain.setValueAtTime(0.0001, t0);
          g.gain.linearRampToValueAtTime(peak, t0 + hold * 0.4);
          g.gain.setValueAtTime(peak, t0 + hold * 0.55);
          g.gain.linearRampToValueAtTime(0.0001, t0 + hold);
          osc.connect(g).connect(this.panned(ctx, out, v.pan));
          osc.start(t0);
          osc.stop(t0 + hold + 0.2);
        }
        timer = window.setTimeout(sound, v.periodSec * 1000);
      };
      // Stagger the first entry somewhere inside this voice's own cycle. Without
      // it every voice strikes together at t=0, which is a downbeat — an
      // unmistakable "the music has started" that the whole design is avoiding.
      timer = window.setTimeout(sound, Math.random() * v.periodSec * 1000);
      this.ambienceStops.push(() => {
        stopped = true;
        window.clearTimeout(timer);
      });
    }
  }

  /**
   * A melody that speaks in PHRASES, with a theme it comes back to.
   *
   * The first premium walks decided every beat on its own: a coin for "rest or
   * play", a step from a table, forever. That is memoryless, and it sounds it -
   * a steady drip with random clumps, no line ever going anywhere and no line
   * ever finishing. Players heard it as noodling they had to tune out.
   *
   * This is the same random material given a SHAPE, from what is known about
   * how a listener expects a melody to move (Huron, Sweet Anticipation):
   *
   *  - A phrase is a handful of notes with a direction. Steps tend to carry on
   *    the way they were going; a leap tends to turn round and fill its gap.
   *  - It ends: the last note lands on a chord tone and is held longer.
   *  - Then it breathes. Several beats of nothing between phrases, which is
   *    where a listener relaxes (Bernardi et al., 2006: the pause, not the slow
   *    music, is what lowered heart rate and breathing).
   *
   * Two more things give a room an IDENTITY without giving it a loop:
   *
   *  - A MOTIF, a few notes that belong to this room, stated rarely and never
   *    the same way twice - from another root, slower, or only its opening.
   *    That is a leitmotif, which is how game scores have given places an
   *    identity for forty years; what keeps it from becoming a loop's seam is
   *    the rationing, minutes apart, and the variation.
   *  - EPISODES. The line plays for a couple of minutes, then leaves the room to
   *    its bed and its weather for a minute, then comes back - the way
   *    Minecraft rations its music, scaled down to a card table. Tension keeps
   *    it playing: a round that is closing is no time for the music to leave.
   */
  private startPhrases(ctx: AudioContext, spec: PhraseSpec): void {
    const between = ([lo, hi]: readonly [number, number]) => lo + Math.random() * (hi - lo);
    const top = spec.scale.length - 1;
    const stepTotal = spec.steps.reduce((sum, [, w]) => sum + w, 0);
    const drawStep = () => {
      let r = Math.random() * stepTotal;
      for (const [size, w] of spec.steps) if ((r -= w) < 0) return size;
      return spec.steps[0][0];
    };
    const nearest = (degrees: readonly number[], to: number) =>
      degrees.reduce((best, d) => (Math.abs(d - to) < Math.abs(best - to) ? d : best), degrees[0]);

    let degree = spec.center;
    let dir = Math.random() < 0.5 ? -1 : 1;
    let lastStep = 0;
    let phraseLeft = 0;
    let hold = 0;
    let rest = 2;
    let beatLen = 1;
    const start = ctx.currentTime;
    let nextMotifAt = start + (spec.motifFirst ? between(spec.motifFirst) : Infinity);
    let playing = true;
    let episodeEnds = spec.episodeOn ? start + between(spec.episodeOn) : Infinity;

    this.startPulse(
      ctx,
      () => {
        const atRest = spec.beat();
        // The beat as it will actually sound, for spacing a motif's notes.
        beatLen = atRest / this.tempo();
        return atRest;
      },
      (t0, beat) => {
        if (t0 >= episodeEnds && spec.episodeOn && spec.episodeOff) {
          playing = !playing;
          episodeEnds = t0 + between(playing ? spec.episodeOn : spec.episodeOff);
          phraseLeft = 0;
          rest = 1;
        }
        if (!playing && this.tension < 0.5) return;
        if (hold > 0) {
          hold--;
          return;
        }
        if (rest > 0) {
          rest--;
          return;
        }
        if (spec.onBeat && !spec.onBeat(beat)) return;

        if (phraseLeft === 0) {
          if (spec.motif && t0 >= nextMotifAt) {
            const { freqs, beats } = this.varyMotif(spec.motif);
            let t = t0;
            freqs.forEach((freq, i) => {
              spec.play(t, freq, { last: i === freqs.length - 1, motif: true, beats: beats[i] });
              t += beats[i] * beatLen;
            });
            hold = Math.ceil(beats.reduce((a, b) => a + b, 0));
            rest = Math.round(between(spec.gap));
            const lastFreq = freqs[freqs.length - 1];
            degree = spec.scale.reduce((best, f, i) => (Math.abs(Math.log(f / lastFreq)) < Math.abs(Math.log(spec.scale[best] / lastFreq)) ? i : best), 0);
            nextMotifAt = t0 + between(spec.motifEvery ?? [120, 240]);
            return;
          }
          phraseLeft = Math.round(between(spec.notes));
          // Start back towards home if the last phrase ended far from it.
          dir = degree > spec.center + 2 ? -1 : degree < spec.center - 2 ? 1 : Math.random() < 0.5 ? -1 : 1;
          lastStep = 0;
        }

        // Step inertia, and a leap turns round (gap fill).
        if (Math.abs(lastStep) >= 3) dir = -Math.sign(lastStep);
        else if (Math.random() < 0.3) dir = -dir;
        let step = drawStep() * dir;
        if (Math.abs(degree + step - spec.center) > 5) step = -step;
        degree += step;
        if (degree < 0) degree = -degree;
        if (degree > top) degree = 2 * top - degree;
        degree = Math.max(0, Math.min(top, degree));
        lastStep = step;

        phraseLeft--;
        const last = phraseLeft === 0;
        if (last) degree = nearest(spec.land, degree);
        const beats = last ? 2 + Math.floor(Math.random() * 2) : Math.random() < 0.22 ? 2 : 1;
        spec.play(t0, spec.scale[degree], { last, motif: false, beats });
        hold = beats - 1;
        if (last) rest = Math.round(between(spec.gap) * (1 - this.tension * 0.5));
      },
    );
  }

  /**
   * One statement of a motif, varied: from one of its roots, and either whole,
   * slowed by half again, or only its opening few notes with the last held.
   */
  private varyMotif(m: Motif): { freqs: number[]; beats: number[] } {
    const root = m.roots[Math.floor(Math.random() * m.roots.length)];
    let semis = m.semis;
    let beats = m.beats;
    const r = Math.random();
    if (r < 0.3 && semis.length > 3) {
      const n = 2 + Math.floor(Math.random() * (semis.length - 3));
      semis = semis.slice(0, n);
      beats = [...beats.slice(0, n - 1), beats[n - 1] * 2];
    } else if (r < 0.55) {
      beats = beats.map((b) => b * 1.5);
    }
    return { freqs: semis.map((s) => root * Math.pow(2, s / 12)), beats: [...beats] };
  }

  /**
   * How much rarer a room's one-shots have become by now: 1 when the room
   * opens, rising to 1.8 over twenty minutes. A bird is charming the first few
   * times and a nag by the fortieth; the more distinctive a sound is, the more
   * its welcome wears out, so the room thins its own weather over a session.
   */
  private wear(ctx: AudioContext): number {
    return 1 + 0.8 * Math.min(1, Math.max(0, ctx.currentTime - this.ambienceStartedAt) / 1200);
  }

  /**
   * Coastal Terrace: waves on a shore under a warm Am9 bed, and gulls a long
   * way off.
   *
   * The waves used to be one sine LFO at 0.07Hz: a swell every 14.3 seconds,
   * exactly, for as long as the app was open - the most regular thing in the
   * game, and a sea is never regular. Each wave is now its own swell, with its
   * own rise, fall, size and brightness, arriving in sets with the odd lull.
   */
  private startCoastal(ctx: AudioContext, out: GainNode): void {
    const src = ctx.createBufferSource();
    src.buffer = this.getNoise(ctx);
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 380;
    const waveGain = ctx.createGain();
    waveGain.gain.value = 0.025;
    src.connect(filter).connect(waveGain).connect(out);
    src.start();

    let stopped = false;
    let timer = 0;
    const wave = () => {
      if (stopped) return;
      const t0 = ctx.currentTime;
      const rise = 2.8 + Math.random() * 3.5;
      const fall = 4.5 + Math.random() * 6;
      const peak = 0.045 + Math.random() * 0.05;
      const trough = 0.018 + Math.random() * 0.014;
      const g = waveGain.gain;
      g.cancelScheduledValues(t0);
      g.setValueAtTime(g.value, t0);
      g.linearRampToValueAtTime(peak, t0 + rise);
      g.linearRampToValueAtTime(trough, t0 + rise + fall);
      // A bigger wave breaks brighter, and only so bright: the hiss of foam is
      // where a sea turns into static.
      const f = filter.frequency;
      f.cancelScheduledValues(t0);
      f.setValueAtTime(f.value, t0);
      f.linearRampToValueAtTime(420 + peak * 5200, t0 + rise);
      f.linearRampToValueAtTime(320, t0 + rise + fall);
      // Waves come in sets: usually the next starts as this one drains, and
      // now and then the sea goes quiet for a few seconds.
      const lull = Math.random() < 0.15 ? 4 + Math.random() * 7 : 0;
      timer = window.setTimeout(wave, (rise + fall * (0.6 + Math.random() * 0.4) + lull) * 1000);
    };
    wave();
    this.ambienceStops.push(() => {
      stopped = true;
      window.clearTimeout(timer);
      src.stop();
      waveGain.disconnect();
    });

    this.startDriftingVoices(ctx, out, VENUE_BEDS.coastal);

    // Gulls, far off over the water: two or three falling cries from one place.
    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.45 + Math.random() * 0.4);
        const cries = 2 + Math.floor(Math.random() * 2);
        const f = 1250 + Math.random() * 300;
        for (let i = 0; i < cries; i++) {
          this.ambientBlip(ctx, out, t0 + i * (0.42 + Math.random() * 0.12), { freq: f * (1 - i * 0.04), freqTo: f * 0.68, gain: 0.006, duration: 0.32, pan });
        }
      },
      () => 45000 + Math.random() * 75000,
      18000,
    );
  }

  /**
   * Alpine Lodge: a fire in the grate under a deep, cosy bed.
   *
   * The fire used to crackle every 90 to 640 milliseconds for ever, high-passed
   * into 1.4-4kHz - the band the ear is most sensitive to, ticking at nearly
   * three a second. A real fire is mostly a low hush that pops now and then and
   * occasionally spits. So: a few soft crackles in the low mids, a spit of
   * several every so often, and a log settling with a thump once in a while.
   */
  private startAlpine(ctx: AudioContext, out: GainNode): void {
    this.startNoiseBed(ctx, out, { filter: "lowpass", freq: 240, gain: 0.05, lfoRate: 0.13, lfoGainDepth: 0.015 });
    this.startDriftingVoices(ctx, out, VENUE_BEDS.alpine);

    // The grate is in one place: everything the fire does comes from there.
    const grate = -0.25;
    const crackle = (t0: number, loud: number) =>
      this.ambientPop(ctx, out, t0, {
        filter: "bandpass",
        freq: 800 + Math.random() * 1300,
        peak: (0.004 + Math.random() * 0.01) * loud,
        duration: 0.012 + Math.random() * 0.04,
        pan: grate + (Math.random() - 0.5) * 0.3,
      });
    // The ordinary crackle: about one every two seconds. Texture, so it does
    // not wear off.
    this.startSparse(ctx, (t0) => crackle(t0, 1), () => 900 + Math.random() * 2100, 0, { wears: false });
    // A spit: a quick run of pops as a pocket of sap goes.
    this.startSparse(
      ctx,
      (t0) => {
        const pops = 4 + Math.floor(Math.random() * 6);
        let t = t0;
        for (let i = 0; i < pops; i++) {
          crackle(t, 1.4);
          t += 0.03 + Math.random() * 0.12;
        }
      },
      () => 14000 + Math.random() * 20000,
      4000,
    );
    // A log settling: a soft low thump, and the fire answers.
    this.startSparse(
      ctx,
      (t0) => {
        this.ambientPop(ctx, out, t0, { filter: "lowpass", freq: 170, peak: 0.05, duration: 0.3, pan: grate });
        for (let i = 0; i < 5; i++) crackle(t0 + 0.12 + i * (0.05 + Math.random() * 0.09), 1.2);
      },
      () => 55000 + Math.random() * 65000,
      25000,
    );
  }

  /**
   * The Reading Room: hushed room tone, a page turning now and then, and the
   * clock on the mantel.
   *
   * The pages turned every three to nine seconds at 1.8-3.4kHz - a brushed
   * click right in the ear's most sensitive band, nine times a minute. Real
   * readers turn a page every half minute or so, and paper is softer than that.
   * The clock is the room's own voice: two soft strokes, rarely, in the bed's
   * key, which is the one thing here a player could come to recognise.
   */
  private startLibrary(ctx: AudioContext, out: GainNode): void {
    this.startNoiseBed(ctx, out, { filter: "lowpass", freq: 180, gain: 0.035, lfoRate: 0.05, lfoGainDepth: 0.01 });
    this.startDriftingVoices(ctx, out, VENUE_BEDS.library);

    const page = (t0: number, pan: number) =>
      this.ambientPop(ctx, out, t0, { filter: "bandpass", freq: 900 + Math.random() * 900, peak: 0.008 + Math.random() * 0.008, duration: 0.2 + Math.random() * 0.14, pan });
    this.startSparse(
      ctx,
      (t0) => {
        // Somebody at another table: each reader sits somewhere.
        const pan = (Math.random() - 0.5) * 1.2;
        page(t0, pan);
        // Sometimes a page turned back to check something.
        if (Math.random() < 0.2) page(t0 + 0.5 + Math.random() * 0.4, pan);
      },
      () => 14000 + Math.random() * 22000,
      5000,
    );
    this.startSparse(
      ctx,
      (t0) => {
        const pan = 0.55;
        this.struck(ctx, out, t0, { freq: 329.63, gain: 0.016, seconds: 3.4, pan, partials: GLASS_PARTIALS, bright: 0.5 }); // E4
        this.struck(ctx, out, t0 + 1.6, { freq: 220.0, gain: 0.018, seconds: 4.2, pan, partials: GLASS_PARTIALS, bright: 0.5 }); // A3
      },
      () => 130000 + Math.random() * 110000,
      60000,
    );
  }

  /**
   * Garden Pavilion: open air, a breeze through cane, birds, and a wind chime
   * hanging from the eaves.
   *
   * The birds called every four to thirteen seconds - around four hundred times
   * an hour - from dead center. They are rarer now, three kinds rather than
   * one, lower, and each from somewhere in the garden. The chime is the room's
   * identity: a few notes when the breeze catches it, always the bed's G major
   * pentatonic, always hanging in the same place.
   */
  private startPavilion(ctx: AudioContext, out: GainNode): void {
    this.startNoiseBed(ctx, out, { filter: "bandpass", freq: 700, gain: 0.03, lfoRate: 0.09, lfoGainDepth: 0.018, lfoFilterDepth: 240 });
    this.startDriftingVoices(ctx, out, VENUE_BEDS.pavilion);

    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.35 + Math.random() * 0.55);
        const kind = Math.random();
        if (kind < 0.45) {
          // Two or three quick falling notes.
          const base = 1500 + Math.random() * 700;
          const notes = 2 + Math.floor(Math.random() * 2);
          for (let i = 0; i < notes; i++) {
            const f = base * (1 - i * 0.08);
            this.ambientBlip(ctx, out, t0 + i * 0.13, { freq: f, freqTo: f * 0.84, gain: 0.012, duration: 0.1, pan });
          }
        } else if (kind < 0.75) {
          // One rising "sweet?".
          const f = 1400 + Math.random() * 400;
          this.ambientBlip(ctx, out, t0, { freq: f, freqTo: f * 1.3, gain: 0.011, duration: 0.22, pan });
        } else {
          // A soft trill.
          const f = 1700 + Math.random() * 300;
          const n = 5 + Math.floor(Math.random() * 4);
          for (let i = 0; i < n; i++) this.ambientBlip(ctx, out, t0 + i * 0.065, { freq: f, freqTo: f * 0.96, gain: 0.007, duration: 0.05, pan });
        }
      },
      () => 9000 + Math.random() * 17000,
      3000,
    );

    // G major pentatonic, G5 to E6: the chime's five tubes.
    const tubes = [783.99, 880.0, 987.77, 1174.66, 1318.51];
    this.startSparse(
      ctx,
      (t0) => {
        const strikes = 3 + Math.floor(Math.random() * 4);
        let t = t0;
        for (let i = 0; i < strikes; i++) {
          this.struck(ctx, out, t, {
            freq: tubes[Math.floor(Math.random() * tubes.length)],
            gain: 0.007 + Math.random() * 0.007,
            seconds: 2.4 + Math.random() * 1.2,
            pan: 0.3 + (Math.random() - 0.5) * 0.2,
            partials: GLASS_PARTIALS,
            bright: 0.6,
          });
          t += 0.18 + Math.random() * 0.5;
        }
      },
      () => 35000 + Math.random() * 45000,
      15000,
    );

    /*
     * An Epic room's extra instrument (theme/rarity.ts): somebody in the garden
     * with a small harp, running up the bed's pentatonic and letting it ring.
     * A figure, not a tune - the sets' rooms are the ones with phrases - and on
     * the far side from the chime, so the two never land in one place.
     */
    const harp = [196.0, 220.0, 246.94, 293.66, 329.63, 392.0, 440.0, 493.88, 587.33];
    this.startSparse(
      ctx,
      (t0) => {
        const notes = 4 + Math.floor(Math.random() * 3);
        const from = Math.floor(Math.random() * (harp.length - notes));
        const pan = -0.35 - Math.random() * 0.25;
        for (let i = 0; i < notes; i++) {
          this.pluck(ctx, out, t0 + i * (0.17 + Math.random() * 0.05), {
            freq: harp[from + i],
            gain: 0.014 - i * 0.0012,
            seconds: 3.2,
            damping: 0.996,
            pan,
          });
        }
      },
      () => 45000 + Math.random() * 40000,
      20000,
    );
  }

  /**
   * Night Observatory: cold air through the dome's slit, chimes like brass
   * settling in the cold, and now and then the dome turning.
   *
   * The air was high-passed noise above 900Hz - a hiss, which is the fault
   * Olympus' wind was fixed for. It is a low moving band now. The chimes were
   * any pitch from 1046 to 1846Hz, which is to say out of tune with the bed
   * almost every time; they are the bed's own open fifths and fourths now, two
   * octaves up.
   */
  private startObservatory(ctx: AudioContext, out: GainNode): void {
    // Louder than the hiss was, because it is all low: the hiss was most of this
    // room's loudness, and taking it out left the room 7dB quieter than its
    // neighbours.
    this.startNoiseBed(ctx, out, { filter: "bandpass", freq: 380, gain: 0.03, lfoRate: 0.04, lfoGainDepth: 0.013, lfoFilterDepth: 160 });
    this.startDriftingVoices(ctx, out, VENUE_BEDS.observatory);

    // G5 A5 D6 E6 G6: no thirds, like the bed.
    const chimes = [783.99, 880.0, 1174.66, 1318.51, 1567.98];
    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() - 0.5) * 1.4;
        const i = Math.floor(Math.random() * chimes.length);
        this.ambientBlip(ctx, out, t0, { freq: chimes[i], gain: 0.012, duration: 1.8 + Math.random() * 0.8, pan });
        if (Math.random() < 0.35) {
          const j = Math.max(0, Math.min(chimes.length - 1, i + (Math.random() < 0.5 ? -2 : 2)));
          this.ambientBlip(ctx, out, t0 + 0.8 + Math.random() * 0.8, { freq: chimes[j], gain: 0.009, duration: 2.2, pan: -pan * 0.5 });
        }
      },
      () => 14000 + Math.random() * 18000,
      6000,
    );

    // The dome turning: a low motor hum that rises a tone and fades.
    this.startSparse(
      ctx,
      (t0) => this.ambientBlip(ctx, out, t0, { freq: 98, freqTo: 110, gain: 0.03, duration: 5 + Math.random() * 2, pan: (Math.random() - 0.5) * 0.6 }),
      () => 100000 + Math.random() * 90000,
      45000,
    );

    /*
     * An Epic room's extra instrument (theme/rarity.ts): the orrery. A brass
     * star map being wound, so a few tine notes climb or fall through the
     * chimes' own open fifths, the planets ticking round. A figure, not a tune -
     * phrases stay with the sets' rooms - and rarer than the chimes, so it is
     * something you notice rather than the room's texture.
     */
    const orrery = [392.0, 440.0, 587.33, 659.25, 783.99, 880.0];
    this.startSparse(
      ctx,
      (t0) => {
        const notes = 4 + Math.floor(Math.random() * 3);
        const rising = Math.random() < 0.6;
        const pan = (Math.random() - 0.5) * 0.8;
        for (let i = 0; i < notes; i++) {
          const k = rising ? i : notes - 1 - i;
          this.struck(ctx, out, t0 + i * 0.24, {
            freq: orrery[k % orrery.length],
            gain: 0.007,
            seconds: 2.6,
            pan: pan + (i / notes - 0.5) * 0.4,
            partials: TINE_PARTIALS,
            bright: 0.45,
          });
        }
      },
      () => 55000 + Math.random() * 45000,
      25000,
    );
  }

  /**
   * The Velvet Room: a warm smoky bass bed, a drummer on brushes who plays a
   * while and lays out a while, and somebody at the vibraphone now and then.
   *
   * The brushes used to land every 1.7-2.6 seconds at 2.6kHz from launch to
   * shutdown - a tick nobody could stop hearing once they had. A drummer in a
   * club plays in PASSAGES: a few bars of swing, softly, then nothing for a
   * while. The sweep is down around 1.5kHz now, where brushes on a snare
   * actually sit, with the two and the four leaned on. The vibes are the room's
   * identity: a rolled chord from the bed's own Ab pentatonic.
   */
  private startVelvet(ctx: AudioContext, out: GainNode): void {
    this.startNoiseBed(ctx, out, { filter: "lowpass", freq: 320, gain: 0.03, lfoRate: 0.11, lfoGainDepth: 0.014 });
    this.startDriftingVoices(ctx, out, VENUE_BEDS.velvet);

    let playing = false;
    let left = 8;
    this.startPulse(
      ctx,
      () => 0.64 + (Math.random() - 0.5) * 0.02,
      (t0, beat) => {
        if (--left <= 0) {
          playing = !playing;
          // A passage is 16-32 beats; a rest is 70-140 of them (45-90 seconds).
          left = playing ? 16 + Math.floor(Math.random() * 17) : 70 + Math.floor(Math.random() * 71);
        }
        if (!playing) return;
        const backbeat = beat % 2 === 1;
        this.ambientPop(ctx, out, t0, {
          filter: "bandpass",
          freq: 1300 + Math.random() * 400,
          peak: backbeat ? 0.009 : 0.005,
          duration: backbeat ? 0.24 : 0.16,
          pan: 0.2,
        });
        // The swung "and" before the backbeat, sometimes.
        if (!backbeat && Math.random() < 0.3) {
          this.ambientPop(ctx, out, t0 + (0.64 * 0.66) / this.tempo(), { filter: "bandpass", freq: 1400, peak: 0.003, duration: 0.08, pan: 0.2 });
        }
      },
    );

    // Eb4 F4 Ab4 Bb4 C5: the bed's pentatonic, an octave up.
    const vibes = [311.13, 349.23, 415.3, 466.16, 523.25];
    this.startSparse(
      ctx,
      (t0) => {
        const first = Math.floor(Math.random() * (vibes.length - 2));
        const notes = [first, first + 2, Math.random() < 0.6 ? first + 1 : -1].filter((i) => i >= 0 && i < vibes.length);
        notes.forEach((i, k) =>
          this.struck(ctx, out, t0 + k * 0.05, { freq: vibes[i], gain: 0.011, seconds: 3.8, pan: -0.3, partials: GLASS_PARTIALS, bright: 0.45 }),
        );
      },
      () => 45000 + Math.random() * 55000,
      25000,
    );
  }

  /**
   * Mount Olympus: a kithara turning over in a marble hall, under wind, with
   * bronze and distant thunder.
   *
   * This is the premium soundscape, and what makes it one is not the notes —
   * it is that four things are true here which are true of none of the six
   * above. Worth keeping all four if this is ever retuned:
   *
   *  1. IT IS PLAYED, NOT FILTERED. The kithara is a real plucked string
   *     (`pluck`) and the bowl is real struck metal (`struck`). Every free venue
   *     is noise through a biquad, and noise through a biquad is what "cheap
   *     generative audio" sounds like.
   *  2. IT IS IN A ROOM. `VENUE_ACOUSTICS.olympian` is a 3.6-second bright tail
   *     at the biggest send in the file. Marble is the loudest thing about this
   *     venue and you hear it before you notice a single note.
   *  3. IT IS IN TIME. The line runs on `startPhrases`, against the audio clock,
   *     in phrases with a motif it comes back to.
   *  4. IT KNOWS WHAT THE GAME IS DOING. As `tension` rises the phrase tightens
   *     and the low octave joins underneath; a round that is nearly over does
   *     not sound like one that has just started.
   *
   * The mode is D Dorian — the diatonic scale the Greeks actually named, and
   * conveniently the one that reads as "ancient" without reading as "funeral"
   * the way the minor scale does. The line walks its pentatonic core, and the
   * Dorian sixth lives in the motif (see below).
   */
  private startOlympian(ctx: AudioContext, out: GainNode): void {
    // Wind round the summit — the altitude. Deliberately quieter than any other
    // bed: the reverb is doing the work of describing the space, and a loud bed
    // on top of a long tail turns into mud.
    //
    // It was a high-passed hiss (everything above 1.1kHz), and through this
    // room's long bright tail that read as STATIC, loud and constant - the one
    // thing players said about the venue's sound. Wind is a BAND, low and
    // moving: noise round 420Hz whose center sweeps with the gusts, and a level
    // that swells and very nearly drops out between them, so there is always
    // somewhere quiet in it.
    this.startNoiseBed(ctx, out, {
      filter: "bandpass",
      freq: 420,
      gain: 0.0065,
      lfoRate: 0.045,
      lfoGainDepth: 0.0055,
      lfoFilterDepth: 220,
    });

    this.startDriftingVoices(ctx, out, VENUE_BEDS.olympian);

    /*
     * D minor PENTATONIC, D3 to C5: Dorian with its two semitones taken out.
     *
     * The kithara walked all seven notes of D Dorian, and its notes ring for
     * two to three and a half seconds in the longest bright tail in the game -
     * so the E against the F, the B against the C and the F against the B were
     * all routinely sounding together, smeared by the marble. The two rooms
     * whose notes ring (the Midnight Zone, the porch) are pentatonic for exactly
     * this reason, and the abyss is the one players find easiest to live with.
     *
     * Dorian's own note, the B, is not gone: it is in the MOTIF, a short note
     * between an A and an A, which is where a characteristic note does its work
     * - heard as the color of a phrase rather than held against anything.
     */
    const scale = [146.83, 174.61, 196.0, 220.0, 261.63, 293.66, 349.23, 392.0, 440.0, 523.25];
    const D3 = 0;
    const A3 = 3;
    const D4 = 5;
    const A4 = 8;
    this.startPhrases(ctx, {
      scale,
      // A lyre player's habit: mostly steps, with the open fourths and fifths a
      // lyre's tuning makes easy.
      steps: [
        [1, 6],
        [2, 3],
        [3, 1.4],
        [4, 0.5],
      ],
      center: D4,
      notes: [3, 6],
      gap: [3, 8],
      land: [D3, A3, D4, A4],
      // Unhurried at rest, and never exactly on a grid. (Tighter as the lap
      // closes too, as every room is: see `startPulse`.)
      beat: () => 1.0 + (Math.random() - 0.5) * 0.14,
      /*
       * The motif: up a fifth, touch the sixth, and settle by step - a lyre
       * call, D A B A F G. It carries the B, and it is the closest thing in the
       * room to a tune: heard every couple of minutes, from D4 or D3, whole,
       * slowed, or only its rising opening.
       */
      motif: { semis: [0, 7, 9, 7, 3, 5], beats: [1, 1, 0.5, 1.5, 1, 2], roots: [293.66, 146.83] },
      motifFirst: [20, 45],
      motifEvery: [100, 190],
      episodeOn: [100, 200],
      episodeOff: [35, 80],
      play: (t0, freq, n) => {
        // Pan follows the contour, so the part occupies a width rather than a
        // point. Low notes sit left, high notes right.
        const pan = (Math.log(freq / 146.83) / Math.log(523.25 / 146.83) - 0.5) * 0.7 + (Math.random() - 0.5) * 0.12;
        this.pluck(ctx, out, t0, {
          freq,
          gain: (0.04 + Math.random() * 0.025) * (1 + this.tension * 0.4) * (n.motif ? 1.1 : 1),
          seconds: n.last ? 3.4 : n.beats > 1 ? 3 : 2.2 + Math.random() * 0.8,
          // Kept under 0.9952, a cutoff of about 4kHz. Over that, every pluck
          // opened with a bright noise burst right in the ear's most sensitive
          // band, and the marble held on to it.
          damping: 0.994 + Math.random() * 0.0012,
          pan,
        });
        // An octave below, only when the round is closing and only on a
        // phrase's last note — weight, where the line comes to rest.
        if (this.tension > 0.55 && n.last && Math.random() < 0.5) {
          this.pluck(ctx, out, t0 + 0.02, { freq: freq / 2, gain: 0.028, seconds: 3.4, damping: 0.997, pan: -pan * 0.6 });
        }
      },
    });

    // A bronze bowl, struck rarely and never in time with anything. Panned wide,
    // because the one thing it must not sound like is part of the phrase.
    this.startSparse(
      ctx,
      (t0) => {
        const pan = Math.random() > 0.5 ? 0.62 : -0.62;
        this.struck(ctx, out, t0, { freq: 73.42 * (Math.random() > 0.6 ? 2 : 1), gain: 0.05, seconds: 7, pan });
      },
      () => 17000 + Math.random() * 26000,
      6000,
    );

    // Thunder somewhere below the mountain: a slow swell of very low noise,
    // rare enough that it is never expected. Zeus is in the deck; this is him
    // being somewhere else.
    /*
     * ONE teardown hook, holding whichever roll is currently sounding — not one
     * pushed per firing. `ambienceStops` is only emptied when the ambience stops,
     * so a hook per roll would grow that array for as long as the app is open,
     * and every one of them would be a closure over a source that finished
     * minutes ago. Rolls never overlap (3.6s long, at least 34s apart), so a
     * single slot is all there is to track.
     */
    let thunder: AudioBufferSourceNode | null = null;
    this.startSparse(
      ctx,
      (t0) => {
        const src = ctx.createBufferSource();
        src.buffer = this.getNoise(ctx);
        src.loop = true;
        const f = ctx.createBiquadFilter();
        f.type = "lowpass";
        f.frequency.setValueAtTime(150, t0);
        f.frequency.exponentialRampToValueAtTime(48, t0 + 3.4);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.linearRampToValueAtTime(0.05, t0 + 0.9);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 3.4);
        src.connect(f).connect(g).connect(this.panned(ctx, out, (Math.random() - 0.5) * 1.2));
        src.start(t0);
        src.stop(t0 + 3.6);
        thunder = src;
      },
      () => 34000 + Math.random() * 48000,
      12000,
    );
    this.ambienceStops.push(() => {
      try {
        thunder?.stop();
      } catch {
        /* already finished on its own */
      }
    });
  }

  /**
   * The Midnight Zone: struck glass firing in the dark over the pressure of a
   * kilometre of water, with something large moving a long way off.
   *
   * The second premium soundscape, and it is deliberately built on the same four
   * properties as Mount Olympus while sharing none of its material — that is
   * what proves those four are the rule rather than a description of one venue:
   *
   *  1. IT IS PLAYED, NOT FILTERED — struck glass (`struck` with
   *     `GLASS_PARTIALS`) and a gliding call (`call`), neither of which exists
   *     anywhere else in this file.
   *  2. IT IS IN A ROOM — but the opposite room. `VENUE_ACOUSTICS.biolume` is
   *     the longest tail here and by far the darkest, because water absorbs
   *     highs where marble returns them.
   *  3. IT IS IN TIME — `startPulse`, so the flashes have a pulse to be sparse
   *     against.
   *  4. IT KNOWS WHAT THE GAME IS DOING — the three `tension` reads below.
   *
   * The mode is C# minor pentatonic. A pentatonic holds no semitone and no
   * tritone anywhere, so the walk cannot produce an interval that wants
   * resolving however it wanders — which matters more here than on Olympus,
   * because these notes ring for three or four seconds and routinely overlap
   * two and three deep.
   */
  private startBiolume(ctx: AudioContext, out: GainNode): void {
    /*
     * Pressure, not wind. Olympus' bed is thin, high and very quiet because
     * altitude is; this is the other end of the same idea — everything under
     * 130Hz, swelling on a forty-second breath. It is the loudest bed in the
     * file and still barely a sound: you notice it when it stops.
     */
    this.startNoiseBed(ctx, out, {
      filter: "lowpass",
      freq: 130,
      gain: 0.034,
      lfoRate: 0.026,
      lfoGainDepth: 0.014,
      lfoFilterDepth: 55,
    });

    this.startDriftingVoices(ctx, out, VENUE_BEDS.biolume);

    // C# minor pentatonic, G#3 up to F#5.
    const scale = [207.65, 246.94, 277.18, 329.63, 369.99, 415.3, 493.88, 554.37, 659.26, 739.99];
    const STEPS = [-4, -3, -2, -2, -1, -1, -1, 1, 1, 1, 2, 2, 3, 4];
    let degree = 4;
    let breath = 0;
    /*
     * WHERE the light is, as its own slow random walk, rather than where the
     * note is.
     *
     * Olympus pans its kithara by pitch, which is right for an instrument being
     * played by a person sitting somewhere. There is nobody down here and no
     * instrument — each flash is an animal — so consecutive flashes arrive from
     * NEARBY places and the position drifts on a clock of its own. That one
     * change is what makes the part read as things moving in the dark rather
     * than as notes on a keyboard, and it costs a variable.
     */
    let place = 0;
    /*
     * The motif, and the one thing added to the room players liked best:
     * C# up to G#, back to E, rising to F# - four notes over nine seconds at
     * this pulse, a slow light-trail, stated every two or three minutes and
     * varied each time (`varyMotif`). Nothing else about the walk changed.
     */
    const MOTIF: Motif = { semis: [0, 7, 3, 5], beats: [1.5, 1, 1, 2.5], roots: [277.18] };
    let nextMotifAt = ctx.currentTime + 40 + Math.random() * 40;
    this.startPulse(
      ctx,
      // Slower than Olympus at rest — this room has further for a sound to
      // travel — and never exactly on a grid.
      () => 1.45 + (Math.random() - 0.5) * 0.3,
      (t0) => {
        if (breath > 0) {
          breath--;
          return;
        }
        // Sparser than Olympus, too: nearly two thirds of beats are a rest and
        // the part sometimes stops for the best part of ten seconds. Long notes
        // need long gaps or they stack into a chord and the whole thing
        // collapses back into a pad.
        if (Math.random() < 0.58 - this.tension * 0.24) {
          if (Math.random() < 0.14) breath = 2 + Math.floor(Math.random() * 5);
          return;
        }

        if (t0 >= nextMotifAt) {
          nextMotifAt = t0 + 120 + Math.random() * 90;
          const { freqs, beats } = this.varyMotif(MOTIF);
          const beatLen = 1.45 / this.tempo();
          let t = t0;
          freqs.forEach((freq, i) => {
            this.struck(ctx, out, t, { freq, gain: 0.04 * (1 + this.tension * 0.35), seconds: 4.4, pan: place + (i - 1.5) * 0.12, partials: GLASS_PARTIALS });
            t += beats[i] * beatLen;
          });
          breath = Math.ceil(beats.reduce((a, b) => a + b, 0)) + 2;
          degree = 2; // C#4, where the trail began
          return;
        }

        degree += STEPS[Math.floor(Math.random() * STEPS.length)];
        // Reflect off the ends rather than clamping, which would park the walk
        // on the top or bottom note and repeat it audibly.
        if (degree < 0) degree = -degree;
        if (degree > scale.length - 1) degree = 2 * (scale.length - 1) - degree;
        degree = Math.max(0, Math.min(scale.length - 1, degree));

        place = Math.max(-0.78, Math.min(0.78, place + (Math.random() - 0.5) * 0.42));

        const flash = (at: number, deg: number, level: number, pan: number) =>
          this.struck(ctx, out, at, {
            freq: scale[Math.max(0, Math.min(scale.length - 1, deg))],
            gain: level * (1 + this.tension * 0.35),
            seconds: 3.2 + Math.random() * 1.8,
            pan,
            partials: GLASS_PARTIALS,
          });

        flash(t0, degree, 0.028 + Math.random() * 0.022, place + (Math.random() - 0.5) * 0.16);

        /*
         * The tension gesture: as the round closes, a flash sometimes sets off
         * two or three more beside it.
         *
         * Olympus answers a closing round by adding an octave underneath, which
         * is WEIGHT. This answers with a cluster, which is ALARM — and it is the
         * one thing a body of water full of bioluminescence actually does when
         * something disturbs it. Same hook, opposite gesture, and the venue is
         * recognisable in the difference.
         */
        if (this.tension > 0.45 && Math.random() < this.tension * 0.3) {
          const answers = 2 + Math.floor(Math.random() * 2);
          for (let i = 1; i <= answers; i++) {
            flash(
              t0 + i * (0.07 + Math.random() * 0.06),
              degree + (Math.random() > 0.5 ? 1 : -1) * (1 + Math.floor(Math.random() * 2)),
              0.016 + Math.random() * 0.012,
              place + (Math.random() - 0.5) * 0.5,
            );
          }
        }
      },
    );

    /*
     * Something large, a long way off. This venue's answer to Olympus' thunder,
     * and tracked the same way for the same reason: ONE slot holding whichever
     * call is currently sounding, rather than a teardown hook pushed per firing,
     * which would grow `ambienceStops` for as long as the app is open. Calls run
     * to six seconds and are never less than fifty apart, so they cannot overlap
     * and one slot is all there is to track.
     *
     * It waits half a minute for the first one. A creature that greets you the
     * instant the screen loads is a UI sound; one that arrives while you are
     * already playing is something that was there the whole time.
     */
    let calling: (() => void) | null = null;
    this.startSparse(
      ctx,
      (t0) => {
        // One animal, so one voice: the call keeps the same shape every time -
        // up most of a fifth, down below where it started - and only its
        // starting pitch, length and place vary. A shape that comes back is a
        // creature; a random glide each time is a synthesizer.
        const from = 56 + Math.random() * 12;
        calling = this.call(ctx, out, t0, {
          from,
          peak: from * (1.68 + Math.random() * 0.08),
          to: from * (0.7 + Math.random() * 0.05),
          gain: 0.05,
          seconds: 3.6 + Math.random() * 2.2,
          pan: (Math.random() - 0.5) * 1.5,
        });
      },
      () => 52000 + Math.random() * 74000,
      31000,
    );
    this.ambienceStops.push(() => calling?.());

    // A bubble train venting somewhere off to one side: a few quick rising
    // blips, tiny, all from the same place. Cheap, and the one element here that
    // says the water is a fluid rather than a color.
    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() - 0.5) * 1.4;
        let freq = 420 + Math.random() * 380;
        const bubbles = 3 + Math.floor(Math.random() * 4);
        for (let i = 0; i < bubbles; i++) {
          this.ambientBlip(ctx, out, t0 + i * (0.05 + Math.random() * 0.07), {
            freq,
            freqTo: freq * 1.5,
            gain: 0.008 + Math.random() * 0.006,
            duration: 0.05 + Math.random() * 0.04,
            pan,
          });
          // Each bubble is smaller than the last as the vent empties, and a
          // smaller bubble rings higher.
          freq *= 1.1 + Math.random() * 0.14;
        }
      },
      () => 15000 + Math.random() * 24000,
      9000,
    );
  }

  /**
   * A cat purring nearby: low noise, rattled on and off twenty-odd times a
   * second, breathing in and out.
   *
   * The rattle is the purr — the larynx opening and closing at 25Hz or so — and
   * it is done as amplitude modulation on filtered noise rather than as an
   * oscillator, because a purr has no pitch; a low sine rattled the same way is
   * a motor. The BREATH is what makes it an animal: it swells on the out-breath
   * and thins on the in-breath, and the rattle runs a little slower going out.
   * A steady purr of constant level is a fridge.
   *
   * Returns its own stop, like `call`, so it cannot outlive the room.
   */
  private purr(ctx: AudioContext, out: GainNode, t0: number, opts: { gain: number; breaths: number; pan?: number }): () => void {
    const src = ctx.createBufferSource();
    src.buffer = this.getNoise(ctx);
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 260;
    lp.Q.value = 0.9;
    const rattle = ctx.createGain();
    rattle.gain.value = 0.5;
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    const depth = ctx.createGain();
    depth.gain.value = 0.5;
    lfo.connect(depth).connect(rattle.gain);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t0);
    let t = t0;
    for (let i = 0; i < opts.breaths; i++) {
      const outBreath = 1.3 + Math.random() * 0.4;
      const inBreath = 0.9 + Math.random() * 0.3;
      // Out: fuller and a touch slower.
      lfo.frequency.setValueAtTime(23 + Math.random() * 2, t);
      env.gain.linearRampToValueAtTime(opts.gain, t + outBreath * 0.3);
      env.gain.linearRampToValueAtTime(opts.gain * 0.7, t + outBreath);
      t += outBreath;
      // In: thinner, quicker.
      lfo.frequency.setValueAtTime(27 + Math.random() * 2, t);
      env.gain.linearRampToValueAtTime(opts.gain * 0.35, t + inBreath * 0.5);
      env.gain.linearRampToValueAtTime(opts.gain * 0.45, t + inBreath);
      t += inBreath;
    }
    env.gain.linearRampToValueAtTime(0.0001, t + 0.6);
    src.connect(lp).connect(rattle).connect(env).connect(this.panned(ctx, out, opts.pan));
    src.start(t0);
    lfo.start(t0);
    src.stop(t + 0.7);
    lfo.stop(t + 0.7);
    return () => {
      for (const node of [src, lfo]) {
        try {
          node.stop();
        } catch {
          /* already finished on its own */
        }
      }
    };
  }

  /**
   * A horn heard a long way off: a sawtooth through a lowpass that opens slowly
   * under a soft attack, which is what a brass tone does as the player's lips
   * settle and the air column speaks.
   *
   * It is the Mead Hall's Gjallarhorn, and the two things that make it a horn
   * rather than a synth lead are the same two that make it FAR. The note is
   * scooped - it starts a hair flat and climbs into pitch, as a lip does - and
   * the lowpass never opens past about a kilohertz, because distance is a
   * lowpass and a horn across a valley has lost everything above its
   * lower harmonics. The signal is also tapped into `echo`, the valley's slap
   * back off the far hillside: reverb send is a property of the whole venue
   * (`VENUE_ACOUSTICS`), not of a voice, so the far-away wetness a single
   * voice needs has to be built for that voice.
   *
   * Returns its own stop, like `organ`.
   */
  private horn(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { freq: number; gain: number; seconds: number; release: number; pan?: number; echo?: AudioNode },
  ): () => void {
    // The call voice's kind: a distant animal-or-horn call is a one-shot to the lab, not melody.
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "call" });
    const attack = 0.45 + Math.random() * 0.2;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.Q.value = 0.8;
    lowpass.frequency.setValueAtTime(opts.freq * 1.3, t0);
    lowpass.frequency.exponentialRampToValueAtTime(Math.min(1100, opts.freq * 4.2), t0 + attack * 1.8);

    const held = t0 + Math.max(attack + 0.2, opts.seconds);
    const end = held + opts.release;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(opts.gain, t0 + attack);
    g.gain.setValueAtTime(opts.gain, held);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    lowpass.connect(g).connect(this.panned(ctx, out, opts.pan));
    if (opts.echo) {
      const tap = ctx.createGain();
      tap.gain.value = 0.9;
      g.connect(tap).connect(opts.echo);
    }

    const sources: OscillatorNode[] = [];
    for (const [type, level] of [["sawtooth", 1], ["sine", 0.5]] as const) {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(opts.freq * 0.97, t0);
      osc.frequency.exponentialRampToValueAtTime(opts.freq, t0 + 0.14);
      const lv = ctx.createGain();
      lv.gain.value = level;
      osc.connect(lv).connect(lowpass);
      osc.start(t0);
      osc.stop(end + 0.05);
      sources.push(osc);
    }
    return () => {
      for (const osc of sources) {
        try {
          osc.stop();
        } catch {
          /* already finished on its own */
        }
      }
    };
  }

  /**
   * One croak of a raven, far off: a harsh low buzz with two nasal resonances,
   * falling in pitch as it goes. A raven's call is "kraa", so the caller plays
   * two of these a beat apart.
   *
   * The harshness is a sawtooth whose amplitude is chopped at 60-80Hz, with a
   * little noise through the same filters; a clean tone through the same
   * formants is a clarinet. The nasal quality is the two bandpasses, near 650Hz
   * and 1.25kHz, and the lowpass above them keeps the whole call under 2kHz
   * both because it is distant and because that band belongs to the game.
   */
  private croak(ctx: AudioContext, out: GainNode, t0: number, opts: { freq: number; gain: number; seconds: number; pan?: number }): void {
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "blip" });
    const end = t0 + opts.seconds;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(opts.freq, t0);
    osc.frequency.exponentialRampToValueAtTime(opts.freq * 0.78, end);
    const rasp = ctx.createGain();
    rasp.gain.value = 0.6;
    const chop = ctx.createOscillator();
    chop.type = "sine";
    chop.frequency.value = 60 + Math.random() * 20;
    const chopDepth = ctx.createGain();
    chopDepth.gain.value = 0.4;
    chop.connect(chopDepth).connect(rasp.gain);
    osc.connect(rasp);

    const noise = ctx.createBufferSource();
    noise.buffer = this.getNoise(ctx);
    const hiss = ctx.createGain();
    hiss.gain.value = 0.25;
    noise.connect(hiss);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(opts.gain, t0 + 0.035);
    env.gain.setValueAtTime(opts.gain, t0 + opts.seconds * 0.55);
    env.gain.exponentialRampToValueAtTime(0.0001, end);
    const ceiling = ctx.createBiquadFilter();
    ceiling.type = "lowpass";
    ceiling.frequency.value = 1700;
    ceiling.connect(env).connect(this.panned(ctx, out, opts.pan));
    for (const [freq, q] of [[650, 5], [1250, 6]] as const) {
      const formant = ctx.createBiquadFilter();
      formant.type = "bandpass";
      formant.frequency.value = freq;
      formant.Q.value = q;
      rasp.connect(formant).connect(ceiling);
      hiss.connect(formant);
    }
    for (const node of [osc, chop, noise]) node.start(t0);
    for (const node of [osc, chop, noise]) node.stop(end + 0.05);
  }

  /**
   * A frame drum, struck once: a sine thump whose pitch drops from 110 to 55Hz
   * as the skin settles, an octave-up copy for phone speakers that cannot play
   * 55Hz at all (the same trick `call` uses), and a short lowpassed slap of
   * noise for the hand on the skin. Short and dry: the room supplies the rest.
   */
  private frameDrum(ctx: AudioContext, out: GainNode, t0: number, opts: { gain: number; pan?: number }): void {
    this.noted(out, { at: t0, freq: 110, gain: opts.gain, kind: "pop" });
    const target = this.panned(ctx, out, opts.pan);
    for (const [mult, level, life] of [[1, 1, 0.38], [2, 0.3, 0.14]] as const) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(110 * mult, t0);
      osc.frequency.exponentialRampToValueAtTime(55 * mult, t0 + 0.16);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(opts.gain * level, t0 + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + life);
      osc.connect(g).connect(target);
      osc.start(t0);
      osc.stop(t0 + life + 0.05);
    }
    const slap = ctx.createBufferSource();
    slap.buffer = this.getNoise(ctx);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1300;
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.0001, t0);
    sg.gain.exponentialRampToValueAtTime(opts.gain * 0.5, t0 + 0.003);
    sg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
    slap.connect(lp).connect(sg).connect(target);
    slap.start(t0, Math.random() * 1.5);
    slap.stop(t0 + 0.06);
  }

  /**
   * One wordless voice of a choir a long way off: a sawtooth through three
   * vowel formants, doubled a few cents apart, under a slow swell.
   *
   * It is the Singularity's line, and the choir is the one instrument here that
   * is a body of people rather than a person, so three things make it one.
   *
   *  - THE VOWEL IS FORMANTS, not a filter sweep. A sawtooth is a buzz until
   *    two or three narrow bandpasses pick out the resonances of a throat:
   *    about 520-680Hz and 920-1180Hz for an "aah", with a faint third. Each
   *    note draws its own pair, so a phrase drifts between vowels as singers do.
   *  - TWO COPIES, A HAIR APART. A section is never in perfect tune with
   *    itself, and the slow beating between two voices a dozen cents apart is
   *    what turns one singer into a choir. Vibrato arrives only once the note
   *    is speaking, as it does for a singer who has to find the pitch first.
   *  - IT SWELLS IN, over 0.6-1.2 seconds, and the lowpass opens with it. A
   *    voice with a shaped onset is heard as somebody breathing in and singing;
   *    an immediate one is a synth patch. The long onset is also why the line
   *    reads as dread rather than melody: nothing in it ever arrives.
   *
   * Everything sits under a 1.8kHz ceiling, both because distance is a lowpass
   * and because 2-5kHz is the band the cards' sounds need
   * (docs/PREMIUM_VENUES.md).
   *
   * Returns its own stop, like `organ`.
   */
  private choir(
    ctx: AudioContext,
    out: GainNode,
    t0: number,
    opts: { freq: number; gain: number; seconds: number; pan?: number; attack?: number; release?: number },
  ): () => void {
    // Reported as the organ's kind, for the reason `bowed` gives.
    this.noted(out, { at: t0, freq: opts.freq, gain: opts.gain, kind: "organ" });
    const attack = opts.attack ?? 0.6 + Math.random() * 0.6;
    const release = opts.release ?? 1.4;
    const held = t0 + Math.max(attack + 0.3, opts.seconds);
    const end = held + release;

    const ceiling = ctx.createBiquadFilter();
    ceiling.type = "lowpass";
    ceiling.Q.value = 0.5;
    ceiling.frequency.setValueAtTime(800, t0);
    ceiling.frequency.linearRampToValueAtTime(1800, t0 + attack * 1.4);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(opts.gain, t0 + attack);
    g.gain.setValueAtTime(opts.gain, held);
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    ceiling.connect(g).connect(this.panned(ctx, out, opts.pan));

    // The makeup gains are because a narrow bandpass throws most of a sawtooth
    // away; without them the voice would be a whisper next to its own bed.
    const voice = ctx.createGain();
    const vowel: Array<[number, number, number]> = [
      [520 + Math.random() * 160, 5, 2.2],
      [920 + Math.random() * 260, 6, 1.5],
      [1500, 6, 0.5],
    ];
    for (const [freq, q, makeup] of vowel) {
      const formant = ctx.createBiquadFilter();
      formant.type = "bandpass";
      formant.frequency.value = freq;
      formant.Q.value = q;
      const level = ctx.createGain();
      level.gain.value = makeup;
      voice.connect(formant).connect(level).connect(ceiling);
    }

    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 4.6 + Math.random() * 0.8;
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(0, t0);
    depth.gain.setValueAtTime(0, t0 + attack);
    depth.gain.linearRampToValueAtTime(4 + Math.random() * 3, t0 + attack + 0.8);
    lfo.connect(depth);
    lfo.start(t0);
    lfo.stop(end + 0.05);

    const sources: OscillatorNode[] = [lfo];
    for (const cents of [-8, 8]) {
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.frequency.value = opts.freq;
      osc.detune.value = cents;
      depth.connect(osc.detune);
      osc.connect(voice);
      osc.start(t0);
      osc.stop(end + 0.05);
      sources.push(osc);
    }
    return () => {
      for (const osc of sources) {
        try {
          osc.stop();
        } catch {
          /* already finished on its own */
        }
      }
    };
  }

  /**
   * One throb of the Singularity: a deep pulse on C, a sine dropping from G2 to
   * C2 in a tenth of a second, a quieter octave above it for phone speakers
   * that cannot play 65Hz (the trick `frameDrum` and `call` use), and a soft
   * breath of lowpassed noise at the front.
   *
   * It is a timpani heard through a wall, not a kick drum, and the attack is
   * what decides that: the sines take 20ms to arrive and the noise 30, where a
   * struck drum's would be near-instant. A hard onset here is what would make
   * it a beat under a card game, and the whole point is that it is felt.
   */
  private throb(ctx: AudioContext, out: GainNode, t0: number, opts: { gain: number; pan?: number }): void {
    this.noted(out, { at: t0, freq: 65.41, gain: opts.gain, kind: "pop" });
    const target = this.panned(ctx, out, opts.pan);
    for (const [mult, level, life] of [[1, 1, 1.1], [2, 0.32, 0.4]] as const) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(98 * mult, t0);
      osc.frequency.exponentialRampToValueAtTime(65.41 * mult, t0 + 0.1);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(opts.gain * level, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + life);
      osc.connect(g).connect(target);
      osc.start(t0);
      osc.stop(t0 + life + 0.05);
    }
    const breath = ctx.createBufferSource();
    breath.buffer = this.getNoise(ctx);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 240;
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0.0001, t0);
    bg.gain.exponentialRampToValueAtTime(opts.gain * 0.45, t0 + 0.03);
    bg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.24);
    breath.connect(lp).connect(bg).connect(target);
    breath.start(t0, Math.random() * 1.5);
    breath.stop(t0 + 0.26);
  }

  /**
   * The Sun Porch: a kalimba noodling in the afternoon, a breeze through the
   * screen door, garden birds, and every so often the cat.
   *
   * The third premium soundscape, on the same four properties as the other two
   * and sharing no material with either — and the first of the three that is
   * meant to be SWEET, which turned out to be mostly a question of rhythm:
   *
   *  1. IT IS PLAYED, NOT FILTERED — plucked steel tines (`struck` with
   *     `TINE_PARTIALS`), whose overtones are all in the attack, and a purr
   *     (`purr`), which exists nowhere else in this file.
   *  2. IT IS IN A ROOM — the smallest and warmest of the premium rooms. See
   *     `VENUE_ACOUSTICS.porch`.
   *  3. IT IS IN TIME, AND IT SWINGS. The others walk on an even pulse; this
   *     one lilts long-short: notes on the long beat, and the short beat left
   *     empty or given a quiet pickup note. A lilt is the difference between noodling and
   *     plodding, and it is most of what "cute" means in music.
   *  4. IT KNOWS WHAT THE GAME IS DOING — but the gesture is PLAY, not weight
   *     (Olympus) or alarm (the Midnight Zone). As the round closes the pulse
   *     quickens and a note sometimes arrives with a little run of grace notes
   *     skittering up to it, like something pouncing.
   *
   * The mode is F major pentatonic, for the reason the Midnight Zone uses a
   * pentatonic: no semitone and no tritone anywhere, so however the walk
   * wanders and however the tails overlap, nothing ever wants resolving. Major
   * because this is a sunny room, and F because its range sits the tines in the
   * middle of a kalimba rather than at the top of a music box, where they would
   * turn tinkly inside five minutes.
   */
  private startPorch(ctx: AudioContext, out: GainNode): void {
    // Air through a screen door: soft, mid-high and slow to change. Quieter than
    // the free venues' beds — the tines are close and should not have to shout.
    this.startNoiseBed(ctx, out, {
      filter: "bandpass",
      freq: 900,
      gain: 0.014,
      lfoRate: 0.06,
      lfoGainDepth: 0.008,
      lfoFilterDepth: 260,
    });

    this.startDriftingVoices(ctx, out, VENUE_BEDS.porch);

    /*
     * F major pentatonic, A3 up to F5 — a fourth lower than it was.
     *
     * The tines ran C4 to A5, and a tine's cantilever overtone sits at 6.27
     * times the note: 1.6 to 5.5kHz, the band the ear is most sensitive to, on
     * every note. Down here the plink lands at 1.4-4.4kHz and the tine's own
     * partial is softer too (`TINE_PARTIALS`), which is a kalimba held in the
     * lap rather than a music box held to the ear.
     */
    const scale = [220.0, 261.63, 293.66, 349.23, 392.0, 440.0, 523.25, 587.33, 698.46];
    const tine = (at: number, freq: number, level: number, seconds: number) =>
      this.struck(ctx, out, at, {
        freq,
        gain: level,
        seconds,
        // Panned by pitch: this is an instrument somebody is playing, sitting
        // in a chair, so low tines one side and high the other — narrower than
        // Olympus' kithara, because a kalimba fits in two hands.
        pan: (Math.log(freq / 220) / Math.log(698.46 / 220) - 0.5) * 0.5 + (Math.random() - 0.5) * 0.1,
        partials: TINE_PARTIALS,
      });

    // The swing: each pair of beats splits long-short, roughly 2:1, and the
    // pair itself breathes. Never exactly on a grid.
    let longBeat = true;
    let pair = 1;
    this.startPhrases(ctx, {
      scale,
      // Noodling: small steps, the odd skip of a third.
      steps: [
        [1, 6],
        [2, 3],
        [3, 0.8],
      ],
      center: 5,
      notes: [3, 5],
      gap: [6, 14],
      // F, A and C: the tonic chord.
      land: [1, 3, 5, 6, 8],
      beat: () => {
        if (longBeat) pair = 1.05 + (Math.random() - 0.5) * 0.08;
        const d = pair * (longBeat ? 0.64 : 0.36);
        longBeat = !longBeat;
        return d;
      },
      // Notes land on the long beats; the short one is where a pickup goes.
      onBeat: (beat) => beat % 2 === 0,
      /*
       * The motif: a falling minor third, sol to mi, C down to A and back - the
       * interval children call each other in from the garden with, which is
       * as close as a melody gets to "here, kitty". From C5 or from F4.
       */
      motif: { semis: [0, -3, 2, 0, -3], beats: [2, 2, 1, 1, 4], roots: [523.25, 349.23] },
      motifFirst: [25, 50],
      motifEvery: [110, 200],
      episodeOn: [90, 180],
      episodeOff: [30, 70],
      play: (t0, freq, n) => {
        const level = (0.03 + Math.random() * 0.018) * (1 + this.tension * 0.3);
        const seconds = n.last ? 2.6 : 1.5 + Math.random() * 0.9;
        /*
         * The pounce. Late in a round, a note sometimes arrives with two or
         * three grace notes skittering up to it from below — quick, quiet, and
         * over before the note lands. The grace notes go ON the beat and the
         * note lands just after them: the scheduler only looks 200ms ahead, and
         * a note placed in the past plays immediately.
         */
        let land = t0;
        if (!n.motif && this.tension > 0.5 && Math.random() < this.tension * 0.22) {
          const run = 2 + Math.floor(Math.random() * 2);
          const d = scale.indexOf(freq);
          for (let i = 0; i < run; i++) tine(t0 + i * 0.055, scale[Math.max(0, d - (run - i))], level * 0.45, 0.6);
          land = t0 + run * 0.055;
        }
        tine(land, freq, level, seconds);
        // A pickup on the short beat now and then: a quiet neighbour leaning
        // into the next note. This is what makes the line lilt.
        if (!n.last && !n.motif && Math.random() < 0.18 + this.tension * 0.12) {
          const d = scale.indexOf(freq);
          const nb = scale[Math.max(0, Math.min(scale.length - 1, d + (Math.random() < 0.5 ? -1 : 1)))];
          tine(t0 + (pair * 0.64) / this.tempo(), nb, level * 0.5, 1.1);
        }
      },
    });

    /*
     * Garden birds, outside and a little way off: a two-note whistle, high then
     * lower. Through a window, so quieter and lower than they were (3.5kHz sat
     * right at the ear's most sensitive point), and rarer.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.5 + Math.random() * 0.35);
        const hi = 2200 + Math.random() * 400;
        this.ambientBlip(ctx, out, t0, { freq: hi, freqTo: hi * 0.98, gain: 0.008, duration: 0.26, pan });
        this.ambientBlip(ctx, out, t0 + 0.34, { freq: hi * 0.84, freqTo: hi * 0.8, gain: 0.007, duration: 0.3, pan });
      },
      () => 28000 + Math.random() * 40000,
      9000,
    );

    /*
     * The cat, asleep in the sun somewhere near the table. ONE slot, holding the
     * purr currently sounding, for the reason Olympus' thunder and the Midnight
     * Zone's calls keep one: a teardown hook per firing would grow
     * `ambienceStops` for as long as the app is open. A purr runs about ten
     * seconds and they are never less than forty apart, so they cannot overlap.
     *
     * It waits a while for the first one, like the other rooms' rare voices: a
     * purr the instant the screen opens sounds like the app purring at you.
     */
    let purring: (() => void) | null = null;
    this.startSparse(
      ctx,
      (t0) => {
        purring = this.purr(ctx, out, t0, {
          gain: 0.05,
          breaths: 3 + Math.floor(Math.random() * 3),
          pan: (Math.random() - 0.5) * 1.1,
        });
      },
      () => 40000 + Math.random() * 60000,
      22000,
    );
    this.ambienceStops.push(() => purring?.());
  }

  /**
   * Hallowed Ground: the cathedral organ at midnight — somebody at the console
   * in an empty nave, flutes high in the vault, the pedal so low it is felt
   * rather than heard, and now and then the full organ in great dark chords.
   * Outside the doors, wind in the yews, an owl, a gate, and the clock tolling
   * in the tower.
   *
   * It began as a xylophone waltz for skeletons (Danse Macabre). The organ is
   * the same room's other famous Halloween sound — Bach's Toccata in D minor —
   * and it keeps the waltz's skeleton: D harmonic minor, a stately three, the
   * same wandering chords and sections, the Dies irae. The four properties:
   *
   *  1. IT IS PLAYED, NOT FILTERED — three stops of one instrument (`organ`,
   *     `ORGAN_STOPS`): a flute on the upper manual for the line, the principal
   *     for the chords, and a 16-foot pedal under them.
   *  2. IT IS IN A ROOM — a cathedral, the longest tail in the game. See
   *     `VENUE_ACOUSTICS.hallowed`.
   *  3. IT IS IN TIME, IN THREE — the melody on the downbeat, the odd quiet note
   *     on two and three. But the LOW end no longer keeps time: the pedal takes
   *     a chord's root as it arrives and holds it for the whole chord, fading,
   *     which is how an organist plays the pedals and why the room has stopped
   *     ticking.
   *  4. IT KNOWS WHAT THE GAME IS DOING — the procession is chosen more, the
   *     chorale too, the flutes start throwing toccata flourishes down into
   *     their notes, and like every room the pulse is 30% quicker on the last
   *     lap (`TENSION_TEMPO_LIFT`).
   *
   * The augmented second (Bb up to C#) was allowed here because a xylophone
   * note was gone before it could clash. An organ note is not gone until the
   * key comes up, so a C# is now only ever HELD over a chord that contains it
   * (the V and the diminished seventh), and walked on to the D otherwise. The
   * bed underneath stays consonant as the bed test demands, and holds no C
   * natural to argue with the C# (see `VENUE_BEDS.hallowed`).
   */
  private startHallowed(ctx: AudioContext, out: GainNode): void {
    /*
     * Wind through the churchyard yews, heard through the doors. A bandpass in
     * the low mids whose center wanders on a slow LFO, which is the one change
     * that turns noise into a MOAN. Quieter than it was: the organ is the room
     * now, and the wind is outside it.
     */
    this.startNoiseBed(ctx, out, {
      filter: "bandpass",
      freq: 420,
      gain: 0.009,
      lfoRate: 0.045,
      lfoGainDepth: 0.005,
      lfoFilterDepth: 190,
    });

    this.startDriftingVoices(ctx, out, VENUE_BEDS.hallowed);

    /*
     * Every pipe plays through this. An organ note HOLDS, so it carries several
     * times the energy of the xylophone click it replaced at the same peak, and
     * the first voicing measured 5.5dB louder than the waltz had been
     * (music-survey). One stage brings the whole instrument down together,
     * which keeps the balance between the stops as they were voiced.
     */
    const pipes = ctx.createGain();
    pipes.gain.value = 0.55;
    pipes.connect(out);

    // The upper manual: D harmonic minor, A4 up to D6 — higher than the
    // xylophone's D4-A5, because a flute pipe can sit up there without the loud
    // twelfth that made the high bars clatter. 0 A4, 1 Bb4, 2 C#5, 3 D5, 4 E5,
    // 5 F5, 6 G5, 7 A5, 8 Bb5, 9 C#6, 10 D6.
    const scale = [440.0, 466.16, 554.37, 587.33, 659.26, 698.46, 783.99, 880.0, 932.33, 1108.73, 1174.66];
    const RAISED = [2, 9];
    const STEPS = [-3, -2, -2, -1, -1, -1, 1, 1, 1, 2, 2, 3];

    /*
     * THE HARMONY WANDERS, as it did under the waltz: each chord lasts a few
     * bars and hands on to one of a few others, a minor key's ordinary moves,
     * so every change sounds like music and no ORDER recurs.
     *
     * The waltz's diminished ii is now the diminished SEVENTH (C# E G Bb) over
     * the A in the pedal — the chord the Toccata in D minor is made of, and the
     * most dramatic sound an organ makes in this key.
     *
     * `pedal` is the root on the pedalboard, D1 to Bb1: two octaves under the
     * old pizzicato's floor. `tones` are the chord's notes on the upper manual,
     * which the flute leans on at a downbeat. `voicing` is the full chord for
     * the chorale, both hands, spread over two and a half octaves.
     */
    type ChordId = "i" | "iv" | "VI" | "V" | "vii";
    const CHORDS: Record<ChordId, { pedal: number; tones: number[]; voicing: number[]; next: Array<[ChordId, number]> }> = {
      // D minor: D3 A3 D4 F4 A4 D5
      i: { pedal: 36.71, tones: [0, 3, 5, 7, 10], voicing: [146.83, 220.0, 293.66, 349.23, 440.0, 587.33], next: [["iv", 3], ["VI", 3], ["V", 3], ["vii", 1]] },
      // G minor: G2 D3 G3 Bb3 D4 G4
      iv: { pedal: 49.0, tones: [1, 3, 6, 8, 10], voicing: [98.0, 146.83, 196.0, 233.08, 293.66, 392.0], next: [["V", 4], ["i", 3], ["vii", 2], ["VI", 1]] },
      // Bb major: Bb2 F3 Bb3 D4 F4 Bb4
      VI: { pedal: 58.27, tones: [1, 3, 5, 8, 10], voicing: [116.54, 174.61, 233.08, 293.66, 349.23, 466.16], next: [["iv", 4], ["V", 3], ["i", 2]] },
      // A major, the raised C# in it: A2 E3 A3 C#4 E4 A4
      V: { pedal: 55.0, tones: [0, 2, 4, 7, 9], voicing: [110.0, 164.81, 220.0, 277.18, 329.63, 440.0], next: [["i", 6], ["VI", 3]] },
      // C# diminished seventh over the A: C#3 G3 Bb3 E4 G4 C#5
      vii: { pedal: 55.0, tones: [1, 2, 4, 6, 8, 9], voicing: [138.59, 196.0, 233.08, 329.63, 392.0, 554.37], next: [["V", 3], ["i", 4]] },
    };
    const pick = <T,>(options: Array<[T, number]>): T => {
      let r = Math.random() * options.reduce((sum, [, w]) => sum + w, 0);
      for (const [value, w] of options) if ((r -= w) < 0) return value;
      return options[options.length - 1][0];
    };

    /*
     * THE PIECE HAS SECTIONS, each for a handful of bars, never the same one
     * twice running, each at its own steady tempo:
     *
     *  - PROCESSION: the whole thing in three — pedal under most chords, the
     *    flute on the downbeats, the odd note on two and three.
     *  - FLUTES: no pedal, the line high and sparse in the vault.
     *  - PEDAL: the bottom of the organ — every chord's root, held, and the
     *    flute low and slow over it.
     *  - HUSH: nearly nothing. A note every few bars, a pedal note now and then,
     *    the wind.
     *  - CHORALE: the dramatic one. Full chords on the principal, both hands and
     *    the pedal, changing every bar or two and coming to rest on the tonic.
     *    It is the rarest section at rest and grows with the tension.
     *
     * `pedal` is the chance a chord's arrival gets a pedal note; `chordBars` how
     * long a chord lasts in this section. The procession and the chorale are
     * chosen more as the lap closes, the hush less.
     */
    type SectionId = "procession" | "flutes" | "pedal" | "hush" | "chorale";
    const SECTIONS: Record<
      SectionId,
      {
        bars: [number, number];
        weight: number;
        pedal: number;
        melody: number;
        pah: number;
        pickup: number;
        center: number;
        tempo: [number, number];
        chordBars: [number, number];
      }
    > = {
      procession: { bars: [6, 12], weight: 1.2, pedal: 0.8, melody: 0.55, pah: 0.1, pickup: 0.1, center: 5, tempo: [1.95, 2.2], chordBars: [2, 4] },
      flutes: { bars: [4, 8], weight: 2, pedal: 0, melody: 0.36, pah: 0.03, pickup: 0.08, center: 7, tempo: [2.05, 2.35], chordBars: [2, 4] },
      pedal: { bars: [4, 8], weight: 1.8, pedal: 1, melody: 0.26, pah: 0, pickup: 0.05, center: 2, tempo: [2.15, 2.45], chordBars: [2, 3] },
      hush: { bars: [2, 5], weight: 1.5, pedal: 0.3, melody: 0.12, pah: 0, pickup: 0, center: 5, tempo: [2.2, 2.5], chordBars: [2, 4] },
      chorale: { bars: [4, 7], weight: 0.9, pedal: 1, melody: 0, pah: 0, pickup: 0, center: 7, tempo: [2.1, 2.4], chordBars: [1, 2] },
    };
    const between = ([lo, hi]: [number, number]) => lo + Math.random() * (hi - lo);
    const count = ([lo, hi]: [number, number]) => lo + Math.floor(Math.random() * (hi - lo + 1));

    // Of each bar, where the next beat falls: a lilt, but only just — a
    // procession, not a dance.
    const SPACING = [0.325, 0.345, 0.33];

    // It begins with the flutes rather than mid-procession.
    let section: SectionId = "flutes";
    let sectionBars = 4;
    let barLen = between(SECTIONS.flutes.tempo);
    let chord: ChordId = "i";
    let chordBars = 3;
    let chordLen = 3;
    let nextChord: ChordId = pick(CHORDS.i.next);
    let degree = 7;
    let playedDownbeat = false;
    let pos = 0;

    /** A note on the flute, held for `beats` of the current bar. */
    const flute = (at: number, deg: number, level: number, beats: number, release = 0.7) => {
      const d = Math.max(0, Math.min(scale.length - 1, deg));
      this.organ(ctx, pipes, at, {
        freq: scale[d],
        gain: level,
        seconds: ((beats * barLen) / 3 / this.tempo()) * 0.92,
        release,
        stop: "flute",
        // Panned by pitch, as pipes stand in a case: low ones one side, high
        // the other, and not wide - it is one instrument at one end of the nave.
        pan: (d / (scale.length - 1) - 0.5) * 0.5 + (Math.random() - 0.5) * 0.08,
      });
    };
    /** The pedal: a chord's root, swelling in and fading for as long as the chord lasts. */
    const pedal = (at: number, freq: number, bars: number, level: number) => {
      this.organ(ctx, pipes, at, {
        freq,
        gain: level,
        seconds: ((bars * barLen) / this.tempo()) * 0.94,
        release: 3.2,
        stop: "pedal",
        pan: -0.08,
      });
    };

    /*
     * The motif is the Dies irae - "day of wrath", the plainchant for the dead,
     * F E F D E C# D D - which Saint-Saëns quotes in Danse Macabre and every
     * organist has played at a funeral. Stated on the principal, legato, from
     * D4 or D5, only while neither the procession nor the chorale is on, every
     * couple of minutes, over a long low D; while it sounds the flute waits.
     */
    const DIES_IRAE: Motif = { semis: [3, 2, 3, 0, 2, -1, 0, 0], beats: [1, 1, 1, 1, 1, 1, 1.5, 1.5], roots: [293.66, 587.33] };
    let nextMotifAt = ctx.currentTime + 45 + Math.random() * 45;
    let motifUntil = 0;

    /** A melody step: a walk that drifts back towards the section's register and reflects off the ends. */
    const walk = (center: number) => {
      let step = STEPS[Math.floor(Math.random() * STEPS.length)];
      // Out past four degrees from where the section sits, a step away from it
      // is usually turned round — a pull, not a leash.
      if (Math.abs(degree + step - center) > 4 && Math.sign(step) === Math.sign(degree - center) && Math.random() < 0.7) step = -step;
      degree += step;
      if (degree < 0) degree = -degree;
      if (degree > scale.length - 1) degree = 2 * (scale.length - 1) - degree;
      degree = Math.max(0, Math.min(scale.length - 1, degree));
    };
    /** The chord tone nearest the melody, for landing a downbeat on the harmony. */
    const nearestTone = (tones: number[]) => tones.reduce((best, t) => (Math.abs(t - degree) < Math.abs(best - degree) ? t : best), tones[0]);
    /** A held C# over a chord without one is a semitone under the bed's D for seconds: walk it up to the D. */
    const settle = (tones: number[]) => {
      if (RAISED.includes(degree) && !tones.includes(degree)) degree++;
    };

    this.startPulse(
      ctx,
      // Each beat's share of the bar, with a whisker of looseness so no two
      // beats land on an exact grid. The bar itself is set on the downbeat.
      () => barLen * SPACING[pos] + (Math.random() - 0.5) * 0.015,
      (t0, beat) => {
        // Set before anything can return: `interval` reads it to space the
        // NEXT beat, and a rest is still a beat of the bar.
        pos = beat % 3;
        const s = SECTIONS[section];
        const busier = this.tension * 0.25;

        if (pos === 0) {
          // A new section, when this one has run its bars.
          if (sectionBars <= 0) {
            const options = (Object.keys(SECTIONS) as SectionId[])
              .filter((id) => id !== section)
              .map((id): [SectionId, number] => {
                const w = SECTIONS[id].weight;
                if (id === "procession") return [id, w * (1 + this.tension * 6)];
                if (id === "chorale") return [id, w * (1 + this.tension * 2)];
                if (id === "hush") return [id, w * (1 - this.tension * 0.85)];
                return [id, w];
              });
            section = pick(options);
            sectionBars = count(SECTIONS[section].bars);
            barLen = between(SECTIONS[section].tempo);
            // The two sections that are about the low end start on a change,
            // so they open with the pedal rather than a bar or two in.
            if (section === "chorale" || section === "pedal") chordBars = 0;
          }
          sectionBars--;
          const now = SECTIONS[section];

          // The harmony: a new chord when this one has had its bars, and the
          // one after it chosen now.
          let chordChanged = false;
          if (chordBars <= 0) {
            chord = nextChord;
            chordBars = count(now.chordBars);
            nextChord = pick(CHORDS[chord].next);
            chordChanged = true;
            // A chorale comes to rest: if it is about to end on anything but
            // the tonic, its last change goes home.
            if (section === "chorale" && chord !== "i") {
              if (sectionBars === 0) {
                chord = "i";
                nextChord = pick(CHORDS.i.next);
              } else if (sectionBars < chordBars) {
                chordBars = sectionBars;
                nextChord = "i";
              }
            }
            chordLen = chordBars;
          }
          chordBars--;
          const c = CHORDS[chord];

          if (chordChanged && Math.random() < now.pedal + (section === "procession" ? busier : 0)) {
            // Mostly the low octave; sometimes the one above, so a long stretch
            // of the same chord is not the same rumble.
            const freq = section !== "pedal" && Math.random() < 0.3 ? c.pedal * 2 : c.pedal;
            pedal(t0, freq, chordLen, (section === "chorale" ? 0.04 : 0.032) + Math.random() * 0.006);
          }

          /*
           * The chorale's chords: both hands on the principal, every note of
           * the voicing, held as long as the chord is. Full organ on the last
           * lap — the top note doubled an octave up.
           */
          if (section === "chorale") {
            if (chordChanged) {
              const notes = this.tension > 0.6 ? [...c.voicing, c.voicing[c.voicing.length - 1] * 2] : c.voicing;
              notes.forEach((freq, i) => {
                this.organ(ctx, pipes, t0 + i * 0.004, {
                  freq,
                  gain: 0.0072 * (1 + this.tension * 0.25),
                  seconds: ((chordLen * barLen) / this.tempo()) * 0.96,
                  release: 2.8,
                  stop: "principal",
                  pan: (i / (notes.length - 1) - 0.5) * 0.6,
                });
              });
            }
            return;
          }

          playedDownbeat = false;
          if (t0 < motifUntil) return;
          if (section !== "procession" && t0 >= nextMotifAt) {
            nextMotifAt = t0 + 110 + Math.random() * 100;
            const { freqs, beats } = this.varyMotif(DIES_IRAE);
            const beatLen = barLen / 3 / this.tempo();
            let t = t0;
            freqs.forEach((freq, i) => {
              const last = i === freqs.length - 1;
              this.organ(ctx, pipes, t, { freq, gain: 0.02, seconds: beats[i] * beatLen * 0.95, release: last ? 2 : 0.35, stop: "principal", pan: -0.1 });
              t += beats[i] * beatLen;
            });
            this.organ(ctx, pipes, t0, { freq: 36.71, gain: 0.026, seconds: t - t0, release: 3.2, stop: "pedal", pan: -0.08 });
            motifUntil = t;
            return;
          }
          if (Math.random() >= s.melody + busier) return;
          walk(s.center);
          // Lean on the harmony: most downbeats land on a chord tone.
          if (Math.random() < 0.65) degree = nearestTone(c.tones);
          settle(c.tones);
          const level = (0.017 + Math.random() * 0.008) * (1 + this.tension * 0.3) * (section === "hush" ? 0.75 : 1);

          /*
           * The flourish. Late in a round, a downbeat sometimes arrives with two
           * to four notes tumbling DOWN into it from above — the toccata's
           * gesture, the organist showing off. The grace notes go ON the beat
           * and the note lands just after them: the scheduler only looks 200ms
           * ahead, and a note placed before `t0` may already be in the past,
           * which plays at once.
           */
          let land = t0;
          if (section !== "hush" && this.tension > 0.5 && Math.random() < this.tension * 0.25) {
            const run = 2 + Math.floor(Math.random() * 3);
            for (let i = 0; i < run; i++) flute(t0 + i * 0.06, degree + (run - i), level * 0.6, 0.12, 0.12);
            land = t0 + run * 0.06;
          }
          // Held for one to three beats: a pipe sings for as long as the key is
          // down, so the length is part of the line.
          const r = Math.random();
          flute(land, degree, level, r < 0.4 ? 1 : r < 0.75 ? 2 : 3);
          playedDownbeat = true;
          return;
        }

        if (section === "chorale") return;
        const c = CHORDS[chord];
        if (t0 < motifUntil) return;

        // Beat two, in the procession only: now and then a second note straight
        // after the downbeat's, a step away — a figure rather than a note.
        if (pos === 1 && section === "procession" && playedDownbeat && Math.random() < 0.2 + busier * 0.5) {
          degree = Math.max(0, Math.min(scale.length - 1, degree + (Math.random() < 0.5 ? -1 : 1)));
          settle(c.tones);
          flute(t0, degree, 0.013 + Math.random() * 0.005, 1);
          return;
        }

        // A pickup on the last beat, a quiet step leaning into the next bar.
        if (pos === 2 && Math.random() < s.pickup + busier * 0.5) {
          degree += Math.random() < 0.5 ? -1 : 1;
          if (degree < 0) degree = 1;
          if (degree > scale.length - 1) degree = scale.length - 2;
          settle(c.tones);
          flute(t0, degree, 0.011 + Math.random() * 0.004, 0.8, 0.4);
          return;
        }

        // The off-beat: one quiet tone of the chord, an octave down on the
        // principal, short. Mostly air; the procession fills in as the lap
        // closes.
        if (Math.random() < s.pah + (section === "procession" ? busier : 0)) {
          const low = c.tones.filter((t) => t <= 5);
          const t = low[Math.floor(Math.random() * low.length)];
          this.organ(ctx, pipes, t0, { freq: scale[t] / 2, gain: 0.007 + Math.random() * 0.003, seconds: (barLen / 3 / this.tempo()) * 0.5, release: 0.4, stop: "principal", pan: 0.12 });
        }
      },
    );

    /*
     * The church clock striking midnight, from across the graves. A bronze bell
     * (the one material this room borrows, and borrowed on purpose: a church
     * bell is a bell), low, far off to one side and heard through a lowpass,
     * because distance is a lowpass. Three to six strokes: never the full
     * twelve, which would be a minute of bell every time and a joke by the
     * third.
     *
     * The whole toll is scheduled in one go, and held in ONE slot for the reason
     * Olympus' thunder, the Midnight Zone's calls and the porch's cat each keep
     * one: a teardown hook per firing would grow `ambienceStops` for as long as
     * the app is open. A toll is at most about twenty seconds of ringing and
     * tolls are never less than seventy apart, so one slot is all there is. It
     * is the one voice in this room that must be stopped explicitly, because it
     * is the one that outlasts a venue change by a long way.
     *
     * Forty seconds before the first: a clock striking the moment the screen
     * opens is the app announcing itself, not the hour.
     */
    let tolling: Array<() => void> = [];
    this.startSparse(
      ctx,
      (t0) => {
        const far = ctx.createBiquadFilter();
        far.type = "lowpass";
        far.frequency.value = 1300;
        far.Q.value = 0.6;
        // A unity gain only because `struck` plays into a GainNode; the filter
        // behind it is the distance.
        const distance = ctx.createGain();
        distance.connect(far).connect(out);
        const freq = Math.random() < 0.5 ? 110.0 : 146.83; // A2 or D3: in the key, either way
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.55 + Math.random() * 0.2);
        const strokes = 3 + Math.floor(Math.random() * 4);
        tolling = [];
        for (let i = 0; i < strokes; i++) {
          tolling.push(
            this.struck(ctx, distance, t0 + i * (2.4 + Math.random() * 0.2), {
              freq,
              gain: 0.034 + Math.random() * 0.008,
              seconds: 6,
              pan,
              partials: BRONZE_PARTIALS,
            }),
          );
        }
      },
      () => 70000 + Math.random() * 50000,
      40000,
    );
    this.ambienceStops.push(() => {
      for (const stop of tolling) stop();
    });

    /*
     * A tawny owl, somewhere in the trees at the edge: one long "hoo", a pause,
     * then a quick "hoo-hoo". Each note sags a little in pitch, as a real one
     * does — a steady sine at owl pitch is a flute. Panned well out, because an
     * owl is never where you are looking.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.6 + Math.random() * 0.25);
        const f = 350 + Math.random() * 70;
        this.ambientBlip(ctx, out, t0, { freq: f, freqTo: f * 0.93, gain: 0.013, duration: 0.5, pan });
        const next = t0 + 0.5 + 0.45 + Math.random() * 0.2;
        this.ambientBlip(ctx, out, next, { freq: f * 1.02, freqTo: f * 0.95, gain: 0.011, duration: 0.2, pan });
        this.ambientBlip(ctx, out, next + 0.24, { freq: f, freqTo: f * 0.9, gain: 0.011, duration: 0.34, pan });
      },
      () => 40000 + Math.random() * 50000,
      14000,
    );

    /*
     * The cemetery gate swinging on a rusty hinge. A creak is STICK-SLIP: the
     * hinge catches and lets go over and over, so what the ear hears is a train
     * of tiny clicks, and the rate of the clicks is the "pitch" of the creak.
     * So this is a run of noise grains rather than a tone, their spacing
     * wobbling and widening as the gate slows to a stop, their band sinking as
     * it goes. A sawtooth glide was the obvious alternative, and is a door in a
     * cartoon rather than one in the dark.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() - 0.5) * 1.2;
        const length = 0.7 + Math.random() * 0.4;
        const wobble = 9 + Math.random() * 6;
        const band = 1300 + Math.random() * 400;
        let t = 0;
        while (t < length) {
          const p = t / length;
          this.ambientPop(ctx, out, t0 + t, {
            filter: "bandpass",
            freq: band * (1 - 0.45 * p),
            // Swells in and out over the swing, rather than every grain landing
            // at one level, which would be a ratchet.
            peak: 0.004 + 0.006 * Math.sin(Math.PI * p),
            duration: 0.02,
            pan,
          });
          t += 0.014 + 0.02 * p + Math.sin(p * wobble) * 0.004;
        }
      },
      () => 70000 + Math.random() * 70000,
      26000,
    );
  }

  /**
   * The Mead Hall: a skald with a bowed lyre by the hearth in a timber
   * longhouse, the fire crackling, wind working at the boards outside, a raven
   * somewhere off, and - when the round tightens - a drum being brought out.
   *
   * The fifth premium soundscape, and the most plainly FOLK: a person playing
   * to a hall rather than a room playing to itself. Same four properties:
   *
   *  1. IT IS PLAYED, NOT FILTERED - a bowed lyre, the tagelharpa (`bowed`):
   *     a sawtooth swelling in under the bow, with the body's two resonances,
   *     vibrato that arrives once the string is speaking, and a breath of
   *     horsehair at the bite. A horn (`horn`) and a frame drum (`frameDrum`)
   *     are the other two instruments, and a raven (`croak`) is the only
   *     thing here that is not a person's.
   *  2. IT IS IN A ROOM - mid-length and dark, a longhouse full of people and
   *     smoke. See `VENUE_ACOUSTICS.meadhall`.
   *  3. IT IS IN TIME, AND SLOWLY. The slowest pulse of any pitched room
   *     (about 15 notes a minute at rest, against the Midnight Zone's 17):
   *     phrases of three to five notes, each ending on a chord tone held for
   *     two or three beats, then several beats of silence. A bowed note
   *     takes a quarter of a second to speak and is held for seconds, and long
   *     notes need long gaps or the line stacks into a drone.
   *  4. IT KNOWS WHAT THE GAME IS DOING - and the gesture is the MUSTER DRUM.
   *     Olympus answers a closing round with weight, the abyss with alarm, the
   *     porch with play, Hallowed Ground with toccata flourishes; the hall
   *     answers by calling people in. Above about a third tension a frame
   *     drum begins to beat paired strokes, a heartbeat, on every other
   *     beat, and the further the round closes the more of those it plays.
   *     At rest there is no drum at all: a full metre is a payoff, not a bed
   *     (docs/PREMIUM_VENUES.md), and a pulse under a whole game is a
   *     foreground pulse under a game that needs attention. Its speed is not
   *     this room's to decide - `startPulse` lifts every pulse in one place.
   *
   * The mode is E minor with no sixth: E F# G A B D, the hexatonic folk mode.
   * Without the C there is no tritone anywhere in it, and the only semitone
   * it holds is F# against G. That one is why the line has no F#3: the bed
   * holds a G3, and a bowed F#3 would beat against it for as long as the key
   * is down (see `VENUE_BEDS.meadhall`). F#4 is safe, a major seventh above.
   */
  private startMeadHall(ctx: AudioContext, room: GainNode): void {
    /*
     * The whole room through one trim. Voiced by ear it measured -54 dB(A) at
     * rest (`music-survey.mjs`), four or more louder than every other room and
     * nearly six over the Midnight Zone, which players find the easiest to sit
     * with. The bowed line and the hearth carry most of that, and taking it off
     * here keeps their balance against each other, which was right.
     */
    const out = ctx.createGain();
    out.gain.value = 0.6;
    out.connect(room);
    this.ambienceStops.push(() => out.disconnect());

    // The hearth's low hush. The crackle on top of it is below; this is the
    // fire as a presence, which a pop every second or two is not.
    this.startNoiseBed(ctx, out, { filter: "lowpass", freq: 210, gain: 0.024, lfoRate: 0.09, lfoGainDepth: 0.008 });

    this.startDriftingVoices(ctx, out, VENUE_BEDS.meadhall);

    // E minor, hexatonic, E3 up to E5, with no F#3 (see above). 0 E3, 1 G3,
    // 2 A3, 3 B3, 4 D4, 5 E4, 6 F#4, 7 G4, 8 A4, 9 B4, 10 D5, 11 E5.
    const scale = [164.81, 196.0, 220.0, 246.94, 293.66, 329.63, 369.99, 392.0, 440.0, 493.88, 587.33, 659.25];
    const BEAT = 1.55;

    /*
     * Panned by PLACE, not by pitch. The player is sitting somewhere by the
     * fire, on one side of the hall for the whole session, and leans a little
     * as they play; the hearth is across from them. Pitch panning (Olympus' and
     * the porch's) is for an instrument heard being played, and here the
     * instrument is a person at one place on a bench.
     */
    const seat = (Math.random() < 0.5 ? -1 : 1) * (0.28 + Math.random() * 0.12);
    let place = seat;
    const hearth = -seat * 0.5;

    /*
     * Gjallarhorn, the horn Heimdall blows at Ragnarok: rising fifths, E3 up to
     * B3, then B3 D4 E4. Stated every two to three minutes on a horn a long way
     * off, and varied each time (`varyMotif`): from E3, or from B3 (B3 F#4 F#4
     * A4 B4), whole, slowed, or only its opening. Every root keeps every note
     * in the key.
     *
     * The valley is the echo that makes it far: a slap back off a hillside,
     * dark and fed back on itself, on the side opposite the horn.
     */
    const GJALLARHORN: Motif = { semis: [0, 7, 7, 10, 12], beats: [2, 2.5, 1, 1, 3], roots: [164.81, 246.94] };
    const valley = ctx.createGain();
    const slap = ctx.createDelay(1);
    slap.delayTime.value = 0.46;
    const damp = ctx.createBiquadFilter();
    damp.type = "lowpass";
    damp.frequency.value = 750;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.5;
    const wet = ctx.createGain();
    wet.gain.value = 0.9;
    const wetPan = ctx.createStereoPanner();
    valley.connect(slap).connect(damp).connect(feedback).connect(slap);
    damp.connect(wet).connect(wetPan).connect(out);
    this.ambienceStops.push(() => {
      for (const node of [valley, slap, damp, feedback, wet, wetPan]) {
        try {
          node.disconnect();
        } catch {
          /* already gone */
        }
      }
    });
    let hornSide = 1;
    let lastHorn = -Infinity;
    let calling: Array<() => void> = [];
    this.ambienceStops.push(() => {
      for (const stop of calling) stop();
    });

    this.startPhrases(ctx, {
      scale,
      // A folk line: mostly steps, a skip of a third, the odd fourth.
      steps: [
        [1, 6],
        [2, 3.5],
        [3, 1],
      ],
      center: 5,
      notes: [3, 5],
      gap: [3, 7],
      // E, G and B: the tonic chord, with the open strings (E and B) twice.
      land: [0, 1, 3, 5, 7, 9, 11],
      beat: () => BEAT + (Math.random() - 0.5) * 0.3,
      motif: GJALLARHORN,
      motifFirst: [40, 80],
      motifEvery: [120, 180],
      episodeOn: [90, 170],
      episodeOff: [30, 60],
      play: (t0, freq, n) => {
        const beatSec = BEAT / this.tempo();
        if (n.motif) {
          // A new statement picks a side: the notes are seconds apart, the
          // statements minutes.
          if (t0 - lastHorn > 12) {
            hornSide = Math.random() < 0.5 ? -1 : 1;
            wetPan.pan.value = -hornSide * 0.5;
            calling = [];
          }
          lastHorn = t0;
          calling.push(
            this.horn(ctx, out, t0, {
              freq,
              gain: 0.03 + Math.random() * 0.006,
              seconds: n.beats * beatSec * 0.95,
              release: n.last ? 2.2 : 0.6,
              pan: hornSide * (0.7 + Math.random() * 0.15),
              echo: valley,
            }),
          );
          return;
        }
        place = Math.max(seat - 0.15, Math.min(seat + 0.15, place + (Math.random() - 0.5) * 0.1));
        this.bowed(ctx, out, t0, {
          freq,
          gain: (0.022 + Math.random() * 0.012) * (1 + this.tension * 0.25),
          // Legato: each note runs a little into the next, as a bow does.
          seconds: n.beats * beatSec * 0.98 + 0.3,
          pan: place,
          bright: 0.4 + Math.random() * 0.6,
        });
      },
    });

    /*
     * THE MUSTER DRUM, this room's answer to the tension hook.
     *
     * Its own pulse, on the shared scheduler so `startPulse` lifts its tempo
     * with the round like every other room's: this does not tighten itself. A
     * beat is 1.6 seconds at rest and the drum speaks only on every other one,
     * the downbeats, and only above a third tension - below that it is not
     * quiet, it is ABSENT, because a metre under a calm table is a foreground
     * pulse under a game that needs attention. The chance a downbeat gets its
     * stroke rises from one in four at that threshold to nine in ten at full
     * tension, so the drum arrives as a rare thing and becomes a steady one.
     *
     * The stroke is a pair, thump-thump and then the rest of the bar: a
     * heartbeat, which is what a hall hears when the war-horn has been blown.
     * The gap inside the pair divides by the tempo like any offset inside a
     * beat, with a floor, or at the top of the lift it collapses into a flam.
     */
    const drumPan = -seat * 0.3;
    this.startPulse(
      ctx,
      () => 1.6 + (Math.random() - 0.5) * 0.12,
      (t0, beat) => {
        if (this.tension < 0.35 || beat % 2 !== 0) return;
        const urgency = (this.tension - 0.35) / 0.65;
        if (Math.random() >= 0.25 + 0.65 * urgency) return;
        const level = 0.05 + 0.04 * urgency;
        this.frameDrum(ctx, out, t0, { gain: level, pan: drumPan + (Math.random() - 0.5) * 0.1 });
        this.frameDrum(ctx, out, t0 + Math.max(0.14, 0.3 / this.tempo()), { gain: level * 0.72, pan: drumPan + (Math.random() - 0.5) * 0.1 });
      },
    );

    /*
     * The hearth. The ordinary crackle is the room itself, a pop every second or
     * two, and opts out of `wear` for it - a fire that thinned out over twenty
     * minutes would just be a fire dying. (The Alpine Lodge's is a closure
     * inside its own room, so this is a second one, kept under 2kHz.) A spit
     * now and then is a one-shot and does wear.
     */
    const crackle = (t0: number, loud: number) =>
      this.ambientPop(ctx, out, t0, {
        filter: "bandpass",
        freq: 450 + Math.random() * 1300,
        peak: (0.004 + Math.random() * 0.009) * loud,
        duration: 0.012 + Math.random() * 0.04,
        pan: hearth + (Math.random() - 0.5) * 0.25,
      });
    this.startSparse(ctx, (t0) => crackle(t0, 1), () => 1100 + Math.random() * 2300, 0, { wears: false });
    this.startSparse(
      ctx,
      (t0) => {
        const pops = 3 + Math.floor(Math.random() * 5);
        let t = t0;
        for (let i = 0; i < pops; i++) {
          crackle(t, 1.4);
          t += 0.03 + Math.random() * 0.12;
        }
      },
      () => 16000 + Math.random() * 22000,
      5000,
    );

    /*
     * Wind working at the timbers outside: a slow swell of low noise whose
     * band climbs as the gust builds and sinks as it passes. Heard through a
     * wall, so nothing above 700Hz, and wide, since the wall is all round you.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const rise = 2.2 + Math.random() * 2;
        const fall = 3 + Math.random() * 3;
        const src = ctx.createBufferSource();
        src.buffer = this.getNoise(ctx);
        src.loop = true;
        const band = ctx.createBiquadFilter();
        band.type = "bandpass";
        band.Q.value = 1.1;
        band.frequency.setValueAtTime(230, t0);
        band.frequency.linearRampToValueAtTime(400, t0 + rise);
        band.frequency.linearRampToValueAtTime(260, t0 + rise + fall);
        const wall = ctx.createBiquadFilter();
        wall.type = "lowpass";
        wall.frequency.value = 700;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.linearRampToValueAtTime(0.02 + Math.random() * 0.015, t0 + rise);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + rise + fall);
        src.connect(band).connect(wall).connect(g).connect(this.panned(ctx, out, (Math.random() > 0.5 ? 1 : -1) * (0.5 + Math.random() * 0.4)));
        src.start(t0, Math.random() * 1.5);
        src.stop(t0 + rise + fall + 0.1);
      },
      () => 26000 + Math.random() * 30000,
      12000,
    );

    /*
     * A raven, far off over the fields: "kraa... kraa", two low nasal croaks,
     * the second a little lower and softer. Rare and late for the first one,
     * like every distinct voice here - a raven the instant the screen opens is
     * the app announcing itself. It wears, as one-shots do.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.55 + Math.random() * 0.3);
        const f = 205 + Math.random() * 40;
        const gain = 0.013 + Math.random() * 0.005;
        this.croak(ctx, out, t0, { freq: f, gain, seconds: 0.3 + Math.random() * 0.1, pan });
        this.croak(ctx, out, t0 + 0.62 + Math.random() * 0.25, { freq: f * 0.94, gain: gain * 0.8, seconds: 0.34 + Math.random() * 0.1, pan });
      },
      () => 60000 + Math.random() * 55000,
      30000,
    );
  }

  /**
   * The Singularity: a wordless choir a very long way off, at the edge of a
   * black hole where the campaign's last boss waits. Low, slow, dark and
   * uneasy rather than loud: it is still music under a card game that people
   * sit with for twenty minutes, so the dread is in the register and the rests.
   *
   * The sixth premium soundscape, on the same four properties:
   *
   *  1. IT IS PLAYED, NOT FILTERED - a choir (`choir`): a sawtooth through
   *     vowel formants, doubled a few cents apart, swelling in over most of a
   *     second. Around it, three things from out in the dark: something
   *     falling in (`call`, gliding an octave down), a pulsar ticking far off,
   *     and now and then a single struck glass note, the only bright thing in
   *     the room.
   *  2. IT IS IN A ROOM - vast and black: the longest tail in the game at the
   *     darkest tilt, with nothing out there to give the top back. See
   *     `VENUE_ACOUSTICS.singularity`.
   *  3. IT IS IN TIME, AND SLOWER STILL. Aimed at under nine notes a minute at
   *     rest, the sparsest pitched room (by the arithmetic of its phrases; not
   *     yet measured with `music-survey.mjs`): phrases of three or four notes on a two-and-a-
   *     half-second beat, each ending on a chord tone held for two or three
   *     beats, then four to eight beats of nothing. A choir note takes most of
   *     a second to arrive and is held for seconds, and long notes need long
   *     gaps or the line stacks into a drone.
   *  4. IT KNOWS WHAT THE GAME IS DOING - and the gesture is THE THROB. Olympus
   *     answers a closing round with weight, the abyss with alarm, the porch
   *     with play, Hallowed Ground with flourishes, the Mead Hall with a
   *     muster drum; this answers with the thing at the middle of the room.
   *     Above about a third tension a deep pulse on C (`throb`) begins, one
   *     slow throb now and then, and the closer the round the steadier it
   *     becomes. At the same time the choir's phrases are more often doubled
   *     an octave below, so the line itself goes down into the floor. At rest
   *     there is nothing rhythmic at all: a full metre is a payoff, not a bed
   *     (docs/PREMIUM_VENUES.md). Its speed is not this room's to decide -
   *     `startPulse` lifts every pulse in one place.
   *
   * The mode is C Phrygian: C Db Eb F G Ab Bb. The flat second is what makes
   * it ominous and it is RATIONED, for Hallowed Ground's reason: a choir note
   * does not stop until the singers do, so a Db held over the bed's C is a
   * semitone beating for as long as it lasts. The Db therefore exists only in
   * the motif, as the neighbor tone of a sigh, and is kept short there (see
   * below); the walking line has no second at all. It also has no Ab3, which
   * would sit a semitone under the bed's G3 for the whole note (see
   * `VENUE_BEDS.singularity`); Ab4 is a minor ninth up, which is safe.
   */
  private startSingularity(ctx: AudioContext, room: GainNode): void {
    /*
     * The whole room through one trim. Voiced by ear and NOT yet measured with
     * `music-survey.mjs`, which is the number to move when it has been: the
     * choir and the throb carry most of the energy, and trimming here keeps
     * their balance against the textures.
     */
    const out = ctx.createGain();
    out.gain.value = 0.7;
    out.connect(room);
    this.ambienceStops.push(() => out.disconnect());

    this.startDriftingVoices(ctx, out, VENUE_BEDS.singularity);

    // C Phrygian without its second, C3 up to C5, and without Ab3 (see above).
    // 0 C3, 1 Eb3, 2 F3, 3 G3, 4 Bb3, 5 C4, 6 Eb4, 7 F4, 8 G4, 9 Ab4, 10 Bb4, 11 C5.
    const scale = [130.81, 155.56, 174.61, 196.0, 233.08, 261.63, 311.13, 349.23, 392.0, 415.3, 466.16, 523.25];
    const BEAT = 2.5;
    const SECOND = 261.63 * Math.pow(2, 1 / 12);

    /*
     * Panned by PLACE, as the Mead Hall's skald is: the choir stands somewhere
     * in the dark, to one side for the whole session, and leans a little. The
     * throb sits on the other side, because a thing that big is not where the
     * singing is.
     */
    const seat = (Math.random() < 0.5 ? -1 : 1) * (0.25 + Math.random() * 0.15);
    let place = seat;

    /*
     * The sigh: C4 Db4 C4 G3, the falling half step and then the fall to the
     * dominant. The most Phrygian gesture there is, stated every two to three
     * minutes and varied each time (`varyMotif`): whole, slowed, or only its
     * opening. The one root is deliberate. A root a fifth down would put the
     * neighbor on G (Ab over the bed's G) and one an octave down would put a
     * Db3 a semitone over the bed's C3; from C4 the Db is a minor ninth over
     * the bed's C, wide enough not to beat. (Against the bed's G3 it is a
     * tritone, which is the unease, and it is gone in a second.)
     */
    const SIGH: Motif = { semis: [0, 1, 0, -5], beats: [3, 1, 1.5, 4], roots: [261.63] };

    this.startPhrases(ctx, {
      scale,
      // A line that mostly creeps by steps, with a third now and then.
      steps: [
        [1, 7],
        [2, 3],
        [3, 0.8],
      ],
      center: 5,
      notes: [3, 4],
      gap: [4, 8],
      // C, Eb and G: the tonic chord, no Db and no Ab to land on.
      land: [0, 1, 3, 5, 6, 8, 11],
      beat: () => BEAT + (Math.random() - 0.5) * 0.4,
      motif: SIGH,
      motifFirst: [45, 90],
      motifEvery: [120, 180],
      episodeOn: [90, 170],
      episodeOff: [40, 80],
      play: (t0, freq, n) => {
        const beatSec = BEAT / this.tempo();
        place = Math.max(seat - 0.15, Math.min(seat + 0.15, place + (Math.random() - 0.5) * 0.1));
        if (n.motif) {
          const second = Math.abs(freq - SECOND) < 1;
          /*
           * The Db is a passing note: it speaks quickly and goes after a second
           * or so whatever length the motif gives it, so it is never held over
           * the bed's C. `varyMotif` can also cut the sigh to its first two
           * notes and double the last one's length, which would hold the Db
           * and end on it, so a Db that closes a statement is answered by the
           * C it fell from.
           */
          this.choir(ctx, out, t0, {
            freq,
            gain: 0.034 + Math.random() * 0.006,
            seconds: second ? Math.min(n.beats * beatSec, 1.4) : n.beats * beatSec * 0.98 + 0.3,
            attack: second ? 0.6 : undefined,
            release: second ? 1.2 : n.last ? 3 : 1.4,
            pan: place,
          });
          if (second && n.last) {
            this.choir(ctx, out, t0 + beatSec * 1.5, { freq: freq / Math.pow(2, 1 / 12), gain: 0.034, seconds: beatSec * 2, release: 3, pan: place });
          }
          return;
        }
        const seconds = n.beats * beatSec * 0.98 + 0.3;
        const release = n.last ? 2.6 : 1.4;
        const gain = (0.03 + Math.random() * 0.012) * (1 + this.tension * 0.25);
        this.choir(ctx, out, t0, { freq, gain, seconds, release, pan: place });
        // The line goes down into the floor as the round closes: more phrases
        // carry an octave under them. Not below C2 (the lowest note is C3).
        if (freq >= 130 && Math.random() < 0.08 + 0.8 * this.tension) {
          this.choir(ctx, out, t0, { freq: freq / 2, gain: gain * 0.8, seconds, release, pan: place * 0.5 });
        }
      },
    });

    /*
     * THE THROB, this room's answer to the tension hook.
     *
     * Its own pulse on the shared scheduler, so `startPulse` lifts its tempo with
     * the round like every other room's. A beat is 2.2 seconds at rest, and
     * below a third tension the throb is not quiet but ABSENT, because a metre
     * under a calm table is a foreground pulse under a game that needs
     * attention. Above it, it starts as one now and then: on every other beat
     * only, and on fewer than half of those. As the round closes it takes every
     * beat and misses almost none, which at full tension and three times the
     * tempo is a slow, steady heart under the table. Single throbs, not the Mead
     * Hall's pair: nothing here is alive enough to have a heartbeat.
     */
    const throbPan = -seat * 0.4;
    this.startPulse(
      ctx,
      () => 2.2 + (Math.random() - 0.5) * 0.2,
      (t0, beat) => {
        if (this.tension < 0.35) return;
        const urgency = (this.tension - 0.35) / 0.65;
        if (urgency < 0.5 && beat % 2 !== 0) return;
        if (Math.random() >= 0.3 + 0.65 * urgency) return;
        this.throb(ctx, out, t0, { gain: 0.05 + 0.04 * urgency, pan: throbPan });
      },
    );

    /*
     * THE FALL: something going in. A very quiet tone gliding an octave down
     * over eight to twelve seconds and fading, which is `call` with a turning
     * point on the way: from a chord tone to the same note an octave under, so
     * it ends in the key. Rare and late, like every distinct voice here; it
     * wears, as one-shots do.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const from = [311.13, 392.0, 523.25][Math.floor(Math.random() * 3)];
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.4 + Math.random() * 0.4);
        this.call(ctx, out, t0, { from, peak: from * 0.72, to: from * 0.5, gain: 0.011 + Math.random() * 0.004, seconds: 8 + Math.random() * 4, pan });
      },
      () => 55000 + Math.random() * 40000,
      35000,
    );

    /*
     * A PULSAR, far off: a dozen soft regular ticks, three to six a second,
     * that swell in and out. The rate is the star's own clock, so it is not
     * divided by the tempo and not jittered - a pulsar that wobbled would be
     * a dripping tap. It is a short rare texture and not a metre, which is the
     * difference from the throb. Lowpassed noise with a sine under it makes a
     * thump-tick rather than a click, and the noise is lowpassed at 520Hz, so
     * it stays well clear of the 2-5kHz the game uses.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const rate = 3 + Math.random() * 3;
        const ticks = 11 + Math.floor(Math.random() * 4);
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.5 + Math.random() * 0.35);
        const loud = 0.007 + Math.random() * 0.003;
        for (let i = 0; i < ticks; i++) {
          const at = t0 + i / rate;
          // A half sine over the run: in from nothing, out to nothing.
          const level = loud * Math.sin((Math.PI * (i + 0.5)) / ticks);
          this.ambientPop(ctx, out, at, { filter: "lowpass", freq: 520, peak: level, duration: 0.05, pan });
          this.ambientBlip(ctx, out, at, { freq: 150, freqTo: 95, gain: level * 0.9, duration: 0.06, pan });
        }
      },
      () => 70000 + Math.random() * 40000,
      50000,
    );

    /*
     * STARLIGHT: one struck glass note, G4 to C5, very quiet and very rare. It
     * is the only bright thing in the room, which is the whole of its job - a
     * second one in a minute would make it a feature. G, Bb and C are all
     * consonant with the bed, so it can never argue with the choir under it.
     */
    this.startSparse(
      ctx,
      (t0) => {
        const freq = [392.0, 466.16, 523.25][Math.floor(Math.random() * 3)];
        const pan = (Math.random() > 0.5 ? 1 : -1) * (0.3 + Math.random() * 0.5);
        this.struck(ctx, out, t0, { freq, gain: 0.011 + Math.random() * 0.004, seconds: 4.5 + Math.random() * 1.5, pan, partials: GLASS_PARTIALS, bright: 0.5 });
      },
      () => 85000 + Math.random() * 50000,
      60000,
    );
  }
}

export const sound = new SoundManager();
